<template>
	<Dialog
		:dialog-visible="quotaDialogVisible"
		title="Continue uploading?"
		:message="quotaDialogMessage"
		agree-text="Upload"
		cancel-text="Cancel"
		@agree="onQuotaDialogAgree"
		@cancel="onQuotaDialogCancel" />
	<Dialog
		:dialog-visible="largeFileDialogVisible"
		title="Large File Upload"
		:message="largeFileDialogMessage"
		agree-text="Upload Anyway"
		cancel-text="Cancel"
		@agree="onLargeFileDialogAgree"
		@cancel="onLargeFileDialogCancel" />
	<v-snackbar
		v-model="dialog"
		location="bottom right"
		:timeout="-1"
		color="white"
		class="file-upload-snackbar"
		:style="snackbarStyle"
		variant="elevated"
		:z-index="50">
		<div
			class="pa-4 d-flex flex-column file-upload-panel"
			style="background-color: white; width: 100%"
			@touchstart.stop
			@mousedown.stop
			@touchmove.stop
			@touchend.stop>
			<div class="d-flex align-start justify-space-between flex-shrink-0 pb-4">
				<div class="d-flex flex-column" data-test-id="file-upload-header">
					<div v-if="remainingFiles.length > 0" class="text-h5">
						<h5 class="text-black">{{ activeUploadHeading }}</h5>
					</div>
					<div v-else-if="failedFiles.length > 0 && uploadedFiles.length === 0" class="text-h5">
						<h5 class="text-black">{{ failedFiles.length }} upload{{ failedFiles.length === 1 ? "" : "s" }} failed</h5>
					</div>
					<div v-else-if="failedFiles.length > 0" class="text-h5">
						<h5 class="text-black">{{ uploadedFiles.length }} uploaded, {{ failedFiles.length }} failed</h5>
					</div>
					<div v-else-if="verifyingOrphanFiles.length > 0" class="text-h5">
						<h5 class="text-black">
							<template v-if="uploadedFiles.length > 0">{{ uploadedFiles.length }} uploaded, </template>Checking
							{{ verifyingOrphanFiles.length }} unfinished upload{{ verifyingOrphanFiles.length === 1 ? "" : "s" }}
						</h5>
					</div>
					<div v-else-if="needsAttentionFiles.length > 0" class="text-h5">
						<h5 class="text-black">
							<template v-if="uploadedFiles.length > 0">{{ uploadedFiles.length }} uploaded, </template
							>{{ needsAttentionFiles.length }} upload{{ needsAttentionFiles.length === 1 ? "" : "s" }}
							{{ needsAttentionFiles.length === 1 ? "hasn’t" : "haven’t" }} finished
						</h5>
					</div>
					<div v-else-if="checkingFiles.length > 0" class="text-h5">
						<h5 class="text-black">
							<template v-if="uploadedFiles.length > 0">{{ uploadedFiles.length }} uploaded, </template>Checking
							{{ checkingFiles.length }} upload{{ checkingFiles.length === 1 ? "" : "s" }}
						</h5>
					</div>
					<div v-else-if="retryingFiles.length > 0" class="text-h5">
						<h5 class="text-black">
							<template v-if="uploadedFiles.length > 0">{{ uploadedFiles.length }} uploaded, </template
							>{{ retryingFiles.length }} still uploading<template v-if="finishingFiles.length > 0"
								>, {{ finishingFiles.length }} finishing</template
							><template v-if="handedOffFiles.length > 0"
								>, {{ handedOffFiles.length }} finishing in the background</template
							>
						</h5>
					</div>
					<div v-else-if="finishingFiles.length > 0" class="text-h5">
						<h5 class="text-black">
							<template v-if="uploadedFiles.length > 0">{{ uploadedFiles.length }} uploaded, </template
							>{{ finishingFiles.length }} finishing<template v-if="handedOffFiles.length > 0"
								>, {{ handedOffFiles.length }} finishing in the background</template
							>
						</h5>
					</div>
					<div v-else-if="handedOffFiles.length > 0" class="text-h5">
						<h5 class="text-black">
							<template v-if="uploadedFiles.length > 0">{{ uploadedFiles.length }} uploaded, </template
							>{{ handedOffFiles.length }} finishing in the background
						</h5>
					</div>
					<div v-else class="text-h5">
						<h5 class="text-black">{{ uploadedFiles.length }} item{{ uploadedFiles.length === 1 ? "" : "s" }} uploaded</h5>
					</div>
					<span v-if="isOffline" class="text-body-2 font-weight-medium text-warning"
						>No internet connection &mdash; uploads paused</span
					>
					<span v-if="!isOffline && hasFilesPreparing" class="text-body-2 font-weight-medium text-warning"
						>Please keep this page open and visible while we prepare your files to be uploaded.</span
					>
					<span v-if="!isOffline && showLongPreparationHint" class="text-body-2 text-medium-emphasis"
						>Files stored in iCloud or another service may need to download to this device before uploading. Large files can take a while.</span
					>
					<span
						v-if="!isOffline && !hasFilesPreparing && verifyingOrphanFiles.length === 0 && retryingFiles.length > 0"
						class="text-body-2 font-weight-medium text-warning"
						>Please keep this page open while we retry your uploads.</span
					>
					<span v-if="!isOffline && !hasFilesPreparing && verifyingOrphanFiles.length > 0" class="text-body-2 font-weight-medium text-warning"
						>We found an unfinished upload from before — please keep this page open while we resume it shortly.</span
					>
				</div>
				<v-icon class="text-h5 text-black" @click="closeDialog">icon-close-medium</v-icon>
			</div>
			<div
				class="d-flex flex-column file-upload-list"
				style="
					gap: 16px;
					max-height: 350px;
					overflow-y: auto;
					overflow-x: hidden;
					width: 100%;
					scrollbar-gutter: stable;
					padding-right: 12px;
				">
				<template v-for="(file, index) in allDisplayFiles" :key="index">
					<div
						:data-test-id="`file-upload-row-${file.sessionId || file.uploadId || file.name}`"
						:data-upload-file-identity="file.sessionId || file.uploadId || file.name"
						class="file-upload-row rounded-xl pa-2 overflow-hidden"
						style="border: 1px solid black; width: 100%">
						<div class="d-flex flex-column" style="width: 100%">
							<div class="d-flex align-center w-100 overflow-hidden" style="width: 100%">
								<v-icon class="mr-2 text-grey-darken-1" :size="40">{{ getIcon(file.type, file.name) }}</v-icon>
								<div
									class="d-flex flex-column mb-1 file-info-container text-black overflow-hidden"
									style="min-width: 0; flex: 1">
									<div class="text-truncate" style="max-width: 100%">{{ file.name }}</div>
									<span v-if="file.failed" class="text-subtitle-3 text-error">{{
										file.failureMessage || "Upload failed"
									}}</span>
									<span v-else-if="file.cancelPending" class="text-subtitle-3 text-warning">Cancellation pending…</span>
									<span
										v-else-if="file.verifyingOrphan"
										v-test="'upload-row-verifying-orphan'"
										class="text-subtitle-3 text-medium-emphasis"
										>Please wait, we'll resume this upload shortly…</span
									>
									<span
										v-else-if="file.needsAttention"
										v-test="'upload-row-needs-attention'"
										class="text-subtitle-3"
										:class="file.refreshNotice ? 'text-info' : 'text-warning'"
										>{{ file.failureMessage || stoppedUploadMessage(undefined, file.retryCeilingReached) }}</span
									>
									<span v-else-if="file.verificationPending" v-test="'upload-row-finishing'" class="text-subtitle-3 text-medium-emphasis">Finishing upload…</span>
									<span v-else-if="file.retrying" v-test="'upload-row-retrying'" class="text-subtitle-3 text-warning">{{
										file.failureMessage || "We'll keep retrying automatically."
									}}</span>
									<span
										v-else-if="file.handedOff || file.deferred || file.ownershipUncertain"
										class="text-subtitle-3 text-medium-emphasis"
										>Finishing in the background…</span
									>
									<span v-else-if="file.waitingForServer && file.isPreparing" class="text-subtitle-3 text-medium-emphasis"
										>Waiting for the server to start this upload…</span
									>
									<span v-else-if="file.isPreparing && !isAwaitingUploadTurn(file)" class="text-subtitle-3 text-medium-emphasis"
										>{{ file.preparationProgress === undefined ? "Accessing file…" : `Preparing… ${file.preparationProgress}%` }}</span
									>
									<span v-else-if="file.pendingCancel && file.progress < 100" class="text-subtitle-3 text-warning"
										>Cancel this upload?</span
									>
									<span v-else-if="file.queuedForUpload" class="text-subtitle-3 text-medium-emphasis"
										>Waiting to upload…</span
									>
									<span v-else v-test="'upload-row-size'" class="text-subtitle-3">{{ formatFileSize(file.size) }}</span>
									<span
										v-if="
											!file.isPreparing && !file.failed && !file.queuedForUpload && !file.retrying && !file.verificationPending &&
											!file.handedOff && !file.deferred && !file.ownershipUncertain &&
											!file.needsAttention && !file.verifyingOrphan && !file.pendingCancel &&
											!file.cancelPending && !file.cancelled && !file.retryCeilingReached &&
											!file.recoveryBlocked && file.progress < 100
										"
										class="text-subtitle-3 text-medium-emphasis">Uploading… {{ file.progress }}%</span>
								</div>
								<v-btn
									v-if="
										file.retryCeilingReached &&
										!file.recoveryBlocked &&
										!file.serverTerminal &&
										!file.cancelled &&
										!file.cancelPending
									"
									v-test="'upload-row-retry-parked'"
									size="small"
									variant="text"
									color="primary"
									class="flex-shrink-0"
									:loading="file.retryInFlight"
									aria-label="Try this upload again"
									@click="retryParkedUpload(file)"
									>Try again</v-btn
								>
								<v-btn
									v-if="
										(file.failed || !!file.uploadTarget ||
											!!file.abortController ||
											file.queuedForUpload ||
											file.retryCeilingReached ||
											file.recoveryBlocked ||
											((file.deferred || file.ownershipUncertain || file.handedOff) && !!file.sessionId)) &&
										!file.cancelled &&
										!file.cancelPending &&
										(file.retryCeilingReached || file.progress < 100)
									"
									size="small"
									variant="text"
									:color="file.pendingCancel ? 'warning' : 'grey-darken-3'"
									class="flex-shrink-0"
									aria-label="Cancel upload"
									@click="requestCancel(file)">
									<v-icon class="icon-close-bold" :size="18">icon-close-medium</v-icon>
								</v-btn>
							</div>
							<v-progress-linear
								v-if="file.isPreparing && !file.failed && !isAwaitingUploadTurn(file)"
								:model-value="file.preparationProgress"
								:indeterminate="file.preparationProgress === undefined"
								color="green" />
							<v-progress-linear v-else-if="file.verifyingOrphan && !file.failed" indeterminate color="green" />
							<v-progress-linear v-else-if="file.verificationPending && !file.failed" indeterminate color="green" />
							<v-progress-linear
								v-else-if="
									file.progress < 100 &&
									!file.failed &&
									!file.handedOff &&
									!file.deferred &&
									!file.ownershipUncertain &&
									!file.localRecordLost &&
									!file.recoveryBlocked &&
									!file.verifyingOrphan
								"
								v-model="file.progress"
								color="green" />
							<div
								v-if="file.pendingCancel && (file.retryCeilingReached || file.progress < 100) && !file.cancelPending"
								class="mt-2 pa-3 rounded-xl bg-orange-lighten-5"
								style="border: 1px solid rgb(var(--v-theme-warning))">
								<p class="text-body-2 text-black mb-2">
									This will stop the upload. You'll need to re-upload the file if you change your mind.
								</p>
								<div class="d-flex justify-end ga-2">
									<v-btn size="small" variant="flat" class="bg-white text-black" rounded="xl" @click="dismissCancel(file)"
										>No, keep uploading</v-btn
									>
									<v-btn size="small" variant="flat" color="error" rounded="xl" @click="confirmCancel(file)"
										>Yes, cancel</v-btn
									>
								</div>
							</div>
						</div>
						<div class="d-flex align-center justify-start"></div>
					</div>
				</template>
			</div>
		</div>
	</v-snackbar>
</template>

<script lang="ts">
	import type { UploadDestination } from "@/types/FileUploadCounts";
	import { UNREADABLE_FILE_MESSAGE } from "@/helpers/fileValidation";
	import api from "@/api";
	import constants from "@/api/constants";
	import { getFilesByType, inferMimeType, ensureFileType } from "@/helpers/fileUploads";
	import { getAllUploadTargets } from "@/services/uploads/adminUploadTargets";
	import { PENDING_UPLOAD_TARGET_PREFIX } from "@/services/uploads/pendingUploadTarget";
	import { UploadProgressEvent, uploadToS3WithProgress } from "@/helpers/s3";
	import { reactive, toRaw } from "vue";
	import { liveQuery } from "dexie";
	import { useToast } from "vue-toastification";
	import { calculateTimeout, getMessageFromError } from "@/helpers/utilities";
	import { logError, logInfo } from "@/helpers/logError";
	import { EventBus } from "@/helpers/eventBus";
	import { useAuthSessionStore } from "@/stores/authSession";
	import {
		uploadFile as chunkedUploadFile,
		persistFileToIndexedDB,
		inferFileCategory,
		UploadCancelledError,
		ChunkNetworkError,
		isUploadLost,
		isUploadDeferredError,
		isUploadSessionDeliverableByResume,
		FileUnreadableError
	} from "@/services/chunkedUploadService";
	import {
		createFilePickerCaptureEvidence,
		drainCaptureEvidenceManifests,
		enqueueFileSessionCaptureEvidence,
		isCaptureEvidenceEnabled
	} from "@/services/captureEvidenceManifestService";
	import type { DirectUploadTarget, MultipartUploadTarget, UploadTarget } from "@/types/uploadTarget";
	import { buildLargeFileWarningMessage, type MobilePlatform } from "@common-ui/utilities/browserDetection";
	import { isUploadTerminalFailure, classifyUploadFailure, isTransportShapedS3Failure, PERSISTED_TERMINAL_REJECTION_CODE } from "@uploads";
	import Dialog from "@/components/Dialog.vue";
	import {
		ADMIN_LANE_INTENT_TTL_MS,
		deleteFileUploadSession,
		deleteChunkDataForSession,
		updateFileUploadSessionStatus,
		getFileUploadSessionByUploadId,
		getFileUploadSessionById,
		getFileUploadSessionsByIds,
		listFileUploadSessions,
		pruneCompletedUploadBatchHistory,
		db as uploadDb,
		isFileUploadRetryDue,
		getCurrentFileUploadLaneId,
		isUploadOwnerLockFree,
		hasUploadOwnerLockManager,
		updateFileUploadSession,
		getCurrentFileUploadSessionOwnerId,
		handoffFileUploadSessionToCancellation,
		markFileUploadSessionPendingForResume,
		releaseFileUploadLaneIntent,
		retryTerminallyParkedFileUploadSession,
		ensureUploadOwnerLivenessLockHeldForOwner,
		subscribeFileUploadSessionInvalidations,
		type FileUploadSession
	} from "@/db/fileUploadDb";
	import {
		createDeferredRowFollowUpRegistry,
		DEFERRED_ROW_FOLLOW_UP_LIFETIME_MS,
		DEFERRED_ROW_FOLLOW_UP_MAX_REATTEMPTS,
		type DeferredRowFollowUpRegistry
	} from "@/services/uploads/adminDeferredRowFollowUp";
	import {
		rememberUnpersistedUploadNotice,
		readUnpersistedUploadNotices,
		forgetUnpersistedUploadFile,
		readUploadBatchRecords,
		updateUploadBatchFile,
		forgetUploadBatchFile
	} from "@/services/uploads/unpersistedUploadNotices";
	import { readUploadRegistryForCompletionRefusal } from "@/services/uploads/adminUploadRegistryProbe";
	import {
		forgetLostUploadRecordNotice,
		readLostUploadRecordNotices,
		rememberLostUploadRecordNotice
	} from "@/services/uploads/lostUploadRecordNotices";
	import {
		PrePersistAbortUndecidedError,
		reconcilePrePersistTarget,
		type PrePersistTargetReconcileHandle
	} from "@/services/uploads/adminPrePersistTargetReconcile";
	import { uploadFailureLogDetails } from "@/services/uploads/uploadLogRedaction";
	import { stoppedUploadMessage } from "@/helpers/uploadErrorMessages";
	import { RECOVERY_BLOCKED_ROW_MESSAGE, uploadStartRetryMessage } from "@/services/uploads/adminUploadRecoveryCopy";
	import { backgroundUploadAttemptsInFlight } from "@/services/uploads/uploadActivity";
	import {
		requestUploadStoragePersistence as requestDashboardUploadStoragePersistence,
		type UploadStoragePersistenceOutcome
	} from "@/services/uploadStoragePersistenceService";
	import { subscribeAdminUploadLeaseClaims } from "@/services/uploads/adminUploadLease";
	import {
		beginAdminUploadBatch,
		endAdminUploadBatch,
		noteAdminUploadCompleted,
		type AdminUploadBatchToken
	} from "@/services/uploads/adminLeavingHint";
	import { cancelAdminUploadSession } from "@/services/uploads/adminUploadCancellation";
	import { isOwnPendingTargetPlaceholder } from "@/services/uploads/pendingUploadTarget";
	import {
		FILE_UPLOAD_CONCURRENCY,
		isUploadVerificationPending,
		formatFileSize,
		isMultipartUrlRefreshError,
		isUploadsUploadTarget,
		newUploadId,
		uploadTargetOpaqueId,
		useScreenWakeLock,
		withFileUploadConcurrencySlot,
		processWithConcurrency
	} from "@uploads";

	async function abortAdminMultipartTarget(target: MultipartUploadTarget, cancelled = true): Promise<void> {
		if (isUploadsUploadTarget(target)) {
			await (target.takeGeneration
				? api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "multipart", target.takeGeneration)
				: api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "multipart"));
			return;
		}
		const legacyTarget = target as { uploadId: string; s3Key: string; bucket: string };
		await api.abortMultipartUpload(legacyTarget.uploadId, legacyTarget.s3Key, legacyTarget.bucket, cancelled);
	}

	async function abortAdminDirectTarget(target: DirectUploadTarget): Promise<void> {
		if (target.family !== "uploads" || !target.registryId || !target.attemptId || !target.generation) return;
		await (target.takeGeneration
			? api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "direct-put", target.takeGeneration)
			: api.abortUploadsUpload(target.registryId, target.attemptId, target.generation, "direct-put"));
	}

	const PREPERSIST_ABORT_TIMEOUT_MS = 50;
	/**
	 * LSP-19865: how long the reconcile loop waits on one shared abort request before treating it as
	 * hung and minting a fresh one on its next tick. Shorter than the loop's own attempt bound so this
	 * wrapper, which owns the shared promise, is the one that decides to replace it.
	 */
	const PRE_PERSIST_RECONCILE_ABORT_WAIT_MS = 25_000;
	const LATE_DURABLE_SESSION_LOOKUP_TIMEOUT_MS = 5_000;
	type PrePersistAbortOutcome = "resolved" | "rejected";

	async function abortPrePersistTarget(
		target: MultipartUploadTarget,
		fileName: string,
		reason = "failed pre-persist"
	): Promise<PrePersistAbortOutcome> {
		const abortRequest = abortAdminMultipartTarget(target);
		return abortRequest.then(
			() => "resolved" as const,
			(error) => {
				console.warn(`[Upload] Multipart abort after ${reason} for "${fileName}" failed`, error);
				return "rejected" as const;
			}
		);
	}

	async function awaitPrePersistAbortBounded(
		abortPromise: Promise<PrePersistAbortOutcome>,
		fileName: string,
		reason: string
	): Promise<PrePersistAbortOutcome | "timed-out"> {
		const outcome = await Promise.race([
			abortPromise,
			new Promise<"timed-out">((resolve) => setTimeout(() => resolve("timed-out"), PREPERSIST_ABORT_TIMEOUT_MS))
		]);
		if (outcome === "timed-out") {
			console.warn(`[Upload] Multipart abort after ${reason} for "${fileName}" timed out; it continues in the background`);
		}
		return outcome;
	}

	/**
	 * LSP-19384: what a row says once the app has stopped retrying it on its own.
	 *
	 * The persisted diagnostic stays in storage. Select customer copy from its code and whether
	 * this row offers a useful retry; never interpolate the diagnostic into the row.
	 */
	function parkedFailureMessage(lastError?: string): string {
		return stoppedUploadMessage(lastError, !isUploadTerminalFailure(lastError));
	}

	/**
	 * LSP-19767 (data-loss): what a row says once this device has lost its record of the upload.
	 *
	 * No reason clause and no diagnostic string: there is no server verdict to report — the browser
	 * simply reclaimed the storage — and the only thing the customer can do about it is send the file
	 * again. It says that in the sentence rather than making them discover it by pressing Try again,
	 * which can only answer the same thing (`retryTerminallyParkedFileUploadSession` -> "no-local-bytes").
	 *
	 * TWO messages, because the two situations are not equally certain and only one of them may be
	 * stated as a fact (AI review, PR #4964). The registry answering "no upload with this identity
	 * exists" is proof; a probe that threw, was unreadable, or reported a not-yet-assembled status is
	 * doubt, and the server may already hold the finished file — a sibling tab completing the upload
	 * DELETES this shared row, which is one ordinary way this tab's watch finds it missing. Telling
	 * someone their file is gone in that case invites a duplicate upload, which is exactly the kind
	 * of untruth this whole state exists to avoid.
	 */
	const LOCAL_UPLOAD_RECORD_LOST_MESSAGES = {
		// Even the "absent" verdict is hedged about the SERVER, deliberately (AI review, PR #4964). The
		// API documents `UPLOAD_NOT_FOUND` as the same answer for an absent row, a lifecycle fence, and
		// a caller who can no longer see the target at all — so a project soft-deleted or access revoked
		// mid-upload reads as "gone" while the server may still hold the object. What we CAN state as
		// fact either way is the local half: this device cannot finish it.
		absent: "This file didn't finish uploading. Please check your files below — if it's missing, try uploading it again.",
		unverified: "We couldn't confirm this upload finished. Please check your files below — if it's missing, try uploading it again."
	} as const;

	/**
	 * LSP-19543: one foreground `openDialog` batch's claim on `activeUploadBatches`. Batch-scoped rather
	 * than component state because batches overlap — a second selection is started while the first is
	 * still uploading, which is the whole reason the completion toast needed gating.
	 */
	interface UploadBatchToken {
		destination?: UploadDestination;
		started: boolean;
		released: boolean;
		leavingHintBatch?: AdminUploadBatchToken;
		leavingHintFullyDrained: boolean;
	}

	/**
	 * LSP-19384: one durably parked upload, as the tray holds it. The drain reads these out of
	 * IndexedDB and hands them over; `readAt` is when it read, so a pass that lands out of order can be
	 * recognised as older rather than trusted.
	 */
	interface ParkedUploadSnapshot {
		sessionId: string;
		uploadId?: string;
		fileName: string;
		fileSize: number;
		fileType: string;
		status?: FileUploadSession["status"];
		lastError?: string;
		readAt?: number;
	}

	interface FileWithProgress {
		uploadBatchId?: string;
		batchFileIndex?: number;
		progress: number;
		isPreparing: boolean;
		preparationProgress?: number;
		sourcePrepared?: boolean;
		waitingForServer?: boolean;
		failed: boolean;
		failureMessage?: string;
		unreadable?: boolean;
		retryCount: number;
		name: string;
		size: number;
		type: string;
		lastModified: number;
		sessionId?: string;
		uploadId?: string;
		uploadOwnerId?: string;
		uploadOwnerClaimId?: string;
		pendingCancel: boolean;
		/** Durable cancel intent was recorded; keep the row visible until server acknowledgement. */
		cancelPending: boolean;
		/**
		 * LSP-22061: this tab wrote the row's "cancelled" tombstone itself while keeping the row on screen
		 * (the pre-persist reconcile's failed row). Its own cross-tab invalidation echo must not remove it.
		 */
		tombstonedLocally?: boolean;
		cancelled: boolean;
		deferred: boolean;
		queuedForUpload?: boolean;
		/** This page's batch lane still intends to upload this row. See `ownsQueuedUploadSession`. */
		laneOwned?: boolean;
		// True only while the transient-failure path is deciding whether recovery owns the upload.
		// Unlike deferred, resume-poll UI reconciliation must not clear this local cancellation lock.
		ownershipUncertain?: boolean;
		/**
		 * LSP-19766: the recurring drain has reported this session as orphaned by a prior page load but
		 * not yet reclaimable — `resetUploadingSessions`'s same-owner branch deliberately holds it back
		 * until FILE_UPLOAD_SESSION_OWNER_STALE_MS elapses, to avoid stealing a genuinely live duplicate
		 * tab's in-flight chunk. Presentation only, cleared once `openForResumedUploads` picks the row up
		 * for real or the drain reports it is no longer being held back.
		 */
		verifyingOrphan?: boolean;
		/**
		 * LSP-19766 (Bugbot): whether THIS row exists only because `showVerifyingOrphanedUpload`
		 * pushed a brand-new placeholder for it, as opposed to an existing real row (still queued,
		 * still preparing, not yet attached to a controller) that method merely flagged in place.
		 * `clearVerifyingOrphanedUpload` deletes a row outright only when this is true — deleting on
		 * `verifyingOrphan` alone would also delete a real row the flag was set on.
		 */
		verifyingOrphanRowCreatedByTray?: boolean;
		/** A timed-out durable lookup may still prove a resumable row; this is local UI state only. */
		awaitingLateSessionLookup?: boolean;
		// Set on a resumed file that couldn't finish locally (IDB data gone) but whose MPU is
		// still open — the server recovery sweep will complete it. Shown as "finishing in the
		// background", NOT as failed, and never asks the user to re-upload.
		handedOff?: boolean;
		/**
		 * LSP-19609: the deferred-row follow-up's absolute give-up instant for this entry. Set on the
		 * FIRST deferral and kept across later yields, so a row that is re-attempted and yields again
		 * does not restart its clock.
		 */
		deferredFollowUpDeadlineAt?: number;
		/** LSP-19609: how many times the follow-up has handed this row back to the lane. Budgeted. */
		deferredReattempts?: number;
		/**
		 * LSP-19867: set when "Row entered the finishing-in-the-background state" was logged for this
		 * entry and cleared when the matching "Row left" line is logged. The exit half keys on THIS, not
		 * on what the row renders at exit time, so the pair stays balanced however the row leaves.
		 */
		backgroundFinishingLoggedAt?: number;
		// Set on a row whose current attempt did not finish but whose session is still
		// resume-deliverable, so the durable retry drain will come back for it on its own.
		//
		// It exists for the same reason `handedOff` does: "not finished yet" is not "failed".
		// Before this flag the exhausted-retry ladder set `failed = true` while showing copy that
		// promised "we'll keep retrying automatically" — the row went red and was counted in the
		// header's "N uploads failed", which is supposed to mean rows that need the USER to act.
		// A retrying row shows its status in warning tone, is never red, and never inflates that
		// count. `failureMessage` carries the status text (shared with `failed` so one template
		// slot renders both).
		retrying?: boolean;
		verificationPending?: boolean;
		/**
		 * The row is durably parked as `needs-attention`: this page is not driving it, the retained
		 * bytes are still on disk, and the recurring requeue cycle will come back for it on a slow
		 * backoff — but nothing will finish it without something changing on the server side.
		 *
		 * Deliberately NOT `failed`. Red and "N uploads failed" are reserved for rows where the user
		 * did something wrong and has to act (an unsupported file type, a deterministic rejection).
		 * Nobody did anything wrong here — the upload's server-side target went away underneath a
		 * perfectly good file — so the row is warning-toned and reported in its own header bucket.
		 * Same carve-out `retrying` and `handedOff` already have, for the same reason.
		 *
		 * Also not `retrying`: that copy promises an automatic retry that will land, and for this
		 * state it will not. Saying so was the whole point of LSP-19325.
		 */
		needsAttention?: boolean;
		// LSP-19384: the app has stopped retrying this row on its own, and only the user can restart
		// it. The opposite end of `retrying` — that row is red and asks the user to act, this row's
		// copy must not promise a retry that will never come. The durable session and its bytes are
		// both still there, which is why this row gets Retry and Remove rather than just a message.
		retryCeilingReached?: boolean;
		/** Recovery proved the retained data cannot reconstruct this file; Cancel/reselect is the only useful action. */
		recoveryBlocked?: boolean;
		/**
		 * LSP-23172: the parked row's stored failure is the server's permanent verdict (`isUploadTerminalFailure`),
		 * the same test that switches its copy to "please email support". Try again cannot help it — the
		 * server answers every re-armed attempt with 404 UPLOAD_TERMINAL — so the row offers Remove only.
		 */
		serverTerminal?: boolean;
		/**
		 * LSP-19384: when the durable snapshot behind this parked row was READ. Only a strictly newer
		 * read may rewrite the row, because the drain does not await its discovery pass and two reads
		 * can land out of order. Set only on rows `showTerminallyParkedUploads` created.
		 */
		parkedReadAt?: number;
		/**
		 * LSP-19384: this row exists only because `showTerminallyParkedUploads` created it, so that
		 * method may delete it when the session stops being parked. A row it merely CONVERTED belongs to
		 * another mechanism and must survive un-parking with its progress and abort controller intact.
		 */
		parkedRowCreatedByTray?: boolean;
		/**
		 * LSP-19767 (data-loss): the browser evicted this upload's local record while it was in flight,
		 * and the upload registry could not prove the server assembled the file. Nothing is going to
		 * finish it and nothing is going to retry it.
		 *
		 * It reuses the LSP-19384 parked presentation (warning tone, its own header bucket, Try again
		 * and Remove) rather than inventing a second surface, but it needs its own flag for one reason:
		 * every OTHER parked row is derived from a durable snapshot, and this row is precisely the one
		 * that is no longer in any snapshot — so `projectParkedUploadsOntoRows` would un-park it on its
		 * very next pass and put it straight back to "Finishing in the background…", re-creating the
		 * silence this state exists to end.
		 */
		localRecordLost?: boolean;
		/**
		 * LSP-23086: this row is one of the two page-refresh notices ("page refreshed" / "another tab"). They
		 * explain what happened rather than report a problem, so they render blue. Every other writer that
		 * sets `needsAttention` clears it, so a row that later becomes a real problem turns yellow.
		 */
		refreshNotice?: boolean;
		/**
		 * LSP-19384: what a converted row looked like before it was parked, so un-parking restores its
		 * owner's presentation instead of leaving it stranded in a parked one nothing will clear.
		 */
		preParkedPresentation?: {
			failed: boolean;
			retrying?: boolean;
			needsAttention?: boolean;
			// Required, mirroring the row: capturing it as optional made restoring it a type error, and
			// widening the ROW to accept undefined would have been the wrong half to loosen.
			deferred: boolean;
			isPreparing: boolean;
			failureMessage?: string;
		};
		/** Set while this row's user-initiated Retry is in flight, so a double-click cannot double-arm. */
		retryInFlight?: boolean;
		releaseRetryTimer?: ReturnType<typeof setTimeout>;
		abortController?: AbortController;
		/** Shared with pre-persist cleanup so a user cancel cannot abort one MPU twice. */
		prePersistAbortPromise?: Promise<PrePersistAbortOutcome>;
		prePersistAbortOutcome?: PrePersistAbortOutcome;
		/**
		 * LSP-19865: the bounded loop that turns an unproven pre-persist cleanup (lookup rejected or
		 * timed out, abort rejected) into a definite abort or adoption once IndexedDB answers again.
		 */
		prePersistTargetReconcile?: PrePersistTargetReconcileHandle;
		uploadTarget?: import("@/types/uploadTarget").MultipartUploadTarget;
		/** An in-memory direct-put target; no IDB row exists in direct mode. */
		directUploadTarget?: DirectUploadTarget;
	}

	/**
	 * Silent-failure rule (CLAUDE.md "Diagnostic logging when fixing silent failures").
	 *
	 * Six different paths can put a row into the "Finishing in the background…" presentation — the
	 * template renders it for `handedOff || deferred || ownershipUncertain`, and the row looks
	 * identical whichever flag set it. Diagnosing a stuck row therefore meant reconstructing which
	 * path fired from indirect signals, every time. Name the path at the moment it commits, with the
	 * identifiers needed to correlate against the drain's own logs.
	 *
	 * Module-scope rather than a component method on purpose: the drain's specs drive the dialog
	 * through a facade that copies a hand-listed set of `methods` onto a plain object
	 * (`breakit-round73…`, `breakit-round74-1…`), so a `this.`-qualified diagnostic would be
	 * `undefined` there and would abort the very method it was meant to observe. These need no
	 * component state, so they take none.
	 */
	/**
	 * How long the batch may go with no observable progress before its rows stop being pinned away
	 * from the background drain. The pin exists so the drain does not upload a row this page's own
	 * lane is about to; if the lane stops existing in any way the `finally`s below cannot see — a
	 * pre-persist promise that never settles, an IndexedDB stall — the pin has to expire on its own,
	 * or the row is never uploaded by anyone (break-it round 1, angle A).
	 *
	 * Batch-wide rather than per-row on purpose: a row sitting 900 places back in a 1000-file queue
	 * makes no progress of its own for a long time and must stay pinned the whole while. What proves
	 * the lane is alive is that SOME row in the batch is moving.
	 */
	// LSP-19537: BATCH_LANE_STALL_MS is now the shared ADMIN_LANE_INTENT_TTL_MS (fileUploadDb.ts) —
	// this in-memory guard and the durable cross-tab lane lease share one TTL by construction.
	let batchLaneActivityAt = 0;
	function noteBatchLaneActivity() {
		batchLaneActivityAt = Date.now();
		void renewLaneLeaseIfDue();
	}

	let laneLeaseToken: unknown | undefined;
	let laneLeaseOperationChain: Promise<void> = Promise.resolve();
	let lastLaneLeaseRenewAt = 0;
	const ADMIN_LANE_INTENT_RENEW_MS = 60_000;

	function enqueueLaneLeaseOperation<T>(operation: () => Promise<T>): Promise<T> {
		const result = laneLeaseOperationChain.then(operation, operation);
		laneLeaseOperationChain = result.then(
			() => undefined,
			() => undefined
		);
		return result;
	}

	async function ensureLaneLeaseAcquired() {
		await enqueueLaneLeaseOperation(async () => {
			if (laneLeaseToken !== undefined) return;
			const laneId = getCurrentFileUploadLaneId();
			try {
				ensureUploadOwnerLivenessLockHeldForOwner(laneId);
				const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
				const ownership = await getAdminDurableOwnershipForCoordinator();
				if (!ownership) return;
				const acquiredToken = await ownership.acquireWriter({
					originKey: "contact-admin",
					identity: { itemId: `lane:${laneId}` },
					ownerId: `admin-lane:${getCurrentFileUploadSessionOwnerId()}:${laneId}`,
					ttlMs: ADMIN_LANE_INTENT_TTL_MS
				});
				if (!acquiredToken) {
					console.warn("[Upload] Lane lease acquisition did not apply; continuing without a durable lane lease", {
						laneId
					});
					return;
				}
				laneLeaseToken = acquiredToken;
			} catch (error) {
				console.warn("[Upload] Lane lease acquisition failed; continuing without a durable lane lease", {
					laneId,
					error
				});
			}
		});
	}

	async function renewLaneLeaseIfDue() {
		await enqueueLaneLeaseOperation(async () => {
			if (laneLeaseToken === undefined) return;
			const token = laneLeaseToken;
			const now = Date.now();
			if (now - lastLaneLeaseRenewAt < ADMIN_LANE_INTENT_RENEW_MS) return;
			lastLaneLeaseRenewAt = now;
			const laneId = getCurrentFileUploadLaneId();
			try {
				const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
				const ownership = await getAdminDurableOwnershipForCoordinator();
				if (!ownership) return;
				const renewed = await ownership.renewWriter({ token: token as never, ttlMs: ADMIN_LANE_INTENT_TTL_MS });
				if (!renewed) {
					if (laneLeaseToken === token) laneLeaseToken = undefined;
					console.warn("[Upload] Lane lease renewal did not apply; dropping the stale lease token", { laneId });
					return;
				}
				if (laneLeaseToken === token) laneLeaseToken = renewed;
			} catch (error) {
				console.warn("[Upload] Lane lease renewal failed; retaining the lease until its TTL lapses", {
					laneId,
					error
				});
			}
		});
	}

	async function releaseLaneLeaseIfHeld() {
		await enqueueLaneLeaseOperation(async () => {
			if (laneLeaseToken === undefined) return;
			const token = laneLeaseToken;
			laneLeaseToken = undefined;
			const laneId = getCurrentFileUploadLaneId();
			try {
				const { getAdminDurableOwnershipForCoordinator } = await import("@/services/uploads/adminUploadLease");
				const ownership = await getAdminDurableOwnershipForCoordinator();
				if (!ownership) return;
				const outcome = await ownership.releaseWriter(token as never);
				if (outcome.kind !== "applied") {
					console.warn("[Upload] Lane lease release did not apply; the lease stays held until its TTL lapses", { laneId });
				}
			} catch (error) {
				console.warn("[Upload] Lane lease release failed; the lease stays held until its TTL lapses", {
					laneId,
					error
				});
			}
		});
	}

	async function releaseLaneIntentBestEffort(sessionIds: readonly string[], phase: "row-finally" | "batch-finally" | "batch-preparation-failed") {
		const laneId = getCurrentFileUploadLaneId();
		try {
			await releaseFileUploadLaneIntent(sessionIds, laneId);
		} catch (error) {
			console.warn("[Upload] Failed to clear file-upload lane intent", {
				phase,
				sessionIds,
				laneId,
				error
			});
		}
	}

	function logBackgroundFinishingEntered(reason: string, file: Partial<FileWithProgress> | undefined, details?: Record<string, unknown>) {
		// `retrying` is checked BEFORE the handedOff/deferred/ownershipUncertain branch in the template
		// and before `handedOffFiles` in the header, so a row between attempts never actually shows this
		// presentation however many of those flags are also set. Claiming it entered a state it does not
		// render is exactly the misdirection these two diagnostics exist to remove.
		if (file?.retrying) return;
		// A re-entry while a stint is still open (a yielded row the drain then hands to the server sweep)
		// closes the open stint first, so the pair never reads "2 entered / 1 left" (AI review, PR review).
		if (file?.backgroundFinishingLoggedAt !== undefined) logBackgroundFinishingCleared(`superseded-by-${reason}`, file);
		try {
			console.warn("[Upload] Row entered the finishing-in-the-background state", {
				reason,
				sessionId: file?.sessionId,
				uploadId: file?.uploadId,
				fileName: file?.name,
				retryCount: file?.retryCount,
				...details
			});
			if (file) file.backgroundFinishingLoggedAt = Date.now();
		} catch {
			// A diagnostic must never change an upload's outcome.
		}
	}

	/**
	 * The exit half of `logBackgroundFinishingEntered`: what finally cleared the row, and when.
	 *
	 * LSP-19867: keyed on whether the ENTRY half logged for this row, not on the row's flags at exit
	 * time. The previous version returned early while `retrying` was set and required one of the
	 * finishing flags to still be up, so the two most common exits never logged: the drain picking a
	 * yielded row up (`markResumedFileRetrying` sets `retrying` together with `deferred`) and then
	 * completing it, and a cancellation clearing the flags first. A healthy recovery therefore read
	 * as "N entered / 0 left" — the exact log shape LSP-19609 was diagnosed from. Callers may invoke
	 * this on every exit route; a row whose entry never logged is a no-op, and a second call after the
	 * exit logged is a no-op too, so the pair is balanced by construction.
	 */
	function logBackgroundFinishingCleared(reason: string, file: Partial<FileWithProgress> | undefined) {
		if (!file || file.backgroundFinishingLoggedAt === undefined) return;
		const heldForMs = Math.max(0, Date.now() - file.backgroundFinishingLoggedAt);
		file.backgroundFinishingLoggedAt = undefined;
		try {
			console.warn("[Upload] Row left the finishing-in-the-background state", {
				reason,
				sessionId: file.sessionId,
				uploadId: file.uploadId,
				fileName: file.name,
				heldForMs,
				heldDeferred: file.deferred === true,
				heldHandedOff: file.handedOff === true,
				heldOwnershipUncertain: file.ownershipUncertain === true,
				nowRetrying: file.retrying === true
			});
		} catch {
			// A diagnostic must never change an upload's outcome.
		}
	}

	function trackPrePersistAbortOutcome(entry: FileWithProgress, abortPromise: Promise<PrePersistAbortOutcome>) {
		const tracked: Promise<PrePersistAbortOutcome> = abortPromise.then((outcome) => {
			// Only the request the entry CURRENTLY shares may record its outcome. A superseded request (a
			// hung abort the reconcile loop replaced, or a rejected one a retry replaced) can settle long
			// after its replacement confirmed the abort, and must not overwrite that confirmation
			// (Bugbot, PR review).
			if (entry.prePersistAbortPromise === tracked) entry.prePersistAbortOutcome = outcome;
			return outcome;
		});
		return tracked;
	}

	// Bounded so a long-lived tab cannot accumulate tombstones from unlimited cancellations in other tabs.
	const MAX_INVALIDATED_UPLOAD_SESSION_TOMBSTONES = 500;

	// A stalled S3 abort must never block the cross-tab "cancelled" notification — the local
	// cancellation is already final by this point, so a slow/hung network call only delays telling
	// other tabs, reintroducing the staleness bug this file exists to fix.
	const CONFIRM_CANCEL_ABORT_TIMEOUT_MS = 5000;
	const RESUMED_CANCEL_RETRY_DELAY_MS = 100;
	const RESUMED_CANCEL_RETRY_ATTEMPTS = 1;
	const EXHAUSTED_RELEASE_RETRY_DELAY_MS = 5000;

	export default {
		components: { Dialog },
		props: {
			/**
			 * LSP-22119: App.vue's measured shell chrome height (banners + app bar + session-lost banner).
			 * The snackbar is teleported to <body>, outside the v-app element App.vue sets
			 * --shell-header-height on, so it is re-applied on the snackbar root for the height cap.
			 */
			shellHeaderHeight: { type: String, default: undefined }
		},
		/**
		 * LSP-19384: emitted after the user's Try again has durably re-armed a parked upload. The tray
		 * cannot start the work itself — the drain owns that — and the drain may well be asleep, because
		 * a tray holding only parked rows has no pending work and therefore no armed timer. Without this
		 * the row vanishes from the tray on click and the upload does not begin until some unrelated
		 * wake, which reads to the user as "Try again does nothing" — the very complaint this fixes.
		 */
		emits: ["parked-upload-rearmed"],
		setup() {
			const toast = useToast();
			return { toast };
		},
		data() {
			return {
				screenWakeLock: useScreenWakeLock(),
				dialog: false as boolean,
				fileList: [] as FileWithProgress[],
				resumedFileList: [] as FileWithProgress[],
				maxRetryCount: 2,
				lastProgressLogTimestamp: 0 as number,
				progressLogInterval: 2500 as number,
				inProgressFiles: new Set<string>(),
				activeUploadBatches: 0,
				uploadWaveDestinations: [] as UploadDestination[],
				dismissedResumedSessionIds: new Set<string>(),
				invalidatedUploadSessionIds: new Set<string>(),
				// LSP-19384: when the user last resolved a parked upload themselves (Try again or Remove),
				// so an in-flight discovery snapshot READ before that click cannot resurrect the row.
				parkedUploadResolvedAt: new Map<string, number>(),
				// LSP-19384: the sessions automatic retry has given up on, and the authority for which
				// rows render as parked. The rows are a projection of this; see
				// `showTerminallyParkedUploads` for why this is state rather than a per-row flag alone.
				parkedUploads: new Map<string, ParkedUploadSnapshot>(),
				/**
				 * LSP-23172: upload ids whose Try again the store refused because their durable record carries the
				 * server's permanent verdict (proved inside the refusing write, so nothing is re-read). Kept by
				 * identity rather than in a snapshot because the verdict never reverses for that upload: no discovery
				 * read, however recent its read time, that still carries the older error may bring Try again back.
				 * Capped like the other in-memory tombstones here.
				 */
				serverTerminalUploadIds: new Set<string>(),
				/** The read time of the newest discovery pass applied, so an older one cannot revoke it. */
				parkedUploadsReadAt: undefined as number | undefined,
				confirmedAbsentInvalidatedUploadSessionIds: new Set<string>(),
				observedInvalidatedUploadSessionIds: new Set<string>(),
				// Sessions whose handed-off row this dialog has already flipped to a completed
				// presentation. Their IDB row is gone, so they can never appear in a resume snapshot
				// again — without remembering them, `shouldKeepExistingResumedEntry` would drop the row
				// on the very pass that completed it and the file would vanish instead of reading as
				// finished. Bounded like the invalidation tombstones so a long-lived tab cannot grow it
				// without limit.
				remoteCompletedResumedSessionIds: new Set<string>(),
				/** First-observation half of the handed-off absence confirmation described above. */
				observedAbsentHandedOffSessionIds: new Set<string>(),
				invalidatedUploadIdsBySessionId: new Map<string, string>(),
				activeSessionCleanupIdentities: new Set<string>(),
				quotaDialogVisible: false,
				quotaDialogMessage:
					"We cannot complete this upload with our normal safety measures turned on.<br><br>" +
					"If your internet stops, you may have to start again.<br><br>" +
					"Tip: It's best to stay on a strong Wi-Fi connection while this finishes.<br><br>" +
					"Would you still like to start this upload?",
				onQuotaDialogAgree: () => {},
				onQuotaDialogCancel: () => {},
				largeFileDialogVisible: false,
				largeFileDialogMessage: "",
				onLargeFileDialogAgree: () => {},
				onLargeFileDialogCancel: () => {},
				isOffline: !navigator.onLine,
				uploadStoragePersistenceRequested: false,
				uploadStoragePersistencePromise: undefined as Promise<UploadStoragePersistenceOutcome> | undefined,
				uploadStoragePersistenceConversationId: undefined as string | undefined,
				unsubscribeUploadLeaseClaims: undefined as undefined | (() => void),
				unsubscribeUploadSessionInvalidations: undefined as undefined | (() => void),
				/** LSP-19609: one watch per row this dialog handed to the background. See `followUpDeferredEntry`. */
				deferredRowFollowUps: undefined as undefined | DeferredRowFollowUpRegistry,
				/** LSP-19865: every armed pre-persist reconcile loop, so unmount can stop them. */
				prePersistTargetReconciles: [] as PrePersistTargetReconcileHandle[],
				isUnmounting: false,
				showLongPreparationHint: false,
				longPreparationHintTimer: undefined as ReturnType<typeof setTimeout> | undefined
			};
		},
		computed: {
			snackbarStyle(): Record<string, string> | undefined {
				return this.shellHeaderHeight ? { "--shell-header-height": this.shellHeaderHeight } : undefined;
			},
			allDisplayFiles(): FileWithProgress[] {
				return [...this.resumedFileList, ...this.fileList];
			},
			remainingFiles() {
				return this.allDisplayFiles.filter(
					(file: FileWithProgress) =>
						file.progress < 100 &&
						!file.failed &&
						!file.needsAttention &&
						!file.retrying &&
						!file.cancelled &&
						!file.handedOff &&
						!file.deferred &&
						!file.ownershipUncertain &&
						!file.verifyingOrphan
				);
			},
			preparingFiles() {
				return this.remainingFiles.filter((file: FileWithProgress) => file.isPreparing && !this.isAwaitingUploadTurn(file));
			},
			activelyUploadingFiles() {
				return this.remainingFiles.filter(
					(file: FileWithProgress) =>
						!file.isPreparing && !file.queuedForUpload && !file.pendingCancel && !file.cancelPending &&
						!file.retryCeilingReached && !file.recoveryBlocked
				);
			},
			activeUploadHeading() {
				const count = this.remainingFiles.length;
				const items = `item${count === 1 ? "" : "s"}`;
				const preparingCount = this.preparingFiles.length;
				const uploadingCount = this.activelyUploadingFiles.length;
				const workingCount = preparingCount + uploadingCount;
				const workingItems = workingCount === count ? `${count} ${items}` : `${workingCount} of ${count} ${items}`;
				if (preparingCount > 0 && uploadingCount > 0) return `Preparing and uploading ${workingItems}`;
				if (preparingCount > 0) return `Preparing ${workingItems}`;
				if (uploadingCount > 0 || this.uploadedFiles.length > 0) return `${this.uploadedFiles.length} of ${this.allDisplayFiles.length} uploaded`;
				return `Upload status for ${count} ${items}`;
			},
			uploadedFiles() {
				return this.allDisplayFiles.filter(
					(file: FileWithProgress) =>
						file.progress >= 100 &&
						!file.failed &&
						// A parked row can sit at 100% — every part reached S3 and only the COMPLETION was
						// refused — so without this it would be counted as uploaded and the header would
						// claim a file the server does not have.
						!file.needsAttention &&
						!file.retrying &&
						!file.handedOff &&
						!file.deferred &&
						!file.ownershipUncertain
				);
			},
			failedFiles() {
				return this.allDisplayFiles.filter((file: FileWithProgress) => file.failed && !file.awaitingLateSessionLookup);
			},
			/**
			 * Rows durably parked as `needs-attention`. Reported separately from `failedFiles` so the
			 * header's "N uploads failed" keeps meaning "the user has to act", and rendered in warning
			 * tone rather than red: the customer did nothing wrong, the upload's server-side target went
			 * away underneath a perfectly good file. See the `needsAttention` flag for the full rule.
			 */
			needsAttentionFiles() {
				return this.allDisplayFiles.filter(
					(file: FileWithProgress) => file.needsAttention && !file.failed && !file.cancelled && !file.awaitingLateSessionLookup
				);
			},
			checkingFiles() {
				return this.allDisplayFiles.filter((file: FileWithProgress) => file.awaitingLateSessionLookup && !file.cancelled);
			},
			/**
			 * Rows that are between attempts: nothing is moving right now, but the app will come back
			 * for them on its own. Kept out of `remainingFiles` (so they do not hold the screen wake
			 * lock or read as "uploading now") and out of `failedFiles` (so the header's "N uploads
			 * failed" keeps meaning rows that need the USER to act) — the same carve-out `handedOff`
			 * already has.
			 */
			retryingFiles() {
				return this.allDisplayFiles.filter(
					(file: FileWithProgress) => file.retrying && !file.failed && !file.cancelled && !file.handedOff
				);
			},
			finishingFiles() {
				return this.allDisplayFiles.filter((file: FileWithProgress) => file.verificationPending && !file.failed && !file.cancelled);
			},
			/**
			 * Rows genuinely finishing without this page: the server sweep has them, or this page gave the
			 * row up. `retrying` is excluded because `retryingFiles` already reports those and the template
			 * renders them as retrying — counting one row in BOTH buckets is what let the header say
			 * "1 still uploading" while a second, genuinely handed-off row went unmentioned entirely
			 * (break-it round 1, angle C).
			 */
			handedOffFiles() {
				return this.allDisplayFiles.filter(
					// `needsAttention` excluded for the same reason `retrying` is: the template renders a
					// parked row from its own branch (checked BEFORE handedOff/deferred/ownershipUncertain),
					// so counting it here too would let the header claim the server is finishing a row that
					// nothing is currently going to finish. `ownershipUncertain` is deliberately not cleared
					// on the row itself — it doubles as a local cancellation lock — so the carve-out lives
					// here instead.
					(file: FileWithProgress) =>
						!file.verificationPending && !file.retrying && !file.needsAttention && (file.handedOff || file.deferred || file.ownershipUncertain)
				);
			},
			/**
			 * LSP-19766: rows the recurring drain has reported as orphaned by a prior page load but not
			 * yet reclaimable — `resetUploadingSessions`'s same-owner branch deliberately holds these back
			 * until FILE_UPLOAD_SESSION_OWNER_STALE_MS elapses, to avoid stealing a genuinely live
			 * duplicate tab's in-flight chunk. Before this, nothing rendered for the whole wait; this
			 * computed backs the interim "we'll resume this upload shortly" wait state.
			 */
			verifyingOrphanFiles() {
				return this.allDisplayFiles.filter(
					(file: FileWithProgress) => file.verifyingOrphan === true && !file.failed && !file.cancelled
				);
			},
			hasFilesPreparing(): boolean {
				return this.allDisplayFiles.some(
					(file: FileWithProgress) =>
						file.isPreparing &&
						!this.isAwaitingUploadTurn(file) &&
						file.progress < 100 &&
						!file.failed &&
						!file.retrying &&
						!file.cancelled &&
						!file.handedOff &&
						!file.deferred &&
						!file.ownershipUncertain
				);
			},
			hasActiveUpload(): boolean {
				return this.remainingFiles.length > 0 || this.hasFilesPreparing || this.checkingFiles.length > 0;
			}
		},
		watch: {
			hasFilesPreparing(isPreparing: boolean) {
				if (this.longPreparationHintTimer !== undefined) clearTimeout(this.longPreparationHintTimer);
				this.longPreparationHintTimer = undefined;
				this.showLongPreparationHint = false;
				if (isPreparing) {
					this.longPreparationHintTimer = setTimeout(() => {
						this.showLongPreparationHint = this.hasFilesPreparing;
						this.longPreparationHintTimer = undefined;
					}, 10_000);
				}
			},
			hasActiveUpload(hasActiveUpload: boolean) {
				if (hasActiveUpload) {
					this.screenWakeLock.start();
				} else {
					this.screenWakeLock.stop();
				}
			}
		},
		methods: {
			getEntrySessionIdentity(
				entry: FileWithProgress,
				uploadId = entry.uploadId ?? (entry.uploadTarget ? uploadTargetOpaqueId(entry.uploadTarget) : undefined)
			) {
				if (!uploadId) return undefined;
				return {
					uploadId,
					uploadOwnerId: entry.uploadOwnerId,
					uploadOwnerClaimId: entry.uploadOwnerClaimId
				};
			},
			deleteEntrySession(entry: FileWithProgress, uploadId?: string, options?: { notifyOtherTabs?: boolean }) {
				const identity = this.getEntrySessionIdentity(entry, uploadId);
				if (!entry.sessionId || !identity) return Promise.resolve(false);
				return deleteFileUploadSession(entry.sessionId, identity.uploadId, identity.uploadOwnerId, identity.uploadOwnerClaimId, {
					requireExactOwnerIdentity: true,
					notifyOtherTabs: options?.notifyOtherTabs
				});
			},
			deleteEntryChunkData(entry: FileWithProgress, uploadId?: string) {
				const identity = this.getEntrySessionIdentity(entry, uploadId);
				if (!entry.sessionId || !identity) return Promise.resolve();
				return deleteChunkDataForSession(entry.sessionId, identity);
			},
			async markEntrySessionCancelled(
				entry: FileWithProgress,
				uploadId?: string,
				options: { notifyOtherTabs?: boolean; lastError?: string } = { notifyOtherTabs: false },
				status: "cancelled" | "needs-attention" = "cancelled"
			): Promise<boolean> {
				const identity = this.getEntrySessionIdentity(entry, uploadId);
				if (!entry.sessionId || !identity) {
					console.warn(`[Upload] Could not mark upload session ${status}`, {
						reason: "no-identity",
						sessionId: entry.sessionId
					});
					return false;
				}
				const updated = await updateFileUploadSessionStatus(entry.sessionId, status, identity, {
					notifyOtherTabs: options.notifyOtherTabs ?? false,
					...(options.lastError !== undefined ? { lastError: options.lastError } : {})
				});
				if (!updated) {
					console.warn(`[Upload] Could not mark upload session ${status}`, {
						reason: "cas-mismatch",
						sessionId: entry.sessionId,
						uploadId: identity.uploadId
					});
				}
				return updated;
			},
			async markEntrySessionCancelledBounded(
				entry: FileWithProgress,
				uploadId: string | undefined,
				fileName: string
			): Promise<boolean> {
				// A stalled fallback tombstone must not hold the batch open after the bounded abort
				// handoff. The write remains attached so a late successful IDB operation still records
				// cancellation; its bytes are never deleted here.
				const markRequest = this.markEntrySessionCancelled(entry, uploadId, { notifyOtherTabs: true });
				let timeoutId: ReturnType<typeof setTimeout> | undefined;
				try {
					const outcome = await Promise.race([
						markRequest,
						new Promise<"timed-out">((resolve) => {
							timeoutId = setTimeout(() => resolve("timed-out"), PREPERSIST_ABORT_TIMEOUT_MS);
						})
					]);
					if (outcome === "timed-out") {
						void markRequest.catch((markError) =>
							console.warn(`[Upload] Background cancellation tombstone for "${fileName}" failed`, markError)
						);
						console.warn(
							`[Upload] Cancellation tombstone for "${fileName}" timed out after ${PREPERSIST_ABORT_TIMEOUT_MS}ms; bytes retained for recovery`
						);
						return false;
					}
					return outcome;
				} finally {
					if (timeoutId !== undefined) clearTimeout(timeoutId);
				}
			},
			async deleteEntrySessionBounded(entry: FileWithProgress, uploadId: string | undefined, fileName: string): Promise<boolean> {
				const deleteRequest = this.deleteEntrySession(entry, uploadId);
				let timeoutId: ReturnType<typeof setTimeout> | undefined;
				try {
					const outcome = await Promise.race([
						deleteRequest,
						new Promise<"timed-out">((resolve) => {
							timeoutId = setTimeout(() => resolve("timed-out"), PREPERSIST_ABORT_TIMEOUT_MS);
						})
					]);
					if (outcome === "timed-out") {
						void deleteRequest.catch((deleteError) =>
							console.warn(`[Upload] Background session delete for cancelled "${fileName}" failed`, deleteError)
						);
						console.warn(
							`[Upload] Session delete for cancelled "${fileName}" timed out after ${PREPERSIST_ABORT_TIMEOUT_MS}ms; retaining the row for cancellation cleanup`
						);
						return false;
					}
					return outcome;
				} finally {
					if (timeoutId !== undefined) clearTimeout(timeoutId);
				}
			},
			async cancelSessionRowAfterRacedRelease(sessionId: string, uploadId: string | undefined, fileName: string): Promise<void> {
				// A release-to-pending that raced a user cancel cleared the owner fields the cancel's own
				// CAS expected. Re-read the released row and hand it off with its full identity so a successor
				// claim that wins either side of the read is never cancelled.
				try {
					const session = await getFileUploadSessionById(sessionId);
					if (!session || session.status === "cancelled") return;
					if (!uploadId || session.status !== "pending" || session.uploadId !== uploadId) {
						console.warn("[Upload] Raced-release re-cancel declined — the released row identity changed", {
							sessionId,
							fileName
						});
						return;
					}
					const outcome = await handoffFileUploadSessionToCancellation(sessionId, {
						status: session.status,
						uploadId: session.uploadId,
						uploadOwnerId: session.uploadOwnerId,
						uploadOwnerClaimId: session.uploadOwnerClaimId,
						endpointFamily: session.endpointFamily,
						registryId: session.registryId,
						attemptId: session.attemptId,
						generation: session.generation,
						takeGeneration: session.takeGeneration,
						s3Key: session.s3Key,
						bucket: session.bucket
					});
					if (outcome !== "tombstoned" && outcome !== "already-cancelled") {
						console.warn("[Upload] Raced-release re-cancel declined — the released row identity changed", {
							sessionId,
							fileName
						});
					}
				} catch (recancelError) {
					console.warn(
						"[Upload] Raced-release re-cancel failed — the released row may be resumed by the drain despite the cancel",
						{
							sessionId,
							fileName,
							recancelError
						}
					);
				}
			},
			// `markEntrySessionNeedsAttention` was removed here under R-0 (owner directive 2026-08-26).
			// LSP-17798 added it because the driver's releasePending left an exhausted-retry session
			// "pending" while the UI claimed a permanent failure, so the resume flow silently re-uploaded
			// a file the user had been told to retry by hand. R-0 removes the contradiction from the
			// other side: the UI now says the app will keep retrying, and the durable auto-requeue
			// (fileUploadDb `requeueNeedsAttentionFileUploadSessions`) does exactly that. With no caller
			// left, keeping a helper whose only job is to terminalize a recoverable row would be an
			// invitation to re-introduce the parked state this directive abolished.
			showQuotaWarningDialog(): Promise<boolean> {
				return new Promise((resolve) => {
					this.onQuotaDialogAgree = () => {
						this.quotaDialogVisible = false;
						resolve(true);
					};
					this.onQuotaDialogCancel = () => {
						this.quotaDialogVisible = false;
						resolve(false);
					};
					this.quotaDialogVisible = true;
				});
			},
			confirmLargeFileWarning(largeFiles: File[], platform: MobilePlatform): Promise<boolean> {
				this.largeFileDialogMessage = buildLargeFileWarningMessage(largeFiles, platform);
				return new Promise((resolve) => {
					this.onLargeFileDialogAgree = () => {
						this.largeFileDialogVisible = false;
						resolve(true);
					};
					this.onLargeFileDialogCancel = () => {
						this.largeFileDialogVisible = false;
						resolve(false);
					};
					this.largeFileDialogVisible = true;
				});
			},
			async requestUploadStoragePersistence(conversationId?: string): Promise<UploadStoragePersistenceOutcome> {
				if (
					!this.uploadStoragePersistencePromise ||
					(conversationId !== undefined && conversationId !== this.uploadStoragePersistenceConversationId)
				) {
					this.uploadStoragePersistenceRequested = true;
					this.uploadStoragePersistenceConversationId = conversationId;
					const authSession = useAuthSessionStore();
					this.uploadStoragePersistencePromise = requestDashboardUploadStoragePersistence(
						conversationId,
						authSession.organizationId
					);
				}
				return this.uploadStoragePersistencePromise;
			},
			updateProgress(file: FileWithProgress) {
				file.progress = Math.min(file.progress + 1, 95);
			},
			async withFileUploadSlot(processor: () => Promise<void>): Promise<void> {
				await withFileUploadConcurrencySlot(processor);
			},
			emitUploadEvent(eventName: "UploadStarted" | "UploadFinished" | "UploadDeferred", ...args: unknown[]) {
				try {
					EventBus.$emit(eventName, ...args);
				} catch (error) {
					logError(`FileUploadDialog: ${eventName} listener failed; upload state preserved`, error);
				}
			},
			startUploadBatch(batch: UploadBatchToken) {
				// break-it round 1: one token is one claim on the count. Seeding the same token twice would
				// add two claims that `releaseUploadBatch` can only give back once, wedging the count above
				// zero — which now means no later batch can ever announce completion.
				if (batch.started) return;
				batch.started = true;
				const destination = batch.destination;
				const isNewDestination = destination && !this.uploadWaveDestinations.some((existing) =>
					existing.conversationId === destination.conversationId && existing.projectId === destination.projectId);
				if (isNewDestination) this.uploadWaveDestinations.push({ ...destination });
				const isFirstBatch = this.activeUploadBatches++ === 0;
				// A joining destination needs its own immediate status/poll signal. Re-announcing the
				// whole wave would also wake unrelated pages whose earlier batches already started.
				if (isFirstBatch || isNewDestination) {
					this.emitUploadEvent("UploadStarted", { destinations: destination ? [{ ...destination }] : [] });
				}
			},
			finishUploadBatch() {
				// break-it round 1: refuse to underflow. Reaching this with nothing in flight would emit an
				// "UploadFinished" no batch produced and leave the count negative, so the next real batch
				// would skip its "UploadStarted" and a batch could claim completion with siblings running.
				if (this.activeUploadBatches === 0) {
					console.warn("[Upload] finishUploadBatch called with no upload batch in flight; refusing to underflow the count", {
						tags: { method: "finishUploadBatch", file: "FileUploadDialog" }
					});
					return false;
				}
				if (--this.activeUploadBatches > 0) return false;
				// Clear before dispatch: a synchronous listener can start the next upload wave.
				const destinations = this.uploadWaveDestinations;
				this.uploadWaveDestinations = [];
				this.emitUploadEvent("UploadFinished", { destinations });
				return true;
			},
			/**
			 * LSP-19543: the only way a batch leaves `activeUploadBatches`, so the count is released exactly
			 * once however the batch ended — a normal return, or a throw caught by `openDialog`'s backstop.
			 * Returns true only for the batch that took the count to zero, i.e. the one entitled to speak for
			 * every upload that was in flight.
			 *
			 * A batch that never started is not released, so the backstop cannot decrement a count this batch
			 * never incremented (a throw above `startUploadBatch` would otherwise wedge it below zero and let
			 * a later batch claim completion while others are still running).
			 */
			releaseUploadBatch(batch: UploadBatchToken) {
				if (!batch.started || batch.released) return false;
				batch.released = true;
				return this.finishUploadBatch();
			},
			// LSP-17969: a bare re-export inside `methods` so the template can call it. The returned
			// string carries its own unit — do not append one at the call site.
			formatFileSize,
			stoppedUploadMessage,
			/**
			 * Reserve at pick, mint lazily: a row whose source is fully saved but whose upload has not been
			 * minted yet is waiting for an upload turn, not preparing — show it as "Waiting to upload…".
			 */
			isAwaitingUploadTurn(file: FileWithProgress): boolean {
				return file.isPreparing && file.queuedForUpload === true && file.sourcePrepared === true && !file.failed;
			},
			/**
			 * LSP-19543: `runUploadBatch` releases the in-flight batch count on each of its own exits, but a
			 * throw between `startUploadBatch()` and those exits would leave the count stuck above zero for
			 * the life of the page — wedging the "UploadFinished" event, the dialog auto-close and (now) the
			 * completion toast for every later batch. The backstop also covers unexpected errors before
			 * per-row preparation or result reconciliation. `releaseUploadBatch` is idempotent, so this
			 * backstop is a no-op for a batch that already exited normally.
			 *
			 * Known limit (break-it round 4, left open deliberately): `processWithConcurrency` fail-fasts, so
			 * a throw that escapes preparation rejects this batch while sibling lanes are still uploading, and
			 * the claim is given back with work still running. That is narrower than what it replaces — without
			 * the backstop the count wedges above zero and NO later batch can ever announce completion — but it
			 * does mean a batch started afterwards could announce while those orphaned lanes finish.
			 */
			async openDialog(
				files: File[],
				conversationId: string,
				conversationName: string,
				onFileUploaded?: (fileType: "image" | "document" | "video") => void,
				projectChecklistItemId?: string,
				projectId?: string | null
			) {
				const batch: UploadBatchToken = {
					started: false,
					released: false,
					leavingHintFullyDrained: false,
					destination: { conversationId, projectId }
				};
				try {
					return await this.runUploadBatch(
						batch,
						files,
						conversationId,
						conversationName,
						onFileUploaded,
						projectChecklistItemId
					);
				} catch (error) {
					console.error("[Upload] Upload batch threw before it could finish; releasing its in-flight batch count", error, {
						tags: { method: "openDialog", file: "FileUploadDialog", conversationId },
						extra: { activeUploadBatches: this.activeUploadBatches, batchStarted: batch.started }
					});
					throw error;
				} finally {
					this.releaseUploadBatch(batch);
					if (batch.leavingHintBatch) endAdminUploadBatch(batch.leavingHintBatch, batch.leavingHintFullyDrained);
				}
			},
			async runUploadBatch(
				batch: UploadBatchToken,
				files: File[],
				conversationId: string,
				conversationName: string,
				onFileUploaded?: (fileType: "image" | "document" | "video") => void,
				projectChecklistItemId?: string
			) {
				// Keep completed siblings while the selection still contains unfinished work.
				// A later selection after the whole receipt completes starts a fresh total.
				const selectionInProgress = this.allDisplayFiles.some(file => !file.failed && !file.cancelled && !this.uploadedFiles.includes(file));
				const completedRowsToKeep = new Set(selectionInProgress ? this.uploadedFiles.filter(file => !file.cancelled) : []);
				this.resumedFileList = this.resumedFileList.filter((f) => (f.progress < 100 || completedRowsToKeep.has(f)) && !f.failed);
				this.fileList = this.fileList.filter(
					(f) => (f.progress < 100 || completedRowsToKeep.has(f)) && (!f.failed || (f.uploadTarget && !f.sessionId && f.prePersistAbortOutcome !== "resolved"))
				);

				// Callers (useFileUploadFlow, ActionUploadPhoto) pre-validate files before calling openDialog,
				// so we skip redundant MIME/magic-bytes checks here and go straight to preparation.
				// Reorder files to [images, documents, videos] to match getAllUploadTargets return order.
				const normalizedFiles = files.map(ensureFileType);
				const { images: validImages, documents: validDocuments, videos: validVideos } = getFilesByType(normalizedFiles);
				const batchFiles = [...validImages, ...validDocuments, ...validVideos];
				const uploadBatchId = newUploadId();
				const batchOrganizationId = useAuthSessionStore().organizationId ?? "";
				// LSP-22950 O8: stamped with the organization so the background drain never mints this row under another user.
				const batchUserId = useAuthSessionStore().userId ?? undefined;
				// Admission is synchronous: a reload during the first source read still has every name.
				if (batchFiles.length && !rememberUnpersistedUploadNotice({
					conversationId, uploadBatchId, organizationId: batchOrganizationId,
					laneId: getCurrentFileUploadLaneId(), createdAt: Date.now(),
					files: batchFiles.map((file, index) => ({ index, name: file.name, size: file.size, type: file.type }))
				})) {
					this.toast.error("We couldn't save this upload batch. Finish or remove earlier uploads, or free some browser storage, then re-add your files.");
					return { success: false, counts: null };
				}
				const laneLeaseReadyPromise = batchFiles.length > 0 ? ensureLaneLeaseAcquired() : undefined;
				const storagePersistencePromise = batchFiles.length > 0 ? this.requestUploadStoragePersistence(conversationId) : undefined;
				const batchEntries: FileWithProgress[] = batchFiles.map((file: File, batchFileIndex: number) =>
					reactive({
						uploadBatchId,
						batchFileIndex,
						progress: 0,
						isPreparing: true,
						preparationProgress: undefined,
						sourcePrepared: false,
						name: file.name,
						size: file.size,
						type: file.type,
						lastModified: file.lastModified,
						failed: false,
						retryCount: 0,
						pendingCancel: false,
						cancelPending: false,
						cancelled: false,
						deferred: false,
						queuedForUpload: true,
						laneOwned: true,
						ownershipUncertain: false,
						awaitingLateSessionLookup: false
					})
				);
				// break-it: admin surface — getFilesByType silently drops a file whose inferred MIME type
				// matches none of image/document/video (e.g. an unrecognized extension with no browser-set
				// file.type, reachable via drag-and-drop past the picker's accept filter). Without this, the
				// file vanished from fileList entirely — no failed row, no error, no diagnostic — leaving the
				// user's displayed count silently short of what they picked. Surface it as an explicit failed
				// entry instead.
				const uncategorizedFiles = normalizedFiles.filter((file) => !batchFiles.includes(file));
				if (uncategorizedFiles.length > 0) {
					console.warn(
						`[Upload] ${uncategorizedFiles.length} file(s) matched no supported category (image/document/video) and were excluded`,
						{
							tags: { method: "openDialog", file: "FileUploadDialog" },
							extra: { fileNames: uncategorizedFiles.map((f) => f.name) }
						}
					);
				}
				const unsupportedEntries: FileWithProgress[] = uncategorizedFiles.map((file) =>
					reactive({
						progress: 0,
						isPreparing: false,
						name: file.name,
						size: file.size,
						type: file.type,
						lastModified: file.lastModified,
						failed: true,
						failureMessage: "This file type isn't supported.",
						retryCount: 0,
						pendingCancel: false,
						cancelPending: false,
						cancelled: false,
						deferred: false
					})
				);
				if (unsupportedEntries.length > 0) {
					const message =
						unsupportedEntries.length === 1
							? `"${unsupportedEntries[0].name}" isn't a supported file type and wasn't uploaded.`
							: `${unsupportedEntries.length} files aren't a supported file type and weren't uploaded.`;
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
				noteBatchLaneActivity();
				this.fileList.push(...batchEntries, ...unsupportedEntries);

				// break-it round 2: a selection with nothing uploadable — every file an unsupported type, or an
				// empty list — has no upload in flight, so it must not claim a place in the in-flight count.
				// A claim held by a batch that can never announce anything swallows a concurrent real batch's
				// completion toast. The rest of the flow is unchanged: the dialog still opens on the
				// unsupported rows, and targets are still requested for the empty lists, which
				// breakit-round6-admin.uncategorized-dialog-actions asserts.
				if (batchFiles.length > 0) {
					this.startUploadBatch(batch);
					batch.leavingHintBatch = beginAdminUploadBatch(conversationId);
				}
				this.dialog = true;
				await laneLeaseReadyPromise;
				const storagePersistenceOutcome = await storagePersistencePromise;
				const persistConcurrency = storagePersistenceOutcome === "denied" && batchFiles.length >= 50 ? 1 : FILE_UPLOAD_CONCURRENCY;

				let uploadTargets: Array<UploadTarget | undefined> = new Array(batchFiles.length).fill(undefined);
				const readyTargetIndexes = new Set<number>();
				// Rows whose source `onPrepared` confirmed fully saved; rounded progress can read 100 one chunk early.
				const preparedSourceIndexes = new Set<number>();
				const deferUnreadyEntries = () => {
					for (const [index, entry] of batchEntries.entries()) {
						if (readyTargetIndexes.has(index)) continue;
						// LSP-23095: a saved source still holding its own placeholder has no server target yet. The drain
						// mints one later, so say so (path A's copy) instead of a reasonless "finishing in the background".
						const savedWithoutTarget =
							Boolean(entry.sessionId) &&
							preparedSourceIndexes.has(index) &&
							entry.uploadId?.startsWith(PENDING_UPLOAD_TARGET_PREFIX) === true &&
							!entry.failed &&
							!entry.cancelled;
						entry.isPreparing = false;
						entry.queuedForUpload = false;
						entry.laneOwned = false;
						entry.deferred = Boolean(entry.sessionId);
						entry.failed = !entry.sessionId;
						if (savedWithoutTarget) {
							startFailureIndexes.add(index);
							entry.retrying = true;
							entry.failureMessage = startFailureMessage;
						}
						// A sibling preparation failure must not erase an already recorded start failure.
						else if (!entry.sessionId || entry.failureMessage !== startFailureMessage) {
							entry.failureMessage = entry.sessionId ? undefined : "We couldn't save this file. Please re-add it to try again.";
						}
						if (entry.sessionId) void releaseLaneIntentBestEffort([entry.sessionId], "batch-preparation-failed");
					}
				};
				const targetResolvers: Array<() => void> = [];
				const targetPromises = batchFiles.map(() => new Promise<void>(resolve => targetResolvers.push(resolve)));
				// Reserve at pick, mint lazily: a row's upload turn (and the next file's S3 upload) is released only
				// when this row's transfer settles. Resolved in the row's own `finally` below, and for every row
				// once the batch ends, so no turn can outlive its row.
				const transferSettledResolvers: Array<() => void> = [];
				const transferSettledPromises = batchFiles.map(() => new Promise<void>(resolve => transferSettledResolvers.push(resolve)));
				let videoStatusUpdate: Promise<void> | undefined;
				const updateVideoStatus = () => {
					videoStatusUpdate ??= (async () => {
						try {
							await api.updateConversationStatus(conversationId, constants.CONVERSATION_STATUS.InProgress);
						} catch (statusError) {
							console.error(statusError, {
								tags: { method: "openDialog", file: "FileUploadDialog", phase: "updateConversationStatus", conversationId },
								extra: { attemptedStatus: constants.CONVERSATION_STATUS.InProgress }
							});
						}
					})();
					return videoStatusUpdate;
				};

				const initiateStartedAt = performance.now();
				logInfo("[Upload] FileUploadDialog: reserving upload targets", {
					conversationId,
					imageCount: validImages.length,
					documentCount: validDocuments.length,
					videoCount: validVideos.length
				});

				const startFailureIndexes = new Set<number>();
				const startFailureMessage = uploadStartRetryMessage("this upload");
				const targetPreparation = (async () => {
					const reservedTargets = await getAllUploadTargets(
						validImages, validDocuments, validVideos, conversationId, projectChecklistItemId,
						{
							batchId: uploadBatchId,
							uploadScope: { organizationId: batchOrganizationId, userId: batchUserId },
							captureEvidence: (file) => isCaptureEvidenceEnabled() ? createFilePickerCaptureEvidence(file) : undefined,
							persistenceConcurrency: persistConcurrency,
							isCancelled: (index) => batchEntries[index].cancelled,
							transferSettled: (index) => transferSettledPromises[index],
							onSessionCreated: async (index, session) => {
								Object.assign(batchEntries[index], { sessionId: session.id, uploadId: session.uploadId, uploadOwnerId: session.uploadOwnerId, uploadOwnerClaimId: session.uploadOwnerClaimId });
								if (!updateUploadBatchFile(uploadBatchId, index, { sessionId: session.id })) throw new Error("Could not save the upload batch session identity");
								await updateFileUploadSession(session.id, { batchOrganizationId, batchUserId, batchFileIndex: index });
							},
							onTargetReady: async (index, target) => {
								batchEntries[index].waitingForServer = false;
								try {
									if (!batchEntries[index].isPreparing || !batchEntries[index].laneOwned) {
										console.info("[Upload] Late target readiness skipped after lane release", {
											conversationId, sessionId: batchEntries[index].sessionId, fileIndex: index,
											deferred: batchEntries[index].deferred, failed: batchEntries[index].failed
										});
										// This row now holds an upload turn until its transfer settles; let its row run (and
										// settle) now rather than wait for a preparation worker queued behind unminted rows.
										preparationResolvers[index]();
										return;
									}
									readyTargetIndexes.add(index);
									uploadTargets[index] = target;
									// Keep the best-effort video status flip before transfer, once per batch.
									if (index >= validImages.length + validDocuments.length) await updateVideoStatus();
									// Reuse the existing per-row preparation and guards without waiting for sibling targets.
									await prepareEntry(batchFiles[index], index);
								} finally {
									targetResolvers[index]();
								}
							},
							onTargetStarting: (index) => {
								batchEntries[index].waitingForServer = true;
							},
							onPrepared: (index, session) => {
								preparedSourceIndexes.add(index);
								Object.assign(batchEntries[index], { sessionId: session.id, uploadId: session.uploadId, uploadOwnerId: session.uploadOwnerId, uploadOwnerClaimId: session.uploadOwnerClaimId, preparationProgress: 100, sourcePrepared: true });
								updateUploadBatchFile(uploadBatchId, index, { sessionId: session.id, persisted: true });
							},
							onPreparationProgress: (index, persisted, total) => {
								noteBatchLaneActivity();
								batchEntries[index].preparationProgress = Math.min(99, Math.round(persisted / total * 100));
							},
							onFailed: (index, error) => {
								const unreadable = error instanceof FileUnreadableError;
								const failureMessage = unreadable ? UNREADABLE_FILE_MESSAGE : "We couldn't save this file for upload. Please re-add it to try again.";
								if (unreadable) this.toast.error(UNREADABLE_FILE_MESSAGE, { timeout: calculateTimeout(UNREADABLE_FILE_MESSAGE) });
								Object.assign(batchEntries[index], { failed: true, unreadable, isPreparing: false, queuedForUpload: false, failureMessage });
								updateUploadBatchFile(uploadBatchId, index, { failureMessage });
								targetResolvers[index]();
							},
							onDeferred: (index, session) => {
								batchEntries[index].waitingForServer = false;
								if (session.targetPending) {
									startFailureIndexes.add(index);
									batchEntries[index].retrying = true;
									batchEntries[index].failureMessage = startFailureMessage;
								}
								Object.assign(batchEntries[index], { sessionId: session.id, uploadId: session.uploadId, uploadOwnerId: session.uploadOwnerId, uploadOwnerClaimId: session.uploadOwnerClaimId, isPreparing: false, deferred: true, queuedForUpload: false });
								targetResolvers[index]();
							}
						}
					);
					logInfo("[Upload] FileUploadDialog: upload targets reserved", {
						conversationId,
						videoCount: validVideos.length,
						targetCount: reservedTargets?.length ?? 0,
						elapsedMs: Math.round(performance.now() - initiateStartedAt)
					});

					if (validVideos?.length > 0 && reservedTargets?.slice(validImages.length + validDocuments.length).some(Boolean)) {
						// LSP-16640: flip the conversation to InProgress via the dedicated thin, txn-free status
						// endpoint (idempotent + retry/backoff) instead of the heavyweight updateConversation,
						// whose row-lock-held side-effect graph caused H12 timeouts when many uploaders hit the
						// same conversation at once.
						//
						// LSP-16617: the status flip is BEST-EFFORT — a transient 503/H12 that survives the API
						// method's own retries must NOT abort the per-file chunked uploads (previously this lived
						// inside the fatal try above, so a single timeout discarded the entire ~50-file batch and
						// forced the user to re-select every file). On failure we log and continue; the files still
						// upload. Losing this flip only skips the transient InProgress indicator — the upload-completion
						// worker advances the conversation to its terminal review status regardless, so no state is
						// permanently lost.
						await updateVideoStatus();
					}

					uploadTargets = reservedTargets;
					for (const [index, target] of uploadTargets.entries()) {
						if (!batchEntries[index]?.sessionId && target) updateUploadBatchFile(uploadBatchId, index, { target });
					}

				})().catch(e => {
					// Ready rows may already be transferring; only untouched rows can yield to the drain.
					const message = getMessageFromError(e);
					this.toast.error(message, { timeout: calculateTimeout(message) });
					console.error(e, {
						tags: { method: "openDialog", file: "FileUploadDialog", phase: "getAllUploadTargets", conversationId }
					});
					deferUnreadyEntries();
					return { success: false, counts: null };
				}).finally(() => {
					for (const [index, resolve] of targetResolvers.entries()) {
						if (!readyTargetIndexes.has(index) || !batchEntries[index].isPreparing) resolve();
					}
				});

				let quotaDecision: Promise<boolean> | undefined;
				let itemsUploaded = 0;
				// LSP-23174: the rows THIS batch uploaded, and the rows whose in-page retries THIS batch ran out of. The
				// end-of-batch tally needs both to tell a row the background drain took over (and may already have
				// finished) from one this batch gave up on, which stays a reported failure (LSP-17798 / R-0).
				const uploadedByBatchIndexes = new Set<number>();
				const exhaustedByBatchIndexes = new Set<number>();
				const uploadedCounts = { images: 0, documents: 0, videos: 0 };
				const categoryForIndex = (index: number): "images" | "documents" | "videos" => {
					if (index < validImages.length) return "images";
					if (index < validImages.length + validDocuments.length) return "documents";
					return "videos";
				};
				const persistPhaseStart = performance.now();
				const preparationResolvers: Array<() => void> = [];
				const preparationRejecters: Array<(error: unknown) => void> = [];
				const preparationPromises = batchFiles.map(
					() =>
						new Promise<void>((resolve, reject) => {
							preparationResolvers.push(resolve);
							preparationRejecters.push(reject);
						})
				);
				const prepareEntry = async (file: File, fileIndex: number) => {
						const target = uploadTargets[fileIndex];
						if (!target) {
							batchEntries[fileIndex].isPreparing = false;
							preparationResolvers[fileIndex]();
							return;
						}
						if (target.type === "direct" && target.family === "uploads") {
							batchEntries[fileIndex].directUploadTarget = target;
						}
						if (target.type !== "multipart" || !conversationId) {
							batchEntries[fileIndex].isPreparing = false;
							preparationResolvers[fileIndex]();
							return;
						}
						// Persisting is the lane working. Without this the stamp only advances once an upload
						// starts ticking progress, so a long enough persist phase looks stalled and drops the pin
						// on rows the batch is about to upload (Bugbot, PR #4588).
						noteBatchLaneActivity();
						const filePersistedStart = performance.now();
						try {
							batchEntries[fileIndex].uploadTarget = target as MultipartUploadTarget;
							// A target-ready row already has its own durable source; never rebuild it after a lookup miss.
							const sessionId = batchEntries[fileIndex].sessionId ?? await persistFileToIndexedDB(
								file,
								target as MultipartUploadTarget,
								conversationId,
								inferFileCategory(inferMimeType(file)),
								projectChecklistItemId,
								uploadBatchId,
								(chunksPersisted, totalChunks) => {
									batchEntries[fileIndex].preparationProgress = Math.min(99, Math.round((chunksPersisted / totalChunks) * 100));
									noteBatchLaneActivity();
								},
								// LSP-18592 go-live: no provenance is attached unless me.config.featureFlags.capture_evidence is on.
								isCaptureEvidenceEnabled() ? createFilePickerCaptureEvidence(file) : undefined
							);
							forgetUnpersistedUploadFile(uploadBatchId, fileIndex);
							batchEntries[fileIndex].sessionId = sessionId;
							const persistedSession = await getFileUploadSessionById(batchEntries[fileIndex].sessionId!);
							if (persistedSession) {
								batchEntries[fileIndex].uploadId = persistedSession.uploadId;
								batchEntries[fileIndex].uploadOwnerId = persistedSession.uploadOwnerId;
								batchEntries[fileIndex].uploadOwnerClaimId = persistedSession.uploadOwnerClaimId;
							}
							// Reuses the identity read above rather than issuing a second one: the ownership CAS
							// gates in this lane count getFileUploadSessionById reads, and an extra read here
							// would consume a distinct row observation those gates rely on (LSP-17798).
							if (isCaptureEvidenceEnabled()) {
								try {
									if (persistedSession) await enqueueFileSessionCaptureEvidence(persistedSession);
									void drainCaptureEvidenceManifests().catch((error) =>
										console.warn("[CaptureEvidence] Admin manifest drain failed", error)
									);
								} catch (error) {
									console.warn("[CaptureEvidence] Optional manifest persistence failed", { sessionId, error });
								}
							}
							// Checked only now that this entry's real persisted identity is known — checking any
							// earlier (before uploadId is populated) can only ever conservatively over-cancel, since
							// an unknown local identity always matches per invalidateUploadSession's design. Passing
							// the tombstone's recorded uploadId lets a genuinely different, legitimately-reused
							// session survive instead of being incorrectly cancelled before its identity loads.
							if (this.invalidatedUploadSessionIds.has(sessionId)) {
								this.invalidateUploadSession(sessionId, this.invalidatedUploadIdsBySessionId.get(sessionId));
							}
							const filePersistedDuration = ((performance.now() - filePersistedStart) / 1000).toFixed(1);
							console.info(`[Upload] "${file.name}" — pre-persist complete in ${filePersistedDuration}s`);
							// break-it: admin surface — confirmCancel can run WHILE this persist is still in
							// flight; at that moment entry.sessionId is still unset so confirmCancel's own
							// cleanup has nothing to delete and never runs again. Once the session/MPU lands
							// here after the fact, clean it up immediately instead of leaving an orphaned S3
							// multipart upload + IDB session that the resume poller could otherwise resurrect.
							if (batchEntries[fileIndex].cancelled) {
								console.info(
									`[Upload] "${file.name}" was cancelled while persisting — cleaning up the now-persisted session`
								);
								try {
									const abortPromise = (batchEntries[fileIndex].prePersistAbortPromise ??= trackPrePersistAbortOutcome(
										batchEntries[fileIndex],
										abortPrePersistTarget(target as MultipartUploadTarget, file.name, "cancellation during pre-persist")
									));
									const abortCompleted = await awaitPrePersistAbortBounded(
										abortPromise,
										file.name,
										"cancellation during pre-persist"
									);
									if (abortCompleted !== "resolved") {
										await this.markEntrySessionCancelledBounded(batchEntries[fileIndex], undefined, file.name).catch(
											(markError) =>
												console.warn(
													`[Upload] markEntrySessionCancelled threw for cancelled-during-persist "${file.name}" — cross-tab notification may not have fired`,
													markError
												)
										);
										preparationResolvers[fileIndex]();
										return;
									}
									try {
										const sessionDeleted = await this.deleteEntrySessionBounded(batchEntries[fileIndex], undefined, file.name);
										if (!sessionDeleted) {
											await this.markEntrySessionCancelledBounded(batchEntries[fileIndex], undefined, file.name).catch(
												(markError) =>
													console.warn(
														`[Upload] markEntrySessionCancelled threw for cancelled-during-persist "${file.name}" — cross-tab notification may not have fired`,
														markError
													)
											);
										}
									} catch (idbError) {
										console.warn(
											`[Upload] Failed to delete IDB session for cancelled-during-persist "${file.name}" — marking session cancelled`,
											idbError
										);
										await this.markEntrySessionCancelledBounded(batchEntries[fileIndex], undefined, file.name).catch(
											(markError) =>
												console.warn(
													`[Upload] markEntrySessionCancelled threw for cancelled-during-persist "${file.name}" — cross-tab notification may not have fired`,
													markError
												)
										);
									}
								} catch (abortError) {
									console.warn(
										`[Upload] Failed to abort S3 multipart upload for cancelled-during-persist "${file.name}"`,
										abortError
									);
									await this.markEntrySessionCancelledBounded(batchEntries[fileIndex], undefined, file.name).catch((markError) =>
										console.warn(
											`[Upload] markEntrySessionCancelled threw for cancelled-during-persist "${file.name}" — cross-tab notification may not have fired`,
											markError
										)
									);
								}
								preparationResolvers[fileIndex]();
								return;
							}
						} catch (persistError) {
							if (persistError instanceof FileUnreadableError) {
								// Unreadable file (cloud-offloaded/stale). Fail it now with guidance; the
								// upload loop below skips entries already marked failed, so we never retry it.
								batchEntries[fileIndex].failed = true;
								batchEntries[fileIndex].failureMessage = UNREADABLE_FILE_MESSAGE;
								batchEntries[fileIndex].unreadable = true;
								// Surface the cloud-download guidance as a toast too (matching the upload-loop branch),
								// so the user gets the actionable message and not just the caller's generic
								// "unable to upload some files" toast.
								this.toast.error(UNREADABLE_FILE_MESSAGE, { timeout: calculateTimeout(UNREADABLE_FILE_MESSAGE) });
								console.error(`[Upload] "${file.name}" — unreadable file, not uploading`, {
									tags: { method: "openDialog", file: "FileUploadDialog", conversationId },
									extra: {
										fileName: file.name,
										fileType: file.type,
										reason: "FileUnreadable",
										// MPU coords so an "abort failed" report can be correlated to the orphan upload server-side.
										uploadId: batchEntries[fileIndex].uploadTarget
											? uploadTargetOpaqueId(batchEntries[fileIndex].uploadTarget)
											: undefined
									}
								});
								// The MPU was already created by getAllUploadTargets; tombstone it (cancelled:true) so the
								// server recovery sweep never tries to finalize an upload for a file the user must re-pick.
								const failedTarget = batchEntries[fileIndex].uploadTarget;
								if (failedTarget) {
									const abortPromise = (batchEntries[fileIndex].prePersistAbortPromise ??= trackPrePersistAbortOutcome(
										batchEntries[fileIndex],
										abortPrePersistTarget(failedTarget, file.name, "unreadable-file cleanup")
									));
									await awaitPrePersistAbortBounded(abortPromise, file.name, "unreadable-file cleanup");
								}
							} else {
								if (persistError instanceof Error && persistError.message.startsWith("QuotaExceeded:")) {
									console.warn(
										`[Upload] IDB quota exceeded for "${file.name}" — asking whether to upload without resume support`
									);
									quotaDecision ??= this.showQuotaWarningDialog();
									if (!(await quotaDecision)) {
										batchEntries[fileIndex].cancelled = true;
										this.fileList = this.fileList.filter((entry) => toRaw(entry) !== toRaw(batchEntries[fileIndex]));
										console.warn(`[Upload] User cancelled unsafe upload "${file.name}" after quota warning`);
										await awaitPrePersistAbortBounded(
											abortPrePersistTarget(target as MultipartUploadTarget, file.name, "quota cancellation"),
											file.name,
											"quota cancellation"
										);
									} else {
										console.warn(`[Upload] User confirmed unsafe upload "${file.name}" after quota warning`);
									}
								} else {
									// LSP-19865: every exit below that is NOT proof of a durable handoff and NOT a
									// confirmed abort arms this. It re-asks IndexedDB on a bounded loop and issues
									// the abort (or adopts the row) once the store answers — instead of leaving the
									// reserved multipart upload pending with zero parts for the server's 24 h window.
									const armPrePersistTargetReconcile = (reason: string) => {
										const entry = batchEntries[fileIndex];
										if (this.isUnmounting) return;
										// A loop that already FINISHED (gave up while the store stayed wedged, exhausted
										// its abort budget, was stopped) must not block a later unproven exit from
										// re-arming — the t=0 late lookup can reject long after a first loop gave up
										// (AI review). Only a loop still running holds the slot.
										if (entry.prePersistTargetReconcile?.outcome === undefined && entry.prePersistTargetReconcile)
											return;
										if (entry.cancelled) return;
										const multipartTarget = target as MultipartUploadTarget;
										let hungReconcileAbort: Promise<PrePersistAbortOutcome> | undefined;
										const handle = reconcilePrePersistTarget({
											uploadId: uploadTargetOpaqueId(multipartTarget),
											fileName: file.name,
											reason,
											lookupSession: (uploadId) => getFileUploadSessionByUploadId(uploadId),
											abortTarget: async () => {
												// Share the request through `prePersistAbortPromise` — the dialog's "one MPU,
												// one abort at a time" invariant — so a user cancel landing while this abort
												// is in flight joins it instead of firing a second, racing request (AI review).
												// A previously REJECTED request is replaced, not reused, so a retry is real; so
												// is a request this wrapper already gave up waiting on (a hung connection).
												if (entry.prePersistAbortOutcome === "rejected") {
													entry.prePersistAbortPromise = undefined;
													entry.prePersistAbortOutcome = undefined;
												}
												if (
													hungReconcileAbort !== undefined &&
													entry.prePersistAbortPromise === hungReconcileAbort
												) {
													entry.prePersistAbortPromise = undefined;
												}
												hungReconcileAbort = undefined;
												const sharedAbort = (entry.prePersistAbortPromise ??= trackPrePersistAbortOutcome(
													entry,
													abortPrePersistTarget(multipartTarget, file.name, "pre-persist target reconcile")
												));
												// Bounded: the abort request has no timeout of its own, and an unbounded await
												// here would stall the tick past the loop's lifetime and block any re-arm.
												let waitTimer: ReturnType<typeof setTimeout> | undefined;
												const outcome = await Promise.race([
													sharedAbort,
													new Promise<"timed-out">((resolve) => {
														waitTimer = setTimeout(
															() => resolve("timed-out"),
															PRE_PERSIST_RECONCILE_ABORT_WAIT_MS
														);
													})
												]).finally(() => {
													if (waitTimer !== undefined) clearTimeout(waitTimer);
												});
												if (outcome === "timed-out") {
													hungReconcileAbort = sharedAbort;
													// Undecided, not rejected: the request may still destroy the reservation, so the
													// loop must keep refusing adoption for this target.
													throw new PrePersistAbortUndecidedError();
												}
												if (outcome === "rejected")
													throw new Error("Multipart abort rejected; the reconcile loop will retry");
											},
											isDeliverableByResume: isUploadSessionDeliverableByResume,
											// A user cancel owns the abort from then on; an adopted row or a confirmed
											// abort (by the late lookup that landed after all) means nothing is left to do.
											isStillUnresolved: () =>
												!entry.cancelled &&
												entry.sessionId === undefined &&
												entry.prePersistAbortOutcome !== "resolved",
											onAdopt: (lateSession) => {
												// The loop re-checks isStillUnresolved after its read, but the dialog owns
												// these flags and guards them itself too: a cancelled entry is never re-adopted,
												// and an entry the late lookup already adopted is not adopted twice.
												if (entry.cancelled || entry.sessionId !== undefined) return;
												// Same adoption the late lookup performs: identify the resumable row for the
												// tray without authorizing this tab to take its lease.
												entry.sessionId = lateSession.id;
												entry.uploadId = lateSession.uploadId;
												entry.progress = Math.min(entry.progress, 99);
												entry.awaitingLateSessionLookup = false;
												entry.failed = false;
												entry.failureMessage = undefined;
												entry.deferred = true;
												entry.ownershipUncertain = false;
												logBackgroundFinishingEntered("dialog-pre-persist-reconcile-adopted", entry);
												this.emitUploadEvent("UploadDeferred", { sessionId: lateSession.id, conversationId });
											},
											onTombstone: async (deadSession) => {
												// The row is resumable on paper but its reservation is gone (or was being torn down):
												// record its identity so the tombstone has one, show the row as failed, and cancel
												// the durable session so no drain ever drives the dead target.
												entry.sessionId = deadSession.id;
												entry.uploadId = deadSession.uploadId;
												entry.uploadOwnerId = deadSession.uploadOwnerId;
												entry.uploadOwnerClaimId = deadSession.uploadOwnerClaimId;
												entry.awaitingLateSessionLookup = false;
												entry.deferred = false;
												entry.failed = true;
												entry.failureMessage = "Upload couldn't be prepared. Please try again.";
												// Set before the write: its notifyOtherTabs broadcast is delivered back to this tab too.
												entry.tombstonedLocally = true;
												await this.markEntrySessionCancelledBounded(entry, undefined, file.name).catch(
													(markError) =>
														console.warn(
															`[Upload] markEntrySessionCancelled threw for the tombstoned pre-persist row "${file.name}"`,
															markError
														)
												);
											},
											onAborted: () => {
												entry.prePersistAbortOutcome = "resolved";
												entry.prePersistAbortPromise = Promise.resolve("resolved" as const);
											},
											log: (event, details) =>
												console.warn("[Upload] Pre-persist target reconcile", { event, ...details })
										});
										entry.prePersistTargetReconcile = handle;
										this.prePersistTargetReconciles.push(handle);
									};
									let durableSession: FileUploadSession | undefined;
									let durableSessionLookupFailed = false;
									let durableSessionLookupTimedOut = false;
									try {
										const sessionLookup = getFileUploadSessionByUploadId(
											uploadTargetOpaqueId(target as MultipartUploadTarget)
										);
										let lookupTimeout: ReturnType<typeof setTimeout> | undefined;
										const lookupOutcome = await Promise.race([
											sessionLookup.then((session) => ({ session })),
											new Promise<{ timedOut: true }>((resolve) => {
												lookupTimeout = setTimeout(() => resolve({ timedOut: true }), PREPERSIST_ABORT_TIMEOUT_MS);
											})
										]);
										if (lookupTimeout) clearTimeout(lookupTimeout);
										if ("timedOut" in lookupOutcome) {
											durableSessionLookupFailed = true;
											durableSessionLookupTimedOut = true;
											const markLateLookupFailed = async (
												confirmedAbsent = false,
												checkingDeadlineExpired = false
											) => {
												if (
													batchEntries[fileIndex].cancelled ||
													(!confirmedAbsent && !batchEntries[fileIndex].awaitingLateSessionLookup)
												) {
													return;
												}
												if (confirmedAbsent) {
													const abortPromise = (batchEntries[fileIndex].prePersistAbortPromise ??=
														trackPrePersistAbortOutcome(
															batchEntries[fileIndex],
															abortPrePersistTarget(
																target as MultipartUploadTarget,
																file.name,
																"late durable-session lookup absence"
															)
														));
													await awaitPrePersistAbortBounded(
														abortPromise,
														file.name,
														"late durable-session lookup absence"
													);
													void abortPromise.then((outcome) => {
														if (outcome === "rejected")
															armPrePersistTargetReconcile(
																"late durable-session lookup absence abort rejected"
															);
													});
												} else if (checkingDeadlineExpired) {
													armPrePersistTargetReconcile("late durable-session lookup deadline expired");
												}
												if (batchEntries[fileIndex].cancelled || !batchEntries[fileIndex].awaitingLateSessionLookup) return;
												batchEntries[fileIndex].awaitingLateSessionLookup = false;
												batchEntries[fileIndex].failed = true;
												const failureMessage = checkingDeadlineExpired
													? "We couldn't confirm this upload was saved. Cancel it before trying again."
													: "Upload couldn't be prepared. Please try again.";
												batchEntries[fileIndex].failureMessage = failureMessage;
												const message = checkingDeadlineExpired
													? failureMessage
													: `We couldn't prepare ${file.name}. Please try again.`;
												this.toast.error(message, { timeout: calculateTimeout(message) });
											};
											const lateLookupDeadline = setTimeout(
												() => void markLateLookupFailed(false, true),
												LATE_DURABLE_SESSION_LOOKUP_TIMEOUT_MS
											);
											void sessionLookup
												.then(async (lateSession) => {
													clearTimeout(lateLookupDeadline);
													if (!lateSession) {
														await markLateLookupFailed(true);
														return;
													}
													if (!isUploadSessionDeliverableByResume(lateSession.status)) {
														await markLateLookupFailed();
														return;
													}
													if (batchEntries[fileIndex].cancelled) {
														batchEntries[fileIndex].sessionId = lateSession.id;
														batchEntries[fileIndex].uploadId = lateSession.uploadId;
														batchEntries[fileIndex].uploadOwnerId = lateSession.uploadOwnerId;
														batchEntries[fileIndex].uploadOwnerClaimId = lateSession.uploadOwnerClaimId;
														await this.markEntrySessionCancelled(batchEntries[fileIndex], undefined, {
															notifyOtherTabs: true
														});
														return;
													}
													// LSP-19865: this lookup can outlive its 5 s deadline, and the deadline arms the
													// reconcile loop. From then on the LOOP is the only actor allowed to adopt or abort
													// this target — its ticks re-read the same row single-flight and refuse adoption
													// after any abort attempt it cannot prove was rejected. A second decision-maker here
													// raced it four review rounds running, so this lookup defers rather than decides.
													if (batchEntries[fileIndex].prePersistTargetReconcile !== undefined) {
														console.warn(
															"[Upload] Late durable-session lookup resolved after the reconcile loop was armed; offering the row to the loop",
															{
																sessionId: lateSession.id,
																uploadId: lateSession.uploadId,
																fileName: file.name,
																loopOutcome: batchEntries[fileIndex].prePersistTargetReconcile.outcome ?? "running"
															}
														);
														// The loop decides under its own rule (adopt only while no abort attempt is
														// unaccounted for); this lookup never adopts once the loop exists.
														await batchEntries[fileIndex].prePersistTargetReconcile.offer(lateSession);
														return;
													}
													// A late lookup can identify the resumable row, but never authorizes this tab to adopt its lease.
													batchEntries[fileIndex].sessionId = lateSession.id;
													batchEntries[fileIndex].uploadId = lateSession.uploadId;
													batchEntries[fileIndex].progress = Math.min(batchEntries[fileIndex].progress, 99);
													batchEntries[fileIndex].awaitingLateSessionLookup = false;
													batchEntries[fileIndex].failed = false;
													batchEntries[fileIndex].failureMessage = undefined;
													batchEntries[fileIndex].deferred = true;
													batchEntries[fileIndex].ownershipUncertain = false;
													logBackgroundFinishingEntered("dialog-late-session-lookup", batchEntries[fileIndex]);
													this.emitUploadEvent("UploadDeferred", { sessionId: lateSession.id, conversationId });
												})
												.catch(async (sessionLookupError) => {
													clearTimeout(lateLookupDeadline);
													await markLateLookupFailed();
													console.warn(
														`[Upload] Delayed durable-session reconciliation failed for "${file.name}"`,
														sessionLookupError
													);
													// The incident exit (LSP-19865): a rejected late lookup proved nothing, and
													// until now nothing aborted the reserved multipart upload.
													armPrePersistTargetReconcile("late durable-session lookup rejected");
												});
										} else if (
											lookupOutcome.session &&
											isUploadSessionDeliverableByResume(lookupOutcome.session.status)
										) {
											durableSession = lookupOutcome.session;
										}
									} catch (sessionLookupError) {
										durableSessionLookupFailed = true;
										console.warn(
											`[Upload] Could not verify durable session for failed pre-persist "${file.name}"`,
											sessionLookupError
										);
									}
									if (durableSession) {
										batchEntries[fileIndex].sessionId = durableSession.id;
										batchEntries[fileIndex].uploadId = durableSession.uploadId;
										batchEntries[fileIndex].uploadOwnerId = durableSession.uploadOwnerId;
										batchEntries[fileIndex].uploadOwnerClaimId = durableSession.uploadOwnerClaimId;
										if (batchEntries[fileIndex].cancelled) {
											await this.markEntrySessionCancelled(batchEntries[fileIndex], undefined, {
												notifyOtherTabs: true
											}).catch((markError) =>
												console.warn(
													`[Upload] Failed to cancel session discovered after cancellation for "${file.name}"`,
													markError
												)
											);
										}
									} else if (batchEntries[fileIndex].cancelled) {
										// confirmCancel already owns the abort for a target cancelled while persistence was in flight.
									} else if (durableSessionLookupFailed) {
										// A failed read is not proof of a durable handoff. Keep this target/bytes available for an
										// explicit cancel, and only clear the visible failure if the timed-out lookup later proves a row.
										batchEntries[fileIndex].failed = true;
										batchEntries[fileIndex].awaitingLateSessionLookup = durableSessionLookupTimedOut;
										batchEntries[fileIndex].failureMessage = durableSessionLookupTimedOut
											? "Checking whether this upload was saved…"
											: "Upload couldn't be prepared. Please try again.";
										// A synchronous rejection has no late lookup coming; the reconcile loop is the
										// only thing that will ever ask again (LSP-19865).
										if (!durableSessionLookupTimedOut) armPrePersistTargetReconcile("durable-session lookup rejected");
									} else {
										batchEntries[fileIndex].failed = true;
										batchEntries[fileIndex].failureMessage = "Upload couldn't be prepared. Please try again.";
										const abortPromise = (batchEntries[fileIndex].prePersistAbortPromise ??= trackPrePersistAbortOutcome(
											batchEntries[fileIndex],
											abortPrePersistTarget(target as MultipartUploadTarget, file.name)
										));
										await awaitPrePersistAbortBounded(abortPromise, file.name, "failed pre-persist");
										// A timed-out abort "continues in the background" — and if that background request
										// rejects, nothing else would ever retry it (LSP-19865).
										void abortPromise.then((outcome) => {
											if (outcome === "rejected") armPrePersistTargetReconcile("failed pre-persist abort rejected");
										});
									}
								}
								// LSP-15979: a failed pre-persist means the chunked-upload resume safety net is
								// gone — a backgrounded-tab failure can then silently lose this file. Promote
								// warn->error so it forwards to Datadog at error level.
								console.error(`[Upload] Could not pre-persist "${file.name}" to IndexedDB`, persistError);
							}
						} finally {
							if (batchEntries[fileIndex].failed || batchEntries[fileIndex].cancelled) forgetUnpersistedUploadFile(uploadBatchId, fileIndex);
							batchEntries[fileIndex].isPreparing = false;
						}
						preparationResolvers[fileIndex]();
				};
				const preparationWork = processWithConcurrency(batchFiles, persistConcurrency, async (file, index) => {
					await targetPromises[index];
					if (batchEntries[index].isPreparing) await prepareEntry(file, index);
					else preparationResolvers[index]();
				}).catch((error) => {
					for (const reject of preparationRejecters) reject(error);
					throw error;
				});
				const preparationSummary = preparationWork.then(
					() => {
						if (batchFiles.length > 0) {
							const persistPhaseDuration = ((performance.now() - persistPhaseStart) / 1000).toFixed(1);
							console.info(`[Upload] Pre-persist phase complete in ${persistPhaseDuration}s`);
						}
					},
					() => {
						// `preparationWork` already rejects every row's preparation gate with this error.
						// The upload-worker promise below owns propagation; this summary is logging-only.
					}
				);

				/**
				 * LSP-19609: keep watching a row this lane just handed to the background.
				 *
				 * The three deferral exits below used to be terminal for the lane: set `deferred`, render
				 * "Finishing in the background…", return. If the drain that took the row then failed its
				 * completion (POST /complete timing out under load) or parked it, the row came back as an
				 * ownerless `pending` row nobody in this tab would ever try again — this tab's own drain was
				 * locked out of the scheduler by the very tab that had taken the row — and if the other side
				 * finished it, the tray never learned. Five rows sat on "finishing" for the life of the page.
				 *
				 * The registry (one timer, one bulk read per tick — `adminDeferredRowFollowUp.ts`) mirrors the
				 * durable row into the tray and, where allowed, hands the row back to this lane through the
				 * ordinary slot + `uploadEntry` path. Not allowed: the lost-target restart ("Do not retry the
				 * dead target" — persisted resume must reinitialize it), a row whose durable upload identity no
				 * longer matches the target this entry holds a File for, or an entry over its re-attempt budget.
				 */
				const followUpDeferredEntry = (
					entry: FileWithProgress,
					index: number,
					reason: "yielded-claim-lost" | "transient-deferral" | "lost-target-restart"
				) => {
					try {
						registerDeferredEntryFollowUp(entry, index, reason);
					} catch (followUpError) {
						// A follow-up is a recovery aid layered on the existing deferral; failing to arm it must
						// never change the outcome the deferral already decided.
						console.warn("[Upload] Could not arm the deferred row follow-up", {
							sessionId: entry.sessionId,
							fileName: entry.name,
							reason,
							error: followUpError
						});
					}
				};
				const registerDeferredEntryFollowUp = (
					entry: FileWithProgress,
					index: number,
					reason: "yielded-claim-lost" | "transient-deferral" | "lost-target-restart"
				) => {
					const registry = this.deferredRowFollowUps;
					const sessionId = entry.sessionId;
					if (!registry || !sessionId) {
						console.warn("[Upload] Deferred row follow-up registration skipped", {
							conversationId, sessionId, fileName: entry.name, reason,
							registryAvailable: Boolean(registry), sessionAvailable: Boolean(sessionId)
						});
						return;
					}
					const entryTarget = uploadTargets[index];
					const multipartTarget = entryTarget?.type === "multipart" ? (entryTarget as MultipartUploadTarget) : undefined;
					// The ONE identity everything below compares against is the immutable target this entry's
					// `uploadEntry` would re-run with — never `entry.uploadId`, which `updateResumedFileIdentity`
					// can move to a reinitialized upload while `uploadTargets[index]` still names the old one
					// (Codex diff review). A row whose `uploadId` is not this target's is not a row this lane may
					// re-attempt, and the registry probe below asks only about this target.
					const entryUploadId = multipartTarget ? uploadTargetOpaqueId(multipartTarget) : undefined;
					entry.deferredFollowUpDeadlineAt ??= Date.now() + DEFERRED_ROW_FOLLOW_UP_LIFETIME_MS;
					const reattemptAllowed = reason !== "lost-target-restart";
					const entryStillInTray = () =>
						this.fileList.includes(entry) && !entry.cancelled && !entry.cancelPending && !entry.pendingCancel;
					registry.watch({
						sessionId,
						fileName: entry.name,
						deadlineAt: entry.deferredFollowUpDeadlineAt,
						isStillDeferred: () =>
							entryStillInTray() &&
							!entry.failed &&
							entry.progress < 100 &&
							(entry.deferred === true || entry.handedOff === true || entry.ownershipUncertain === true),
						canReattempt: (row) =>
							reattemptAllowed &&
							entryUploadId !== undefined &&
							row.uploadId === entryUploadId &&
							(entry.deferredReattempts ?? 0) < DEFERRED_ROW_FOLLOW_UP_MAX_REATTEMPTS,
						isRetryDue: (row, now) => isFileUploadRetryDue(row, now),
						onComplete: () => this.markResumedFileComplete(sessionId),
						// A row cancelled in another tab: the same path the cross-tab invalidation broadcast takes.
						// Matched on the session id alone: the durable row we just read IS this session, and
						// another tab may have reinitialized its upload id since the watch was armed, in which case
						// the entry's own id has moved on from the target's and an id filter would miss it (AI
						// review, PR #4812).
						onCancelled: () => this.invalidateUploadSession(sessionId),
						onReattempt: async () => {
							if (!entryStillInTray()) return;
							entry.deferredReattempts = (entry.deferredReattempts ?? 0) + 1;
							logBackgroundFinishingCleared("deferred-row-reclaimed-by-lane", entry);
							entry.deferred = false;
							entry.handedOff = false;
							entry.ownershipUncertain = false;
							entry.retrying = false;
							entry.failed = false;
							entry.failureMessage = undefined;
							// A fresh lane attempt gets the in-page ladder afresh; the follow-up's own budget is
							// `deferredReattempts`, counted above.
							entry.retryCount = 0;
							entry.laneOwned = true;
							noteBatchLaneActivity();
							// The batch's `finally` released the lane lease once nothing was lane-owned; take it
							// back for the duration of this attempt (idempotent while already held).
							await ensureLaneLeaseAcquired();
							try {
								await this.withFileUploadSlot(() => uploadEntry(entry, index));
							} finally {
								// Mirror of the batch loop's per-row `finally`: the pin ends with the attempt.
								entry.laneOwned = false;
								void releaseLaneIntentBestEffort([sessionId], "row-finally");
								if (!this.fileList.some((other: FileWithProgress) => other.laneOwned === true)) {
									void releaseLaneLeaseIfHeld();
								}
								noteBatchLaneActivity();
							}
						},
						// LSP-19767 (data-loss): the durable row is gone and the registry below could not prove
						// the file was assembled. Nothing will finish this upload and nothing will retry it, so
						// the row stops saying "Finishing in the background…" and becomes one the user can act
						// on. Before this the watch simply ended here and the tray said nothing at all.
						onLocalRecordLost: (evidence) => this.markLocalUploadRecordLost(sessionId, evidence, conversationId),
						// The local row is gone. Admin deletes it on completion AND on a bytes-gone discard, so only
						// the upload registry can say which — same fail-closed read the completion path uses.
						onRowMissing: async () => {
							if (!multipartTarget || !entryUploadId) return "unknown";
							// Ask about the upload this entry CURRENTLY names. A reinitialized row carries a new id,
							// and the lease-claim broadcast moves the entry's id with it; if THAT upload is assembled,
							// this file did complete. Only the re-attempt path insists on the original target, because
							// only a re-attempt would re-run against it. A moved id has no target of its own here, so
							// the registry id falls back to the upload id (which, for `/uploads`, is the registry id).
							const currentUploadId = entry.uploadId ?? entryUploadId;
							const identityMoved = currentUploadId !== entryUploadId;
							const verdict = await readUploadRegistryForCompletionRefusal({
								uploadId: currentUploadId,
								endpointFamily: isUploadsUploadTarget(multipartTarget) ? "uploads" : "legacy",
								registryId:
									!identityMoved && isUploadsUploadTarget(multipartTarget) ? multipartTarget.registryId : undefined
							});
							if (verdict === "assembled") return "complete";
							// `absent` is the probe's own positive "no upload matching this identity exists"
							// (LSP-19325 split it out from the catch-all for exactly this reason). It is the only
							// answer here that PROVES nothing will finish this row, so it is the only one whose
							// copy may say so; everything else stays doubt.
							return verdict === "absent" ? "absent" : "unknown";
						},
						log: (event, details) => {
							// Forwarded to Datadog: keep raw Error objects out of the record, the same way every other
							// upload diagnostic does.
							const { error, ...rest } = details;
							console.warn("[Upload] Deferred row follow-up", {
								event,
								reason,
								conversationId,
								...rest,
								...(error === undefined ? {} : uploadFailureLogDetails(error))
							});
						}
					});
				};

				const uploadEntry = async (_batchEntry: FileWithProgress, i: number) => {
					const file = batchFiles[i];
					const target = uploadTargets[i];
					const entry = batchEntries[i];
					if (!target) return;
					entry.queuedForUpload = false;

					if (entry.cancelled) {
						return;
					}

					if (entry.deferred) {
						return;
					}

					if (entry.ownershipUncertain) {
						return;
					}

					if (entry.failed) {
						// Already terminally failed during pre-persist (e.g. unreadable file). Don't attempt upload.
						return;
					}

					const abortController = new AbortController();
					entry.abortController = abortController;

					const uploadWithRetry = async () => {
						const attemptIdentity = this.getEntrySessionIdentity(entry);
						try {
							let multipartOutcome: "completed" | "yielded" | undefined;
							if (target.type === "multipart") {
								multipartOutcome = await chunkedUploadFile(
									file,
									target as MultipartUploadTarget,
									(bytesUploaded: number) => {
										entry.progress = Math.min(Math.round((bytesUploaded / file.size) * 100), 99);
										noteBatchLaneActivity();
									},
									conversationId,
									inferFileCategory(inferMimeType(file)),
									abortController.signal,
									uploadBatchId
								);
							} else {
								await uploadToS3WithProgress(
									file,
									target.presignedUrl,
									(progressEvent: UploadProgressEvent) => {
										entry.progress = progressEvent.percentage;
										noteBatchLaneActivity();

										const logProgress = (event: UploadProgressEvent) => {
											const logFile = event.blobOrFile as File;
											delete event.blobOrFile;

											const totalSize = formatFileSize(logFile.size);
											const uploadedSize = formatFileSize(event.loaded);
											const logMessage = event.completed
												? `Uploaded file ${logFile.name} (${totalSize}) in ${event.durationSeconds} seconds`
												: `Uploading file ${logFile.name} (${uploadedSize}/${totalSize}) ${event.percentage}%`;
											console.info(logMessage, { ...event, conversationId });
										};

										const logFile = progressEvent.blobOrFile as File;

										if (!this.inProgressFiles.has(logFile.name) || progressEvent.completed) {
											logProgress(progressEvent);
											this.inProgressFiles.add(logFile.name);
										} else if (new Date().getTime() - this.lastProgressLogTimestamp > this.progressLogInterval) {
											logProgress(progressEvent);
											this.lastProgressLogTimestamp = new Date().getTime();
										}
									},
									undefined,
									abortController.signal,
									target.headers
								);
							}
							if (multipartOutcome === "yielded") {
								if (entry.cancelled) {
									throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
								}
								entry.deferred = true;
								logBackgroundFinishingEntered("dialog-yielded-claim-lost", entry);
								if (entry.pendingCancel) {
									console.warn(
										`[Upload] Discarded pending cancellation for entry yielded to another tab "${entry.name}"`,
										{
											sessionId: entry.sessionId
										}
									);
								}
								entry.pendingCancel = false;
								if (entry.sessionId) {
									try {
										const session = await getFileUploadSessionById(entry.sessionId);
										if (session) {
											this.updateResumedFileIdentity(session);
										}
									} catch (sessionLookupError) {
										console.warn(`[Upload] Could not refresh identity for yielded "${file.name}"`, sessionLookupError);
									}
								}
								if (entry.cancelled) {
									entry.deferred = false;
									throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
								}
								if (entry.sessionId) {
									this.emitUploadEvent("UploadDeferred", { sessionId: entry.sessionId, conversationId });
								}
								followUpDeferredEntry(entry, i, "yielded-claim-lost");
								return;
							}

							if (entry.cancelled) {
								throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
							}
							console.info(`File ${file.name} uploaded successfully`);
							entry.progress = 100;
							forgetUnpersistedUploadFile(uploadBatchId, i);
							itemsUploaded++;
							uploadedByBatchIndexes.add(i);
							uploadedCounts[categoryForIndex(i)]++;
							try {
								noteAdminUploadCompleted(conversationId);
							} catch (hintError) {
								console.warn(
									"[LSP-21294] admin-upload leaving-hint noteContentCompleted threw; upload remains complete",
									{ conversationId, fileName: file.name },
									hintError
								);
							}

							if (onFileUploaded) {
								try {
									onFileUploaded(inferFileCategory(inferMimeType(file)));
								} catch (callbackError) {
									console.warn(
										`[Upload] Completion callback failed for "${file.name}"; upload remains complete`,
										callbackError
									);
								}
							}
						} catch (err) {
							if (err instanceof UploadCancelledError) {
								throw err;
							}

							// Single-part uploadToS3WithProgress rejects with S3UploadError("AbortError")
							// rather than UploadCancelledError. Treat any post-cancel error as a clean
							// cancellation so we don't retry-loop the aborted signal and mark the file failed.
							if (abortController.signal.aborted) {
								throw new UploadCancelledError(`Upload of "${file.name}" was cancelled`);
							}
							if (isUploadVerificationPending(err)) {
								entry.verificationPending = true;
								entry.retrying = false;
								entry.failureMessage = undefined;
								entry.deferred = true;
								this.emitUploadEvent("UploadDeferred", { sessionId: entry.sessionId, conversationId, verificationPending: true });
								return;
							}
							entry.verificationPending = false;

							// LSP-17798: an online ChunkNetworkError has already exhausted the chunk retry budget
							// and must surface as failed. Offline failures and the other established deferral
							// categories still defer exactly as before.
							if (isUploadDeferredError(err) && !(err instanceof ChunkNetworkError && navigator.onLine)) {
								entry.ownershipUncertain = true;
								entry.deferred = true;
								if (entry.pendingCancel) {
									console.warn(
										`[Upload] Discarded pending cancellation while checking transient deferral for "${entry.name}"`,
										{ sessionId: entry.sessionId }
									);
								}
								entry.pendingCancel = false;
								let deferredSessionId: string | undefined;
								const deferredEntryIdentity = this.getEntrySessionIdentity(entry);
								let deferredUploadId: string | undefined;
								let deferredSessionAlreadyPending = false;
								if (target.type === "multipart" && conversationId) {
									deferredUploadId = uploadTargetOpaqueId(target);
									deferredSessionId = entry.sessionId;
									try {
										const session = await getFileUploadSessionByUploadId(
											uploadTargetOpaqueId(target as MultipartUploadTarget)
										);
										if (entry.cancelled) {
											// Same repair as the UPLOAD_LOST branch below: a row already released to
											// `pending` means the cancel's owner-fenced CAS had nothing to fence against
											// and missed, so make it terminal again before surfacing the cancel.
											if (session?.status === "pending") {
												await this.cancelSessionRowAfterRacedRelease(session.id, session.uploadId, file.name);
											}
											throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
										}
										if (session && isUploadSessionDeliverableByResume(session.status)) {
											entry.sessionId = session.id;
											entry.uploadId = session.uploadId;
											// Cursor Bugbot High (PR #3354): this lookup is untrusted — it finds ANY
											// resumable session for this uploadId, which can belong to a duplicated
											// tab's own live claim (shares this tab's owner id via copied
											// sessionStorage, but has a distinct claim id). Blindly copying
											// uploadOwnerId/uploadOwnerClaimId here used to let a later exhausted
											// retry's needs-attention gate trust that foreign claim as "mine" and
											// terminalize the duplicate tab's in-flight upload. Ownership fields are
											// populated only by the trusted paths (the persist-time read, and the
											// subscribeAdminUploadLeaseClaims broadcast) — not here.
											deferredSessionId = session.id;
											deferredSessionAlreadyPending = session.status === "pending";
										} else if (session) {
											deferredSessionId = undefined;
											console.warn(
												`[Upload] Resumable session for deferred "${file.name}" is "${session.status}", not resume-deliverable; falling back to normal retry handling`
											);
										} else if (deferredSessionId) {
											console.warn(
												`[Upload] UploadId lookup missed pre-persisted session for deferred "${file.name}"; deferring by session id`
											);
										}
									} catch (sessionLookupError) {
										if (entry.cancelled) {
											throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
										}
										if (sessionLookupError instanceof UploadCancelledError) {
											throw sessionLookupError;
										}
										if (deferredSessionId) {
											console.warn(
												`[Upload] Could not verify resumable session for deferred "${file.name}"; deferring by pre-persisted session id`,
												sessionLookupError
											);
										} else {
											console.warn(
												`[Upload] Could not look up resumable session for deferred "${file.name}"; falling back to normal retry handling`,
												sessionLookupError
											);
										}
									}
								}
								if (deferredSessionId && !deferredSessionAlreadyPending) {
									try {
										const sessionIdentity = deferredEntryIdentity;
										const reclaimable =
											sessionIdentity && sessionIdentity.uploadId === deferredUploadId
												? await markFileUploadSessionPendingForResume(deferredSessionId, {
														uploadId: sessionIdentity.uploadId,
														uploadOwnerId: sessionIdentity.uploadOwnerId,
														uploadOwnerClaimId: sessionIdentity.uploadOwnerClaimId
													})
												: false;
										if (entry.cancelled) {
											// Same raced-release repair as the UPLOAD_LOST branch below: the cancel's CAS
											// lost to this release (which cleared the owner fields it expected), so the row
											// must be made terminal again before the cancel is surfaced.
											if (reclaimable) {
												await this.cancelSessionRowAfterRacedRelease(
													deferredSessionId,
													sessionIdentity?.uploadId,
													file.name
												);
											}
											throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
										}
										if (!reclaimable) {
											console.warn(
												`[Upload] Deferred session "${deferredSessionId}" no longer exists; falling back to normal retry handling`
											);
											deferredSessionId = undefined;
										}
									} catch (reclaimError) {
										if (entry.cancelled) {
											throw new UploadCancelledError(`Upload of "${file.name}" was cancelled in another tab`);
										}
										if (reclaimError instanceof UploadCancelledError) {
											throw reclaimError;
										}
										console.warn(
											`[Upload] Could not make deferred session "${deferredSessionId}" pending for resume; falling back to normal retry handling`,
											reclaimError
										);
										deferredSessionId = undefined;
									}
								}
								if (deferredSessionId) {
									logInfo("[Upload] FileUploadDialog: upload deferred by transient upload failure", {
										conversationId,
										fileName: entry.name,
										retryCount: entry.retryCount
									});
									entry.sessionId = deferredSessionId;
									// A resume-drain callback may have updated this row while persistence was deciding.
									// A successful pending-for-resume write is the authoritative ownership decision.
									entry.progress = Math.min(entry.progress, 99);
									entry.failed = false;
									entry.retrying = false;
									entry.failureMessage = undefined;
									entry.handedOff = false;
									entry.deferred = true;
									entry.pendingCancel = false;
									entry.ownershipUncertain = false;
									logBackgroundFinishingEntered("dialog-transient-deferral", entry, {
										deferredSessionId,
										errorName: err instanceof Error ? err.name : typeof err
									});
									this.emitUploadEvent("UploadDeferred", { sessionId: deferredSessionId, conversationId });
									followUpDeferredEntry(entry, i, "transient-deferral");
									return;
								}
								// Persistence declined the handoff, so the next retry remains locally owned even if
								// resume-drain callbacks touched this row while the decision was pending.
								entry.progress = Math.min(entry.progress, 99);
								entry.failed = false;
								entry.retrying = false;
								entry.failureMessage = undefined;
								entry.handedOff = false;
								entry.deferred = false;
								entry.abortController = abortController;
								entry.ownershipUncertain = false;
							}

							// UploadLostError = the multipart upload is gone AND the assembled object is
							// confirmed absent (server returned 409 UPLOAD_LOST). Retrying re-uses the same
							// dead uploadId, so no attempt against THIS target can succeed and the server
							// sweep can't recover it either (nothing to complete).
							//
							// R-1 ("there is no destroy", owner directive 2026-08-26): that is a verdict about
							// the TARGET, never about the bytes. This branch used to delete the intact local
							// session and its retained chunk buffers and tell the user to upload the file
							// again — the closest living relative of the expiration defect that destroyed file
							// parts. The driver has already released the row for resume, and the durable drain
							// reinitializes it against a freshly minted target from those same buffers
							// (adminSessionStore.resetForReinitialization), so the honest status here is
							// "restarting", not "re-upload".
							//
							// LSP-23007: refresh-urls now answers a cancelled/superseded target with the same 409,
							// and the driver rethrows it wrapped in a MultipartUrlRefreshError — read the verdict
							// through the wrapper, as contact-async's OptionsPage does.
							if (isUploadLost(err) || (isMultipartUrlRefreshError(err) && isUploadLost(err.originalError))) {
								const lostUploadId =
									target.type === "multipart" ? uploadTargetOpaqueId(target as MultipartUploadTarget) : undefined;
								console.warn(
									`[Upload] File ${file.name} reported UPLOAD_LOST; restarting it against a fresh upload target`,
									{
										conversationId,
										fileName: entry.name,
										retryCount: entry.retryCount,
										sessionId: entry.sessionId,
										uploadId: lostUploadId
									}
								);
								if (entry.sessionId) {
									const lostSessionId = entry.sessionId;
									let restartSessionPending = false;
									try {
										const session = await getFileUploadSessionById(lostSessionId);
										// Bugbot (PR #4377): a cancel confirmed while an await here was in flight must win,
										// exactly like the transient-defer path's post-await checks above — otherwise this
										// branch releases the row and the drain resumes a file the user cancelled.
										if (entry.cancelled) {
											await this.cancelSessionRowAfterRacedRelease(lostSessionId, lostUploadId, file.name);
											throw new UploadCancelledError(
												`Upload of "${file.name}" was cancelled during its lost-target release`
											);
										}
										restartSessionPending = session?.status === "pending" && session.uploadId === lostUploadId;
										if (
											!restartSessionPending &&
											attemptIdentity?.uploadOwnerId === getCurrentFileUploadSessionOwnerId() &&
											attemptIdentity.uploadOwnerClaimId !== undefined
										) {
											restartSessionPending = await markFileUploadSessionPendingForResume(
												lostSessionId,
												attemptIdentity
											);
										}
										if (entry.cancelled) {
											// The cancel's own CAS raced this release and lost (the release cleared the owner
											// fields it expected), so the row this branch just made resumable must be
											// re-cancelled before the cancel is surfaced.
											if (restartSessionPending) {
												await this.cancelSessionRowAfterRacedRelease(lostSessionId, lostUploadId, file.name);
											}
											throw new UploadCancelledError(
												`Upload of "${file.name}" was cancelled during its lost-target release`
											);
										}
									} catch (releaseError) {
										if (releaseError instanceof UploadCancelledError) {
											throw releaseError;
										}
										if (entry.cancelled) {
											// The release threw, so nothing was made resumable — the cancel path owns the row.
											throw new UploadCancelledError(
												`Upload of "${file.name}" was cancelled during its lost-target release`
											);
										}
										console.warn(`[Upload] Could not release lost upload "${file.name}" for restart`, releaseError);
									}
									if (restartSessionPending) {
										const restartMessage = `We're restarting the upload of ${file.name} — your file is safe and we'll keep going automatically.`;
										this.toast.warning(restartMessage, { timeout: calculateTimeout(restartMessage) });
										// Same shape as the transient-defer handoff above: the durable row owns this
										// upload from here, so the entry stops claiming a local attempt is in flight.
										entry.progress = Math.min(entry.progress, 99);
										entry.failureMessage = undefined;
										entry.failed = false;
										entry.retrying = false;
										entry.handedOff = false;
										entry.deferred = true;
										entry.pendingCancel = false;
										entry.ownershipUncertain = false;
										logBackgroundFinishingEntered("dialog-lost-target-restart", entry);
										this.emitUploadEvent("UploadDeferred", { sessionId: entry.sessionId, conversationId });
										followUpDeferredEntry(entry, i, "lost-target-restart");
										return;
									}
									// Do not retry the dead target. The exhausted-retry path below already owns the
									// fenced release retry and timer when IndexedDB is temporarily unavailable.
									entry.retryCount = this.maxRetryCount;
								} else {
									// No durable session means there were never any retained buffers to protect (the
									// pre-persist was refused — quota, or an IndexedDB failure), so nothing survives
									// this attempt and the honest terminal is the bytes-gone one.
									const bytesGoneMessage = `We no longer have a local copy of ${file.name}, so we can't finish it. Please upload it again.`;
									this.toast.error(bytesGoneMessage, { timeout: calculateTimeout(bytesGoneMessage) });
									console.error(
										`[Upload] File ${file.name} reported UPLOAD_LOST with no durable session to reinitialize`,
										{
											conversationId,
											fileName: entry.name,
											uploadId: lostUploadId
										}
									);
									entry.failureMessage = bytesGoneMessage;
									entry.retrying = false;
									entry.failed = true;
									return;
								}
							}

							// LSP-19325: the server has twice reported this upload absent, so the driver has
							// ALREADY parked the durable row as needs-attention before throwing. The shared
							// `classifyUploadFailure` below cannot see that — it reads the underlying 404 as
							// auth-gated and non-terminal, which is correct for a bare 404 — so without this
							// branch the dialog spent its whole in-page ladder re-POSTing a completion for
							// coordinates the server does not currently recognize, then told the user "we'll
							// keep retrying automatically". The durable row still requeues on the slow cycle,
							// so a cause that later clears (a re-granted share) still lands the file; what
							// changes here is only that the in-page ladder is not spent on it.
							//
							// Warning tone, not red, and no `failed`: nobody did anything wrong (see the
							// `needsAttention` row flag). The durable row is already correct, so there is
							// nothing to write here — only the presentation to fix and the ladder to stop.
							// Matched by the error's own declared NAME rather than by importing a predicate: this
							// is a presentation decision inside a Vue component, and it is the same shape the
							// surrounding code already uses for `AbortError` and `S3UploadError`. The
							// authoritative, `instanceof`-fenced classification stays in the driver, which is
							// what actually parked the durable row before this throw reached us.
							if (err instanceof Error && err.name === "UploadTargetGoneError") {
								const parkedMessage = stoppedUploadMessage(err);
								this.toast.warning(parkedMessage, { timeout: calculateTimeout(parkedMessage) });
								console.warn(
									"[Upload] Upload target is gone; parking the row instead of spending the in-page retry ladder",
									{
										conversationId,
										fileName: entry.name,
										sessionId: entry.sessionId,
										uploadId: entry.uploadId
									}
								);
								entry.failureMessage = parkedMessage;
								entry.retrying = false;
								entry.failed = false;
								entry.needsAttention = true;
								entry.refreshNotice = false;
								if (entry.sessionId) {
									try {
										const parkedSession = await getFileUploadSessionById(entry.sessionId);
										if (parkedSession) this.updateResumedFileIdentity(parkedSession);
									} catch (parkedLookupError) {
										console.warn(`[Upload] Could not refresh identity for parked "${file.name}"`, parkedLookupError);
									}
								}
								return;
							}

							// Server-rejected file types and malformed upload requests are permanent for this
							// file. Do not spend the legacy retry budget on a deterministic 4xx response.
							const failureClassification = classifyUploadFailure(err);
							// AbortError/cancelled is terminal in the shared classifier because an explicit
							// cancellation must not retry, but an upload stall can surface the same marker
							// without this entry's abort signal being set. Let that case use normal retry handling.
							// AI review (PR #4964): the same narrowing the background park uses. `classifyUploadFailure`
							// calls every status-400 terminal, but the store spends 400 on transport problems too —
							// a dropped mid-body PUT (`IncompleteBody`), throttling (`SlowDown`), a checksum a
							// re-send satisfies (`BadDigest`), or a body that is not its error XML at all (a WAF
							// page). This lane is the WORSE place to get that wrong: it stamps
							// `[UPLOAD_REJECTED]` into the durable row, which makes it evidence-terminal so the
							// requeue loop refuses it forever — and tells the customer to choose a different file
							// when nothing is wrong with theirs. One shared predicate, so the two lanes cannot
							// disagree about the same error.
							if (
								failureClassification.terminal &&
								failureClassification.kind !== "cancelled" &&
								isTransportShapedS3Failure(err)
							) {
								// Log parity with the driver's copy of this check: both exclusions name
								// themselves, so "why did this retry instead of stopping?" is answerable from
								// either lane's logs (AI review, PR #4964).
								console.warn(
									"[Upload] The storage rejection looks like a transport problem, not a verdict; keeping the upload retryable",
									{
										fileName: file.name,
										...uploadFailureLogDetails(err)
									}
								);
							}
							if (
								failureClassification.terminal &&
								failureClassification.kind !== "cancelled" &&
								!isTransportShapedS3Failure(err)
							) {
								const message = `We couldn't upload ${file.name}. Please choose a different file.`;
								const errorMessage = getMessageFromError(err);
								const errorDetails = err as {
									name?: unknown;
									status?: unknown;
									statusCode?: unknown;
									code?: unknown;
									httpStatus?: unknown;
									response?: { status?: unknown };
									error?: unknown;
								};
								const errorStatus = [
									errorDetails.status,
									errorDetails.statusCode,
									errorDetails.code,
									errorDetails.httpStatus,
									errorDetails.response?.status
								].find((candidate): candidate is number => typeof candidate === "number");
								// Keep the typed rejection evidence with the durable session. Resume only has the
								// persisted string after reload, so losing the status here would turn a permanent
								// 4xx into an indistinguishable retryable row.
								const terminalMarker = [errorDetails.name, errorDetails.code, errorMessage, errorDetails.error]
									.filter((candidate): candidate is string => typeof candidate === "string")
									.join(" ")
									.toUpperCase();
								const persistedTerminalMarker = `[${PERSISTED_TERMINAL_REJECTION_CODE}]`;
								const durableFailureReason =
									errorStatus !== undefined
										? `${persistedTerminalMarker} HTTP ${errorStatus}${errorMessage ? ` ${errorMessage}` : ""}${terminalMarker.includes("REPLACEMENT_IN_PROGRESS") ? " REPLACEMENT_IN_PROGRESS" : ""}`
										: terminalMarker.includes("REPLACEMENT_IN_PROGRESS")
											? `${persistedTerminalMarker} ${errorMessage ? `${errorMessage} ` : ""}REPLACEMENT_IN_PROGRESS`
											: `${persistedTerminalMarker} ${
													errorMessage ||
													(terminalMarker.includes("INVALID FILE TYPE")
														? "INVALID FILE TYPE"
														: terminalMarker.includes("UNSUPPORTED")
															? "UNSUPPORTED MEDIA TYPE"
															: message)
												}`;
								this.toast.error(message, { timeout: calculateTimeout(message) });
								entry.failureMessage = message;
								entry.failed = true;
								// Permanent failures must park a resume-deliverable durable session so reload cannot
								// silently retry it. Require the same ownership fence as exhausted retries before
								// changing a session another tab may have reclaimed.
								if (entry.sessionId) {
									let session;
									try {
										session = await getFileUploadSessionById(entry.sessionId);
									} catch (sessionError) {
										if (entry.cancelled) throw new UploadCancelledError(`Upload of "${file.name}" was cancelled`);
										console.error(
											`[Upload] Could not inspect terminal failure session for "${file.name}"`,
											sessionError
										);
										this.toast.warning(`Upload failure for ${file.name} could not be saved; please try again.`, {
											timeout: 8_000
										});
										return;
									}
									if (entry.cancelled) throw new UploadCancelledError(`Upload of "${file.name}" was cancelled`);
									if (session?.status === "needs-attention") {
										// The driver already persisted the terminal handoff. Preserve its ownerless
										// session so the user can cancel it instead of re-parking or retrying it here.
										this.updateResumedFileIdentity(session);
										entry.failed = true;
										entry.retrying = false;
										entry.failureMessage = message;
										return;
									}
									try {
										const ownsSession =
											session &&
											(session.uploadOwnerId === undefined ||
												(session.uploadOwnerId === getCurrentFileUploadSessionOwnerId() &&
													session.uploadOwnerClaimId !== undefined &&
													session.uploadOwnerClaimId === entry.uploadOwnerClaimId));
										if (session && ownsSession && isUploadSessionDeliverableByResume(session.status)) {
											this.updateResumedFileIdentity(session);
											const parked = await this.markEntrySessionCancelled(
												entry,
												undefined,
												{ notifyOtherTabs: false, lastError: durableFailureReason },
												"needs-attention"
											);
											if (entry.cancelled) throw new UploadCancelledError(`Upload of "${file.name}" was cancelled`);
											if (!parked) {
												this.toast.warning(
													`Upload failure for ${file.name} could not be saved; please try again.`,
													{
														timeout: 8_000
													}
												);
											} else {
												try {
													const refreshedSession = await getFileUploadSessionById(entry.sessionId);
													if (refreshedSession) this.updateResumedFileIdentity(refreshedSession);
												} catch (readbackError) {
													// The status CAS already succeeded; verification is advisory and must not
													// turn a parked permanent rejection back into an upload retry.
													console.warn(
														`[Upload] Could not verify terminal failure park for "${file.name}"`,
														readbackError
													);
												}
											}
										}
									} catch (sessionError) {
										if (entry.cancelled) throw new UploadCancelledError(`Upload of "${file.name}" was cancelled`);
										// The durable write is best-effort when IndexedDB itself is unavailable, but do not
										// make a permanent verdict look fully persisted: the next load may retry this row.
										console.error(
											`[Upload] Could not persist terminal failure for "${file.name}"; resume may retry it`,
											sessionError
										);
										this.toast.warning(`Upload failure for ${file.name} could not be saved; please try again.`, {
											timeout: 8_000
										});
										// Do not retry the already-rejected upload; only the durable park failed.
									}
								}
								return;
							}

							// A stale / cloud-offloaded HEIC (iCloud "Optimize Storage" — RUM shows this on iOS
							// Safari AND macOS Chrome) makes the multipart read helper throw NotFoundError; that is
							// now classified as a terminal FileUnreadableError AT THE READ (adminChunkSource
							// isFileReadError, LSP-16961), so it arrives here already wrapped. Keep this outer catch
							// keyed on `instanceof FileUnreadableError` — raw DOMExceptions that reach here are
							// IndexedDB errors (quota / SecurityError / NotFoundError) that must retry, not be
							// misread as unreadable files (matches chunkedUploadService's "classify at the read;
							// outer catches use instanceof" contract). The single-part path can't be distinguished
							// here anyway: uploadToS3WithProgress normalizes every transport failure to a generic
							// S3UploadError (see toS3UploadError).
							if (err instanceof FileUnreadableError) {
								this.toast.error(UNREADABLE_FILE_MESSAGE, { timeout: calculateTimeout(UNREADABLE_FILE_MESSAGE) });
								console.error(
									`[Upload] File ${file.name} could not be read; not retrying (cloud-offloaded/stale reference)`,
									{
										conversationId,
										fileName: entry.name,
										fileType: file.type,
										// MPU coords so an "abort failed" report can be correlated to the orphan upload server-side.
										uploadId: entry.uploadTarget ? uploadTargetOpaqueId(entry.uploadTarget) : undefined
									}
								);
								// Free the chunk data, but keep the session row until the server-side MPU is tombstoned.
								// Abort the MPU FIRST (cancelled:true); only drop the session once the abort lands. If the
								// abort fails (offline / S3 5xx — common on the flaky mobile connections this fixes), leave the
								// session marked "cancelled" so drainCancelledUploadSessions retries the abort on the next load,
								// rather than orphaning a partial MPU for the recovery sweep to finalize. Mirrors confirmCancel.
								if (entry.sessionId) {
									await this.deleteEntryChunkData(entry).catch(() => {});
								}
								if (entry.uploadTarget) {
									try {
										await abortAdminMultipartTarget(entry.uploadTarget);
										if (entry.sessionId) {
											try {
												const sessionDeleted = await this.deleteEntrySession(entry, undefined, {
													notifyOtherTabs: false
												});
												if (!sessionDeleted) {
													console.warn(
														`[Upload] MPU abort succeeded but local cleanup was rejected for unreadable "${file.name}" — marking session cancelled for next-load drain`
													);
													await this.markEntrySessionCancelled(entry).catch((markError) =>
														console.warn(
															`[Upload] markEntrySessionCancelled threw for unreadable "${file.name}" — cross-tab notification may not have fired`,
															markError
														)
													);
												}
											} catch (idbError) {
												console.warn(
													`[Upload] MPU abort succeeded but local cleanup failed for unreadable "${file.name}" — marking session cancelled for next-load drain`,
													idbError
												);
												await this.markEntrySessionCancelled(entry).catch((markError) =>
													console.warn(
														`[Upload] markEntrySessionCancelled threw for unreadable "${file.name}" — cross-tab notification may not have fired`,
														markError
													)
												);
											}
										}
									} catch (abortError) {
										console.warn(
											`[Upload] S3 abort failed for unreadable "${file.name}" — marking session cancelled for next-load drain`,
											abortError
										);
										// Best-effort: if this IDB write itself fails (rare — IDB erroring mid-error-handling), the
										// row stays "uploading" and a later resetUploadingSessions flips it to "pending"; that resume
										// then reconciles to UPLOAD_LOST and discards it (self-healing, just a wasted cycle). No better
										// local recovery exists once IDB itself is failing.
										if (entry.sessionId) {
											await this.markEntrySessionCancelled(entry).catch((markError) =>
												console.warn(
													`[Upload] markEntrySessionCancelled threw for unreadable "${file.name}" — cross-tab notification may not have fired`,
													markError
												)
											);
										}
									}
								} else if (entry.sessionId) {
									// No MPU to tombstone (single-part upload); drop the local session.
									await this.deleteEntrySession(entry, undefined, { notifyOtherTabs: false }).catch((deleteError) =>
										console.warn(
											`[Upload] deleteEntrySession threw for unreadable "${file.name}" — local session may still exist`,
											deleteError
										)
									);
								}
								entry.failureMessage = UNREADABLE_FILE_MESSAGE;
								entry.unreadable = true;
								entry.retrying = false;
								entry.failed = true;
								return;
							}

							console.error(err, {
								tags: {
									conversationId
								},
								extra: {
									method: "openDialog",
									file: "FileUploadDialog",
									message: "Failed to upload file to s3.",
									fileType: file.type,
									result: err
								}
							});

							if (entry.retryCount < this.maxRetryCount) {
								entry.retryCount++;
								console.warn("Retrying file upload", {
									conversationId,
									fileName: entry.name,
									retryCount: entry.retryCount
								});
								await uploadWithRetry();
							} else {
								// R-0 (owner directive 2026-08-26): running out of this dialog's in-page retry ladder
								// is no longer a terminal event. The session's bytes are already persisted, the row is
								// left resume-deliverable, and the running app's durable auto-requeue keeps retrying it
								// on an escalating backoff (fileUploadDb `requeueNeedsAttentionFileUploadSessions`). So
								// the copy is a STATUS, not a verdict — telling the user to "try again" when the app is
								// about to try again for them is what made F-17 feel like a dead end.
								//
								// LSP-17798 review follow-up: the toast and the inline failureMessage still share the
								// same copy for the same event; only the wording's finality changed.
								const message = `We're still uploading ${file.name} — it didn't go through yet, and we'll keep retrying automatically.`;
								this.toast.warning(message, { timeout: calculateTimeout(message) });
								console.warn(`File ${file.name} upload ladder exhausted; left to the durable retry drain`, {
									conversationId,
									fileName: entry.name,
									retryCount: entry.retryCount
								});
								if (entry.sessionId) {
									try {
										let session = await getFileUploadSessionById(entry.sessionId);
										const entryIdentity = this.getEntrySessionIdentity(entry);
										// The driver's best-effort release can fail before its error reaches this retry ladder.
										// Use the identity captured before this attempt; a successor can claim while it unwinds.
										if (
											session &&
											(session.status === "pending" ||
												session.status === "queued" ||
												session.status === "uploading") &&
											attemptIdentity?.uploadOwnerId === getCurrentFileUploadSessionOwnerId() &&
											attemptIdentity.uploadOwnerClaimId !== undefined &&
											entryIdentity?.uploadId === attemptIdentity.uploadId &&
											entryIdentity.uploadOwnerId === attemptIdentity.uploadOwnerId &&
											entryIdentity.uploadOwnerClaimId === attemptIdentity.uploadOwnerClaimId &&
											session.uploadId === attemptIdentity.uploadId &&
											session.uploadOwnerId === attemptIdentity.uploadOwnerId &&
											session.uploadOwnerClaimId === attemptIdentity.uploadOwnerClaimId
										) {
											const exhaustedSessionId = session.id;
											const releaseExactAttempt = async (): Promise<FileUploadSession | undefined> => {
												for (let releaseAttempt = 0; releaseAttempt < 2; releaseAttempt++) {
													try {
														const released = await markFileUploadSessionPendingForResume(
															exhaustedSessionId,
															attemptIdentity,
															// LSP-19088: this release used to wipe `lastError`, so a row handed to the
															// drain after the in-page ladder ran out carried a climbing `retryAttempt`
															// and no reason for any of it. Say what happened, so a parked row can be
															// triaged from the row itself instead of from whoever still has the console
															// open.
															{
																reason: `In-page retries were exhausted after ${entry.retryCount} attempt${entry.retryCount === 1 ? "" : "s"}; handed to the durable retry drain. Last error: ${getMessageFromError(err) || "unknown"}`
															}
														);
														if (released) {
															const releasedSession = await getFileUploadSessionById(exhaustedSessionId);
															if (releasedSession) this.updateResumedFileIdentity(releasedSession);
															return releasedSession;
														}
														return undefined;
													} catch (error) {
														if (releaseAttempt === 0) continue;
														console.warn("[Upload] Exhausted-retry lease release will retry", {
															fileName: entry.name,
															sessionId: exhaustedSessionId,
															error
														});
														entry.releaseRetryTimer ??= globalThis.setTimeout(() => {
															entry.releaseRetryTimer = undefined;
															void releaseExactAttempt();
														}, EXHAUSTED_RELEASE_RETRY_DELAY_MS);
													}
												}
												return undefined;
											};
											session = await releaseExactAttempt();
										}
										if (session?.status === "needs-attention") {
											this.updateResumedFileIdentity(session);
										} else if (session?.status === "pending" && session.uploadOwnerId === undefined) {
											// R-0: DELIBERATELY left resume-deliverable. This branch used to terminalize the
											// row as `needs-attention` (LSP-17798) precisely because the UI claimed a
											// permanent failure while the driver had left the row pending — a silent
											// re-upload of a file the user was told to retry by hand. R-0 resolves that
											// contradiction from the other side: the copy above now says the app will keep
											// retrying, and it does, so terminalizing here would be the bug. The entry's
											// identity is still refreshed from the row the driver released.
											this.updateResumedFileIdentity(session);
											console.warn(
												"[Upload] Exhausted-retry session left resume-deliverable for the durable retry drain",
												{
													fileName: entry.name,
													sessionId: entry.sessionId,
													priorStatus: session.status,
													retryCount: entry.retryCount
												}
											);
										} else {
											// Adversarial review (PR #3354): every fail-condition of this gate gets a
											// log line — a session owned by another tab (or in a status this branch
											// doesn't handle) is deliberately left untouched, not a bug to reconstruct
											// from indirect signals later.
											console.warn("[Upload] Exhausted-retry session left untouched", {
												fileName: entry.name,
												sessionId: entry.sessionId,
												sessionStatus: session?.status,
												// LSP-18217: length only, matching every other owner-touching log in this
												// subsystem — a corrupt owner value must never be echoed into the console.
												// Computed inline rather than through fileUploadDb's helper: a dozen specs mock
												// that module with explicit factories, and a new import would break every one
												// of them that reaches this branch.
												sessionOwnerIdLength:
													typeof session?.uploadOwnerId === "string" ? session.uploadOwnerId.length : undefined,
												// codex review follow-up: this only proves an owner exists, not that it
												// belongs to another tab (could be this tab's own recovered claim, e.g.
												// if releasePending failed and a later lookup recovered) — label it as
												// what it actually is.
												reason: !session
													? "session-missing"
													: !isUploadSessionDeliverableByResume(session.status)
														? "not-resume-deliverable"
														: "session-owned"
											});
										}
									} catch (sessionLookupError) {
										console.warn(
											`[Upload] Could not refresh identity for needs-attention "${file.name}"`,
											sessionLookupError
										);
									}
								}
								entry.failureMessage = message;
								// R-0 follow-through: the copy above promises the app will keep retrying, and the
								// branch above deliberately leaves the session resume-deliverable so it does. Setting
								// `failed` here was the last piece of the old verdict — it rendered the row red and
								// counted it in the dialog header's "N uploads failed", which is supposed to mean
								// "these need YOU to do something". `retrying` is the same idea as `handedOff`: a row
								// that is not finished and not failed, shown in warning tone, excluded from the
								// failure count. It is cleared by every terminal transition
								// (markResumedFileComplete/Failed/HandedOff) and by the next byte of progress.
								entry.retrying = true;
								entry.failed = false;
								exhaustedByBatchIndexes.add(i);
							}
						}
					};

					try {
						await uploadWithRetry();
					} catch (err) {
						if (err instanceof UploadCancelledError) {
							entry.cancelled = true;
							return;
						}
						const message = `We couldn't upload ${file.name}. Please try again — if it keeps happening, contact support.`;
						console.error(err, {
							tags: { conversationId },
							extra: {
								method: "openDialog",
								file: "FileUploadDialog",
								message: "Unexpected per-file upload failure escaped retry handling.",
								fileName: file.name,
								fileType: file.type
							}
						});
						entry.failureMessage = message;
						entry.retrying = false;
						entry.failed = true;
					}
				};
				noteBatchLaneActivity();
				try {
					await Promise.all(batchEntries.map(async (batchEntry, index) => {
						try {
							await preparationPromises[index];
							if (batchEntry.deferred) {
								if (batchEntry.backgroundFinishingLoggedAt === undefined)
									logBackgroundFinishingEntered("dialog-batch-preparation-deferred", batchEntry);
								followUpDeferredEntry(batchEntry, index, "transient-deferral");
							}
							noteBatchLaneActivity();
							await this.withFileUploadSlot(() => uploadEntry(batchEntry, index));
						} finally {
							// Release the row the moment this lane is done with it, however it ended. The pin has
							// to outlast `uploadEntry`'s ENTRY, not just its queue wait: `queuedForUpload` is
							// cleared as its first statement but the durable lease claim only lands several awaits
							// later, and a drain pass in that window admits a row this lane is actively setting up
							// (break-it round 1, angle B — all three active rows of a 23-file batch were taken).
							if (batchEntry.failed || batchEntry.cancelled || batchEntry.sessionId)
								forgetUnpersistedUploadFile(uploadBatchId, index);
							batchEntry.queuedForUpload = false;
							batchEntry.laneOwned = false;
							if (batchEntry.sessionId) void releaseLaneIntentBestEffort([batchEntry.sessionId], "row-finally");
							if (!this.fileList.some((entry: FileWithProgress) => entry.laneOwned === true)) {
								void releaseLaneLeaseIfHeld();
							}
							noteBatchLaneActivity();
							transferSettledResolvers[index]();
						}
					}));
				} finally {
					for (const settle of transferSettledResolvers) settle();
					if (!this.fileList.some((entry: FileWithProgress) => entry.laneOwned === true)) {
						void releaseLaneLeaseIfHeld();
					}
				}
				await preparationSummary;
				const targetFailure = await targetPreparation;
				const checkingLateSessionLookup = batchEntries.some((entry) => entry.awaitingLateSessionLookup && !entry.cancelled);
				const hasHardFailure = Boolean(targetFailure) || batchEntries.some((entry) => entry.failed && !entry.awaitingLateSessionLookup && !entry.cancelled);
				let cancelledCount = 0;
				let deferredCount = 0;
				// A row parked by the twice-confirmed gone-target verdict (LSP-19325) is neither uploaded nor
				// failed: its bytes are kept and the durable drain requeues it. Counting it with the deferred
				// rows keeps it out of the "unable to upload some files" arithmetic below — that red toast is
				// for genuine faults, and the row already raised its own warning toast (Jake, LSP-19325 review).
				let parkedCount = 0;
				// LSP-23174: rows the background drain took over before this batch ended. The drain's own row updates clear
				// `deferred` (progress, completion, hand-off to the server sweep), so the flags above no longer say "background"
				// for them. Without these counts such a row is neither uploaded nor deferred, and the caller tells the user to
				// upload files again that are already uploading (crowd test 2026-10-28: 19 of 19 files arrived on their own).
				let finishedInBackgroundCount = 0;
				let takenOverStartFailureCount = 0;
				const notUploadedIndexes: number[] = [];
				const cancelledByCategory = { images: 0, documents: 0, videos: 0 };
				const deferredByCategory = { images: 0, documents: 0, videos: 0 };
				const checkingByCategory = { images: 0, documents: 0, videos: 0 };
				for (const [index, entry] of batchEntries.entries()) {
					if (entry.cancelled) {
						cancelledCount++;
						cancelledByCategory[categoryForIndex(index)]++;
					} else if (entry.deferred || entry.ownershipUncertain) {
						deferredCount++;
						deferredByCategory[categoryForIndex(index)]++;
					} else if (entry.needsAttention) {
						deferredCount++;
						parkedCount++;
						deferredByCategory[categoryForIndex(index)]++;
					} else if (uploadedByBatchIndexes.has(index)) {
						// Uploaded by this batch; counted in `itemsUploaded`.
					} else if (
						entry.sessionId &&
						!entry.failed &&
						!entry.awaitingLateSessionLookup &&
						!(exhaustedByBatchIndexes.has(index) && entry.retrying)
					) {
						// A durable row this batch neither uploaded nor gave up on, and nothing has failed: the drain is driving
						// it, finished it, or handed it to the server sweep. A finished one already counts in `completedCounts`.
						// A row this batch exhausted counts here only once the drain has moved it on (its progress clears
						// `retrying`).
						if (entry.progress >= 100) {
							finishedInBackgroundCount++;
						} else {
							deferredCount++;
							deferredByCategory[categoryForIndex(index)]++;
							if (startFailureIndexes.has(index)) takenOverStartFailureCount++;
						}
					} else {
						notUploadedIndexes.push(index);
					}
					if (entry.awaitingLateSessionLookup && !entry.cancelled) checkingByCategory[categoryForIndex(index)]++;
				}
				const uploadedCount = itemsUploaded + finishedInBackgroundCount;
				const completedCounts = {
					images: validImages.length - cancelledByCategory.images - deferredByCategory.images - checkingByCategory.images,
					documents:
						validDocuments.length - cancelledByCategory.documents - deferredByCategory.documents - checkingByCategory.documents,
					videos: validVideos.length - cancelledByCategory.videos - deferredByCategory.videos - checkingByCategory.videos
				};
				batch.leavingHintFullyDrained =
					batchEntries.length > 0 &&
					unsupportedEntries.length === 0 &&
					itemsUploaded === batchEntries.length &&
					batchEntries.every(
						(entry) =>
							entry.progress === 100 &&
							!entry.failed &&
							!entry.cancelled &&
							!entry.deferred &&
							!entry.ownershipUncertain &&
							!entry.needsAttention &&
							!entry.awaitingLateSessionLookup
					);

				// The foreground owns the whole selected batch; drain passes only see subsets of failed starts.
				const startFailureCount = [...startFailureIndexes].filter(
					index => !batchEntries[index].cancelled && !batchEntries[index].needsAttention && batchEntries[index].progress < 100 &&
						batchEntries[index].retrying && batchEntries[index].failureMessage === startFailureMessage
				).length;
				if (startFailureCount > 0) {
					const message = uploadStartRetryMessage(`uploading ${startFailureCount} file${startFailureCount === 1 ? "" : "s"}`);
					this.toast.error(message, { timeout: calculateTimeout(message) });
				}
				const isLastBatchInFlight = this.releaseUploadBatch(batch);
				// break-it round 2: read the live count rather than this batch's verdict. A batch that took no
				// claim (nothing uploadable) still has to be able to shut an empty dialog, which it did before
				// the claim became conditional, and no batch may shut the dialog while another is still running.
				if (this.activeUploadBatches === 0 && this.allDisplayFiles.length === 0) this.dialog = false;

				if (
					uploadedCount < batchEntries.length - cancelledCount - deferredCount &&
					(!checkingLateSessionLookup || hasHardFailure)
				) {
					// LSP-23174: this result is what makes the caller say "unable to upload some files", and the report
					// that found it had to be reconstructed from drain logs. Name every row behind the verdict.
					console.warn("[Upload] Batch ended with files that did not upload", {
						conversationId,
						uploadBatchId,
						batchSize: batchEntries.length,
						uploadedByBatch: itemsUploaded,
						finishedInBackground: finishedInBackgroundCount,
						stillInBackground: deferredCount,
						cancelled: cancelledCount,
						targetPreparationFailed: Boolean(targetFailure),
						checkingLateSessionLookup,
						notUploaded: notUploadedIndexes.map((index) => ({
							index,
							sessionId: batchEntries[index].sessionId,
							hasSession: Boolean(batchEntries[index].sessionId),
							failed: batchEntries[index].failed === true,
							retrying: batchEntries[index].retrying === true,
							handedOff: batchEntries[index].handedOff === true,
							awaitingLateSessionLookup: batchEntries[index].awaitingLateSessionLookup === true,
							progress: batchEntries[index].progress
						}))
					});
					return { success: false, counts: targetFailure && itemsUploaded > 0 ? uploadedCounts : null };
				}
				if (checkingLateSessionLookup) {
					return { success: false, checking: true, counts: uploadedCount > 0 ? completedCounts : null };
				}

				// Parked rows have already said what they needed to say (their own warning toast); only rows
				// genuinely finishing in the background earn the "will finish" promise. LSP-23174: neither do failed
				// starts the drain has already taken over while the start-failure toast is up — it already promised
				// that they continue automatically, so a second toast for them would be noise.
				const backgroundCount =
					deferredCount - parkedCount - startFailureCount - (startFailureCount > 0 ? takenOverStartFailureCount : 0);
				if (uploadedCount > 0) {
					if (deferredCount > 0) {
						if (backgroundCount > 0) {
							const message = "Some files uploaded; the rest will finish in the background.";
							this.toast.info(message, { timeout: calculateTimeout(message) });
						}
						return { success: false, deferred: true, counts: completedCounts };
					}
					// LSP-19543: "Files were uploaded successfully." speaks for every upload in flight, not for
					// this batch alone. A user who adds a second batch while the first is still running was
					// otherwise told everything had finished the moment the first batch settled — and acting on
					// that (closing the tab, navigating away) interrupts the batch that is still running. Only
					// the batch that takes the in-flight count to zero gets to make the claim.
					// break-it round 2: `releaseUploadBatch` emits "UploadFinished" synchronously, so a listener
					// that starts another upload runs BEFORE this line and `isLastBatchInFlight` is already
					// stale. Recheck the live count — for every non-re-entrant caller this is the same answer.
					// QA reopen (LSP-19543): the count above only knows about FOREGROUND dialog batches. After
					// a reload the durable drain resumes the pending rows in the background, so a batch
					// finishing here can still be the not-last upload in the tab. `backgroundUploadAttemptsInFlight`
					// is the drain's side of the same stateless rule: whichever side finishes last speaks.
					const backgroundUploadsInFlight = backgroundUploadAttemptsInFlight();
					if (isLastBatchInFlight && this.activeUploadBatches === 0 && backgroundUploadsInFlight === 0) {
						const message = "Files were uploaded successfully.";
						this.toast.success(message, { timeout: calculateTimeout(message) });
					} else {
						console.info("[Upload] Batch finished while other uploads are still in flight; withholding the success toast", {
							conversationId,
							uploadBatchId,
							activeUploadBatches: this.activeUploadBatches,
							backgroundUploadAttemptsInFlight: backgroundUploadsInFlight,
							itemsUploaded
						});
					}
					return {
						success: true,
						counts: completedCounts
					};
				}

				if (deferredCount > 0) {
					if (backgroundCount > 0) {
						const message = "Uploads will finish in the background.";
						this.toast.info(message, { timeout: calculateTimeout(message) });
					}
					return { success: false, deferred: true, counts: null };
				}

				if (targetFailure) return { success: false, counts: null };

				// Reached when nothing succeeded and nothing failed — i.e. every entry in the batch was cancelled.
				// Flag as cancelled so the caller suppresses its "failed" error toast.
				return { success: false, cancelled: true, counts: null };
			},
			getIcon(type: string, fileName: string) {
				const file = { type, name: fileName } as File;
				const mimeType = inferMimeType(file);
				if (mimeType.startsWith("image/")) {
					return "icon-image";
				} else if (mimeType.startsWith("video/")) {
					return "icon-video-solid";
				} else {
					return "icon-file-blank-solid";
				}
			},
			openForResumedUploads(
				sessions: (Partial<Pick<FileUploadSession, "uploadId" | "uploadOwnerId" | "uploadOwnerClaimId">> & {
					sessionId: string;
					fileName: string;
					fileSize: number;
					fileType: string;
				})[],
				activeResumedSessionIds?: ReadonlySet<string>
			) {
				const sessionsById = new Map<string, (typeof sessions)[number][]>();
				for (const session of sessions) {
					const matchingSessions = sessionsById.get(session.sessionId) ?? [];
					matchingSessions.push(session);
					sessionsById.set(session.sessionId, matchingSessions);
				}
				const legitimateReusedSessionsById = new Map<string, (typeof sessions)[number]>();
				for (const invalidatedSessionId of this.invalidatedUploadSessionIds) {
					const discoveredSessions = sessionsById.get(invalidatedSessionId);
					if (!discoveredSessions) {
						if (this.observedInvalidatedUploadSessionIds.has(invalidatedSessionId)) {
							this.clearUploadSessionInvalidation(invalidatedSessionId);
							console.info(
								"[Upload] Cleared invalidated upload session tombstone after the previously observed session became absent",
								{ sessionId: invalidatedSessionId }
							);
						} else {
							this.confirmedAbsentInvalidatedUploadSessionIds.add(invalidatedSessionId);
							console.info("[Upload] Confirmed invalidated upload session is absent from resume snapshot", {
								sessionId: invalidatedSessionId
							});
						}
						continue;
					}

					const invalidatedUploadId = this.invalidatedUploadIdsBySessionId.get(invalidatedSessionId);
					// LSP-23105: a session id is minted per pick, so a snapshot still carrying this session's own
					// placeholder predates its first target. It is a stale read, never a reuse of the session. A
					// tombstone recorded with that placeholder likewise names no incarnation to be reused past.
					const legitimateReusedSession = discoveredSessions.find(
						(session) =>
							invalidatedUploadId !== undefined &&
							!isOwnPendingTargetPlaceholder(invalidatedSessionId, invalidatedUploadId) &&
							session.uploadId !== undefined &&
							session.uploadId !== invalidatedUploadId &&
							!isOwnPendingTargetPlaceholder(invalidatedSessionId, session.uploadId)
					);
					if (legitimateReusedSession) {
						legitimateReusedSessionsById.set(invalidatedSessionId, legitimateReusedSession);
						this.clearUploadSessionInvalidation(invalidatedSessionId);
						console.info("[Upload] Cleared invalidated upload session tombstone for legitimate session reuse", {
							sessionId: invalidatedSessionId,
							uploadId: legitimateReusedSession.uploadId
						});
					} else if (this.confirmedAbsentInvalidatedUploadSessionIds.has(invalidatedSessionId)) {
						continue;
					} else {
						this.observedInvalidatedUploadSessionIds.add(invalidatedSessionId);
					}
				}
				const invalidatedSessionIds = new Set(this.invalidatedUploadSessionIds);
				const resumableSessions = sessions.filter((session) => !invalidatedSessionIds.has(session.sessionId));
				const allPreviouslyDismissed =
					resumableSessions.length > 0 &&
					resumableSessions.every((session) => this.dismissedResumedSessionIds.has(session.sessionId));
				const allSessionsInvalidated = sessions.length > 0 && resumableSessions.length === 0;

				const resumedSessionIds = new Set(resumableSessions.map((session) => session.sessionId));
				this.fileList = this.fileList.filter(
					(file) =>
						!file.sessionId ||
						// LSP-22061: a row this tab tombstoned itself stays as its own failed row. Its session is
						// invalidated (by this tab's own cross-tab echo) only so it is never resumed.
						file.tombstonedLocally ||
						(!invalidatedSessionIds.has(file.sessionId) &&
							(!resumedSessionIds.has(file.sessionId) || (!file.failed && !file.cancelled)))
				);

				// Several drain specs borrow these methods onto a hand-rolled `this` that predates these
				// fields, and a missing Set would throw inside the tray reconciliation below — which
				// would wipe out the whole resumed-tray update, not just this feature. Self-heal instead.
				this.remoteCompletedResumedSessionIds ??= new Set<string>();
				this.observedAbsentHandedOffSessionIds ??= new Set<string>();
				const remoteCompletedResumedSessionIds = this.remoteCompletedResumedSessionIds;
				const observedAbsentHandedOffSessionIds = this.observedAbsentHandedOffSessionIds;

				// A handed-off row is the ONE resumed state whose durable row is guaranteed gone: the
				// terminal recovery policy only returns HANDED_OFF after `deleteLocalSession` reported a
				// successful delete (adminTerminalRecoveryPolicy), and it only reaches that verdict when
				// the evidence was `remote-complete` — the server holds the assembled object. So the
				// session can never appear in a resume snapshot again, and the old predicate below kept
				// the row on `file.handedOff` forever: "Finishing in the background…" with nothing left
				// to finish, and no pass that could ever clear it.
				//
				// Every other absent-from-snapshot state is NOT proof of anything (a retrying row is
				// simply parked on a future `retryAt`, an actively-resuming row is excluded from
				// discovery by its controller), so this reconciliation is deliberately scoped to
				// `handedOff` alone.
				//
				// The honest presentation is COMPLETE, not gone: the server has the file. Silently
				// dropping the row would tell the user nothing about an upload they watched sit there.
				//
				// Confirmed over TWO observations, the same two-pass shape the invalidated-session
				// tombstones above already use. A drain can coalesce a second discovery wave into the
				// pass that produced the hand-off, and flipping on that wave would contradict the
				// announcement the very same pass is about to make ("N will finish in the background").
				const stillPresentHandedOffSessionIds = new Set<string>();
				// Every session id the tray still holds a row for, in any state — the bound on
				// `observedAbsentHandedOffSessionIds` (see the prune after this loop).
				const trackedResumedSessionIds = new Set<string>();
				for (const file of this.resumedFileList) {
					if (file.sessionId) trackedResumedSessionIds.add(file.sessionId);
					if (!file.handedOff || !file.sessionId) continue;
					if (resumedSessionIds.has(file.sessionId) || activeResumedSessionIds?.has(file.sessionId)) {
						stillPresentHandedOffSessionIds.add(file.sessionId);
						continue;
					}
					if (invalidatedSessionIds.has(file.sessionId)) continue;
					if (file.cancelPending) {
						// Same rule as markResumedFileComplete: an in-flight cancellation owns this row's
						// terminal state, and a background reconciliation must not revoke it.
						console.warn(`[Upload] Left handed-off row "${file.name}" alone while its cancellation is in flight`, {
							sessionId: file.sessionId
						});
						continue;
					}
					if (!observedAbsentHandedOffSessionIds.has(file.sessionId)) {
						observedAbsentHandedOffSessionIds.add(file.sessionId);
						console.info("[Upload] Handed-off upload session is absent from a resume snapshot; awaiting confirmation", {
							sessionId: file.sessionId,
							uploadId: file.uploadId
						});
						continue;
					}
					console.info("[Upload] Handed-off upload is finished on the server; showing it as complete", {
						sessionId: file.sessionId,
						uploadId: file.uploadId,
						fileName: file.name
					});
					logBackgroundFinishingCleared("handed-off-completed-on-server", file);
					file.handedOff = false;
					file.deferred = false;
					file.ownershipUncertain = false;
					// LSP-22113: the server has the file; a stale preparing flag would read "Preparing… 100%".
					file.isPreparing = false;
					file.retrying = false;
					file.failed = false;
					file.failureMessage = undefined;
					file.progress = 100;
					file.pendingCancel = false;
					observedAbsentHandedOffSessionIds.delete(file.sessionId);
					remoteCompletedResumedSessionIds.add(file.sessionId);
					if (remoteCompletedResumedSessionIds.size > MAX_INVALIDATED_UPLOAD_SESSION_TOMBSTONES) {
						const oldestSessionId = remoteCompletedResumedSessionIds[Symbol.iterator]().next().value;
						if (oldestSessionId !== undefined) remoteCompletedResumedSessionIds.delete(oldestSessionId);
					}
				}
				// A hand-off that came back into a snapshot (a legitimate session reuse) restarts the
				// confirmation, so a stale first observation cannot combine with a much later one.
				for (const sessionId of stillPresentHandedOffSessionIds) observedAbsentHandedOffSessionIds.delete(sessionId);
				// …and drop the first observation for any row the tray no longer holds at all. Unlike its
				// sibling `remoteCompletedResumedSessionIds` (capped at MAX_INVALIDATED_UPLOAD_SESSION_
				// TOMBSTONES), this set had no bound and no exit other than the confirm/reappear paths
				// above: a handed-off row dismissed, cancelled out of the tray, or dropped by the
				// keep-predicate below while awaiting its second observation left its id here for the
				// lifetime of the page. Pruning to what the tray actually holds is the exact bound —
				// there is no reason to remember a first observation for a row nobody can see, and it
				// also prevents a much later reuse of the same session id from being confirmed on ONE
				// observation. Deleting from a Set while iterating it is well-defined.
				for (const sessionId of observedAbsentHandedOffSessionIds) {
					if (!trackedResumedSessionIds.has(sessionId)) observedAbsentHandedOffSessionIds.delete(sessionId);
				}

				const shouldKeepExistingResumedEntry = (file: FileWithProgress) =>
					!file.sessionId ||
					(!invalidatedSessionIds.has(file.sessionId) &&
						(resumedSessionIds.has(file.sessionId)
							? !file.failed && !file.cancelled
							: file.failed ||
								file.cancelled ||
								file.handedOff ||
								// Keeps the row the loop above just flipped from handed-off to complete. It is
								// at progress 100 with no session left to discover, so every other clause is
								// false and it would otherwise be dropped on this very pass.
								remoteCompletedResumedSessionIds.has(file.sessionId) ||
								(Boolean(file.uploadBatchId) && file.progress === 100) ||
								(file.progress < 100 && !file.handedOff) ||
								activeResumedSessionIds?.has(file.sessionId)));

				const resumedEntries = this.resumedFileList.filter(shouldKeepExistingResumedEntry);
				for (const session of resumableSessions) {
					const legitimateReusedSession = legitimateReusedSessionsById.get(session.sessionId);
					if (legitimateReusedSession && session !== legitimateReusedSession) continue;

					const activeFileListEntry = this.fileList.find(
						(file) => file.sessionId === session.sessionId && !file.failed && !file.cancelled
					);
					if (activeFileListEntry) {
						if (activeFileListEntry.uploadId && session.uploadId && activeFileListEntry.uploadId !== session.uploadId) {
							activeFileListEntry.pendingCancel = false;
						}
						activeFileListEntry.uploadId = session.uploadId;
						activeFileListEntry.uploadOwnerId = session.uploadOwnerId;
						activeFileListEntry.uploadOwnerClaimId = session.uploadOwnerClaimId;
						if (session.sourcePrepared) activeFileListEntry.sourcePrepared = true;
						if (activeFileListEntry.verifyingOrphan) {
							// Bugbot (LSP-19766): a real resume attaching to this row must also disarm the
							// tray-placeholder marker — otherwise clearVerifyingOrphanedUpload deletes this now-real
							// row outright the next time the drain flags-then-clears the same session (e.g. a
							// momentary 0%-progress, no-controller retry window).
							activeFileListEntry.verifyingOrphan = false;
							activeFileListEntry.verifyingOrphanRowCreatedByTray = false;
						}
						if (activeFileListEntry.deferred && !activeFileListEntry.ownershipUncertain) {
							logBackgroundFinishingCleared("resume-snapshot-reactivated", activeFileListEntry);
							activeFileListEntry.isPreparing = false;
							activeFileListEntry.handedOff = false;
							activeFileListEntry.deferred = false;
						}
						continue;
					}

					const activeResumedEntry = resumedEntries.find(
						(file) => file.sessionId === session.sessionId && !file.failed && !file.cancelled
					);
					if (activeResumedEntry) {
						if (activeResumedEntry.uploadId && session.uploadId && activeResumedEntry.uploadId !== session.uploadId) {
							activeResumedEntry.pendingCancel = false;
						}
						activeResumedEntry.uploadId = session.uploadId;
						activeResumedEntry.uploadOwnerId = session.uploadOwnerId;
						activeResumedEntry.uploadOwnerClaimId = session.uploadOwnerClaimId;
						if (session.sourcePrepared) activeResumedEntry.sourcePrepared = true;
						if (activeResumedEntry.verifyingOrphan) {
							// Bugbot (LSP-19766): see the matching activeFileListEntry branch above — clearing
							// verifyingOrphan alone leaves a stale verifyingOrphanRowCreatedByTray marker that would
							// let a later flag-then-clear cycle delete this now-real resumed row outright.
							activeResumedEntry.verifyingOrphan = false;
							activeResumedEntry.verifyingOrphanRowCreatedByTray = false;
						}
						if (activeResumedEntry.deferred && !activeResumedEntry.ownershipUncertain) {
							activeResumedEntry.isPreparing = false;
							activeResumedEntry.handedOff = false;
							activeResumedEntry.deferred = false;
						}
						continue;
					}
					resumedEntries.push({
						progress: 0,
						isPreparing: false,
						name: session.fileName,
						size: session.fileSize,
						type: session.fileType,
						lastModified: 0,
						failed: false,
						retryCount: 0,
						sessionId: session.sessionId,
						uploadId: session.uploadId,
						uploadOwnerId: session.uploadOwnerId,
						uploadOwnerClaimId: session.uploadOwnerClaimId,
						pendingCancel: false,
						cancelPending: false,
						cancelled: false,
						deferred: false
					});
				}
				this.resumedFileList = resumedEntries;

				if (allSessionsInvalidated) {
					console.info("[Upload] Resumed upload tray remains closed because all sessions were invalidated", {
						sessionIds: sessions.map((session) => session.sessionId)
					});
					return;
				}
				if (allPreviouslyDismissed) {
					console.info("[Upload] Resumed upload tray remains closed because all resumable sessions were previously dismissed", {
						sessionIds: resumableSessions.map((session) => session.sessionId)
					});
					return;
				}
				if (this.allDisplayFiles.length === 0) {
					console.info("[Upload] Resumed upload tray remains closed because no displayable uploads remain", {
						sessionCount: sessions.length
					});
					return;
				}

				this.dismissedResumedSessionIds.clear();
				this.dialog = true;
			},
			/**
			 * LSP-19384: show the rows the app has stopped retrying on its own.
			 *
			 * Deliberately a sibling of `openForResumedUploads` rather than a branch inside it. That
			 * method's whole contract is "these rows are about to be uploaded" — the drain attaches an
			 * AbortController to every row it surfaces there. A parked row must appear WITHOUT any of
			 * that, so it gets its own entry point and touches nothing but presentation.
			 *
			 * The tray opens for them. These rows need the user to act, and the action is durable in
			 * both directions: Remove writes a cancellation, which is the one status the startup reset
			 * does not reclaim, so a removed row is gone for good rather than back on the next load.
			 *
			 * **The set of parked sessions is STATE, and the rows are a projection of it.** Six review
			 * rounds found eight defects in this one method, and every one had the same cause: it decided
			 * what to do to a row from how the row LOOKED, and it needed a different answer for each
			 * reason a session might have left the snapshot (no longer parked, invalidated, claimed by a
			 * lane, or already resolved by the user). That matrix grew a new wrong cell every round.
			 *
			 * So there is no matrix now. `parkedUploads` is the authority: a session is in it or it is
			 * not, and a row is parked if and only if its session is in it. Everything that used to be a
			 * reason to skip — invalidated, live-claimed, locally resolved — is simply a reason not to
			 * PUT the session in the map, decided in one loop. Un-parking never has to ask why a session
			 * left. Do not reintroduce a per-reason branch, and do not key any decision here off a
			 * presentation flag: `findFileBySessionId` is how you find the one row for a session.
			 *
			 * `readAt` is when the caller READ the snapshot, captured before the read. The drain does not
			 * await this call, so passes can land out of order, and recency is now checked in exactly two
			 * places: once for the whole pass (an older read may not revoke a newer read's verdict) and
			 * once per session (an older read may not overwrite a newer one's `uploadId`, because a
			 * reused session id on a stale uploadId is how Remove aborts the wrong upload).
			 */
			showTerminallyParkedUploads(
				sessions: {
					sessionId: string;
					uploadId?: string;
					fileName: string;
					fileSize: number;
					fileType: string;
					status?: FileUploadSession["status"];
					lastError?: string;
				}[],
				readAt?: number
			) {
				this.resumedFileList ??= [];
				// Recency gate for the whole pass. Without it a stale empty discovery could un-park a row a
				// newer pass had just parked. A pass with no read time is not refused here: it cannot be
				// shown to be older, and the per-session rule below still protects established values.
				if (readAt !== undefined && this.parkedUploadsReadAt !== undefined && readAt < this.parkedUploadsReadAt) return;

				const invalidatedSessionIds = new Set(this.invalidatedUploadSessionIds ?? []);
				const nextParkedUploads = new Map<string, ParkedUploadSnapshot>();
				// A terminal transition can land while discovery is awaiting IndexedDB. Preserve that
				// newer observation, including against an empty snapshot; a later read may retire it.
				for (const [sessionId, held] of this.parkedUploads as Map<string, ParkedUploadSnapshot>) {
					if (
						held.status === "recovery-blocked" &&
						!invalidatedSessionIds.has(sessionId) &&
						!this.wasParkedUploadResolvedAfter(sessionId, held.readAt) &&
						(readAt === undefined || (held.readAt !== undefined && readAt <= held.readAt))
					) {
						nextParkedUploads.set(sessionId, held);
						console.warn("[Upload] Retaining newer recovery-blocked observation over stale parked discovery", {
							sessionId, readAt, heldReadAt: held.readAt
						});
					}
				}
				for (const session of sessions) {
					// Every exclusion lives here, and none of them is remembered as a special case: a session
					// excluded for ANY reason is simply absent, and an absent session un-parks its row.
					if (invalidatedSessionIds.has(session.sessionId)) continue;
					// Older than the user's own Try again or Remove. That read still lists the session, and
					// acting on it resurrects a row the user already resolved — a ghost beside the restarted
					// upload, whose Remove aborts that upload.
					if (this.wasParkedUploadResolvedAfter(session.sessionId, readAt)) continue;
					// A lane actively working this session outranks the snapshot: the durable read can lag a
					// claim that already restarted the upload, and showing one file as both "uploading" and
					// "we stopped trying" is worse than either. `retrying` and `deferred` are deliberately
					// NOT active — a row between attempts is exactly the row that must park once the durable
					// row says no further attempt is coming.
					//
					// Asked of EVERY row for the session rather than of `findFileBySessionId`, deliberately.
					// That helper prefers a row that is not failed or cancelled, and since a parked row is
					// now `needsAttention` rather than `failed` (see `applyParkedPresentation`), it would
					// hand back the parked row itself and hide a genuinely live one behind it.
					const activelyUploadingElsewhere = (this.allDisplayFiles as FileWithProgress[]).some(
						(file) =>
							file.sessionId === session.sessionId &&
							!file.retryCeilingReached &&
							!file.failed &&
							!file.needsAttention &&
							!file.cancelled &&
							!file.cancelPending &&
							!file.retrying &&
							!file.deferred
					);
					if (activelyUploadingElsewhere) continue;
					// Deduped by id, and an older read never overwrites a newer one's values.
					const held = this.parkedUploads.get(session.sessionId);
					const isNewer = readAt !== undefined && (held?.readAt === undefined || readAt > held.readAt);
					nextParkedUploads.set(session.sessionId, held !== undefined && !isNewer ? held : { ...session, readAt });
				}

				this.parkedUploads = nextParkedUploads;
				if (readAt !== undefined) this.parkedUploadsReadAt = readAt;
				this.projectParkedUploadsOntoRows();
			},

			/**
			 * LSP-19384: make the rows match `parkedUploads` exactly. Idempotent by construction — run it
			 * as often as you like — because it derives every parked row from that map rather than from
			 * what the previous pass happened to do.
			 */
			projectParkedUploadsOntoRows() {
				let newlyParkedCount = 0;
				for (const [sessionId, session] of this.parkedUploads as Map<string, ParkedUploadSnapshot>) {
					const row = this.findFileBySessionId(sessionId) as FileWithProgress | undefined;
					// LSP-19767: a lost-record row's own verdict outranks any durable snapshot, and the
					// recency gate cannot protect it — that gate orders discovery passes against each other,
					// not against the eviction. A read taken BEFORE the storage was wiped can land after this
					// row was surfaced, and re-applying it would replace the evidence-specific sentence with
					// the generic "we stopped trying after repeated failures" text, losing the hedge that
					// keeps an UNVERIFIED loss from reading as a confirmed one (AI review, PR #4964). The row
					// is already parked, so skipping the re-apply changes nothing else about it.
					if (row?.localRecordLost) continue;
					if (row) {
						if (!row.retryCeilingReached) {
							newlyParkedCount += 1;
							// Parking a row another mechanism owns overwrites its presentation, so remember what
							// it looked like. When the session leaves the map, the row goes back to that rather
							// than being deleted (it may be a live upload's row, controller and progress included)
							// or left stranded in a parked presentation nothing would clear.
							row.preParkedPresentation = {
								failed: row.failed,
								retrying: row.retrying,
								needsAttention: row.needsAttention,
								deferred: row.deferred,
								isPreparing: row.isPreparing,
								failureMessage: row.failureMessage
							};
						}
						this.applyParkedPresentation(row, session);
						continue;
					}
					const newRow: FileWithProgress = {
						progress: 0,
						isPreparing: false,
						name: session.fileName,
						size: session.fileSize,
						type: session.fileType,
						lastModified: 0,
						failed: false,
						retryCount: 0,
						sessionId: session.sessionId,
						pendingCancel: false,
						cancelPending: false,
						cancelled: false,
						deferred: false,
						// This row exists only because this method made it, so this method may delete it. A row
						// it merely PARKED belongs to someone else and must survive un-parking.
						parkedRowCreatedByTray: true
					};
					this.applyParkedPresentation(newRow, session);
					(this.resumedFileList as FileWithProgress[]).push(newRow);
					newlyParkedCount += 1;
				}

				const keepAfterUnparking = (file: FileWithProgress) => {
					if (!file.retryCeilingReached) return true;
					// Cancellation temporarily invalidates discovery, but owns this row until it
					// settles. Keep it attached so a refused/failed cancel can restore its actions.
					if (file.cancelPending) return true;
					// LSP-19767: this row's durable snapshot is GONE — that is the state it reports — so it
					// can never be in `parkedUploads` and the check below would un-park it every pass.
					if (file.localRecordLost) return true;
					if (file.sessionId !== undefined && this.parkedUploads.has(file.sessionId)) return true;
					// Fresh durable absence (including cancellation in another tab) retires a terminal
					// row. Stale absence was filtered when constructing the authoritative map above.
					if (file.recoveryBlocked) {
						console.info("[Upload] Retiring recovery-blocked row absent from fresh parked discovery", { sessionId: file.sessionId });
						return false;
					}
					const priorPresentation = file.preParkedPresentation;
					file.retryCeilingReached = false;
					file.recoveryBlocked = false;
					file.serverTerminal = false;
					file.parkedReadAt = undefined;
					file.preParkedPresentation = undefined;
					if (priorPresentation) {
						file.failed = priorPresentation.failed;
						file.retrying = priorPresentation.retrying;
						file.needsAttention = priorPresentation.needsAttention;
						file.deferred = priorPresentation.deferred;
						file.isPreparing = priorPresentation.isPreparing;
						file.failureMessage = priorPresentation.failureMessage;
					}
					return file.parkedRowCreatedByTray !== true;
				};
				// Both lists, because a row parked in place can live in either one. Scanning only
				// `resumedFileList` leaves a parked `fileList` row parked forever after the snapshot drops
				// it, with no automatic retry coming and no pass that would ever clear it.
				this.resumedFileList = (this.resumedFileList as FileWithProgress[]).filter(keepAfterUnparking);
				this.fileList = (this.fileList as FileWithProgress[]).filter(keepAfterUnparking);

				if (newlyParkedCount === 0) return;
				console.info("[Upload] Showing upload(s) that are no longer retried automatically", {
					sessionIds: [...this.parkedUploads.keys()]
				});
				this.dialog = true;
			},

			/**
			 * The parked presentation, in one place so a created row and a parked-in-place row cannot
			 * drift apart.
			 *
			 * `needsAttention`, NOT `failed`. LSP-19325 (PR #4603, already on release/99) settled that a
			 * durably parked upload is warning-toned and counted in its own "N uploads need attention"
			 * bucket, because red and "N uploads failed" are reserved for rows where the customer did
			 * something wrong. Nobody did anything wrong here either — the network kept failing under a
			 * perfectly good file — so this state joins that bucket rather than inventing a second look
			 * for the same idea. `retryCeilingReached` is what still distinguishes it: it is the flag the
			 * Try again button keys off, and the reason this row's copy must not promise a retry.
			 */
			applyParkedPresentation(row: FileWithProgress, session: ParkedUploadSnapshot) {
				// A parked row no longer renders "finishing in the background" (`deferred` is cleared below),
				// so this is an exit for a row that entered it; a no-op for one that never did (LSP-19867).
				logBackgroundFinishingCleared("parked-needs-attention", row);
				row.uploadId = session.uploadId ?? row.uploadId;
				row.recoveryBlocked = session.status === "recovery-blocked";
				row.serverTerminal =
					isUploadTerminalFailure(session.lastError) || (!!row.uploadId && this.serverTerminalUploadIds.has(row.uploadId));
				// `parkedFailureMessage`, with the retry question answered by the verdict above rather than by this
				// snapshot alone, so a snapshot that predates a refusal cannot invite a Try again that is not there.
				row.failureMessage = row.recoveryBlocked
					? RECOVERY_BLOCKED_ROW_MESSAGE
					: stoppedUploadMessage(session.lastError, !row.serverTerminal);
				row.needsAttention = true;
				row.refreshNotice = false;
				row.failed = false;
				row.retrying = false;
				row.deferred = false;
				row.isPreparing = false;
				row.retryCeilingReached = true;
				row.parkedReadAt = session.readAt;
			},

			/**
			 * LSP-19767 (data-loss): the browser evicted this upload's local record mid-flight, and the
			 * upload registry could not prove the server assembled the file.
			 *
			 * Measured on staging: all three S3 PUTs of an 11.4 MB PNG returned 200, then the IndexedDB
			 * stores were wiped. `/complete` never fired, so the server never assembled the file, and the
			 * app said NOTHING — the tray row kept reading "Finishing in the background…" and the header
			 * sat at "Media: (0) - 1 Pending" forever. Browsers evict this storage on their own (low disk,
			 * Safari's 7-day cap, private windows, "clear site data"), so the customer did nothing wrong.
			 *
			 * Reuses `applyParkedPresentation` deliberately: this is the same idea LSP-19384 already
			 * shipped a surface for — "the app has stopped trying and only you can act" — so it gets that
			 * row, that tone, that header bucket, and that Try again / Remove pair. The one thing it does
			 * not reuse is the copy: the retained bytes are gone with the record, so Try again cannot
			 * resume anything and the row must say so up front (`retryTerminallyParkedFileUploadSession`
			 * answers "no-local-bytes" and `retryParkedUpload` toasts the same instruction).
			 */
			markLocalUploadRecordLost(sessionId: string, evidence: "absent" | "unverified" = "unverified", conversationId?: string) {
				const row = this.findFileBySessionId(sessionId) as FileWithProgress | undefined;
				// Silent-failure rule: four different refusals used to share one bare `return`, so a row that
				// was NOT surfaced looked identical to one that never reached here (AI review, PR #4964).
				const declineSurfacing = (refusal: string) => {
					console.warn("[Upload] Not surfacing a lost local upload record", { sessionId, conversationId, refusal, evidence });
				};
				if (!row) return declineSurfacing("row-gone");
				if (row.cancelled) return declineSurfacing("cancelled");
				if (row.cancelPending) return declineSurfacing("cancel-pending");
				if (row.localRecordLost) return declineSurfacing("already-surfaced");
				// Deliberately NOT gated on `progress >= 100`. All of this ticket's own bytes reach the
				// store before the eviction — the measured case is a 3-of-3-parts row whose `/complete`
				// never fired — so a progress gate here would refuse exactly the row this state exists to
				// surface. "The row completed" is the registry's question, and it is asked there:
				// `adminDeferredRowFollowUp` re-checks `isStillDeferred()` after the probe resolves.
				row.localRecordLost = true;
				// Named before the generic parked exit inside applyParkedPresentation so the log says WHY the
				// row left the finishing state (LSP-19867, review finding).
				logBackgroundFinishingCleared("local-record-lost", row);
				this.applyParkedPresentation(row, {
					sessionId,
					fileName: row.name,
					fileSize: row.size,
					fileType: row.type,
					uploadId: row.uploadId
				});
				row.failureMessage = LOCAL_UPLOAD_RECORD_LOST_MESSAGES[evidence];
				// `applyParkedPresentation` clears `deferred`; these two render the same "Finishing in the
				// background…" line and would otherwise keep the row promising a finish that is not coming.
				row.handedOff = false;
				row.ownershipUncertain = false;
				// LSP-20327: the row above is in-memory only, and the state it reports is "this upload is in
				// no durable store" — so a reload has nothing to rebuild it from and the warning, its Try
				// again and its Remove all disappear while the conversation still reads "1 Pending". Write
				// the notice down so the next load can put the same row back.
				rememberLostUploadRecordNotice({
					sessionId,
					fileName: row.name,
					fileSize: row.size,
					fileType: row.type,
					uploadId: row.uploadId,
					conversationId,
					evidence,
					noticedAt: Date.now()
				});
				// Silent-failure rule: the whole defect was that this moment produced no signal anywhere.
				console.warn("[Upload] This upload's local record is gone; surfacing it for the user to act on", {
					sessionId,
					conversationId,
					uploadId: row.uploadId,
					// Which of the two sentences the row is showing, so a support report can be matched to
					// what the probe actually proved rather than guessed at from the copy.
					evidence
				});
				this.dialog = true;
			},

			restoreDurableUploadBatchRows(snapshots: { session: FileUploadSession; progress: number }[]) {
				const byId = new Map(snapshots.map((snapshot) => [snapshot.session.id, snapshot]));
				const existingById = new Map(this.allDisplayFiles.map((file) => [file.sessionId, file]));
				const existingByBatchFile = new Map(this.allDisplayFiles.map(file => [`${file.uploadBatchId}:${file.batchFileIndex}`, file]));
				const added: FileWithProgress[] = [];
				const batches = readUploadBatchRecords();
				// Completed metadata is recovery context for an unfinished batch, not upload history
				// to reopen on every mount or append to a later selection (LSP-22138).
				const unfinishedBatchIds = new Set(snapshots
					.filter(({ session }) => session.status !== "complete" && session.status !== "cancelled")
					.map(({ session }) => session.batchId));
				// Foreground-owned rows are excluded from snapshots; unread/failed sources may have no session yet.
				for (const file of this.allDisplayFiles) {
					if (file.progress < 100 && !file.cancelled) unfinishedBatchIds.add(file.uploadBatchId);
				}
				for (const batch of batches) {
					if (batch.files.some(file => file.failureMessage || (!file.persisted && !byId.has(file.sessionId ?? "")))) {
						unfinishedBatchIds.add(batch.uploadBatchId);
					}
				}
				const recordedIds = new Set(batches.flatMap(batch => batch.files.map(file => file.sessionId)));
				// Active sources outlive the bounded notice cache. Restore their own durable identity.
				for (const { session } of snapshots) {
					if (!session.batchId || !session.batchOrganizationId || session.batchFileIndex === undefined || recordedIds.has(session.id)) continue;
					// An incomplete source needs its unread-file notice. Do not recreate a dismissed/expired
					// warning as Preparing; a live writer may still finish and make this source durable.
					if (session.targetPending && !session.sourcePrepared) continue;
					batches.push({
						conversationId: session.conversationId, organizationId: session.batchOrganizationId,
						uploadBatchId: session.batchId, laneId: "", createdAt: session.createdAt,
						files: [{ index: session.batchFileIndex, sessionId: session.id, name: session.fileName, size: session.fileSize, type: session.fileType }]
					});
				}
				for (const batch of batches) {
					if (batch.organizationId !== useAuthSessionStore().organizationId) continue;
					for (const file of batch.files) {
						if (file.failureMessage) {
							if (!this.allDisplayFiles.some((row) => row.uploadBatchId === batch.uploadBatchId && row.batchFileIndex === file.index)) added.push({
								name: file.name, size: file.size, type: file.type, lastModified: 0, progress: 0, isPreparing: false,
								failed: true, needsAttention: true, localRecordLost: true, recoveryBlocked: true,
								failureMessage: file.failureMessage, uploadBatchId: batch.uploadBatchId, batchFileIndex: file.index,
								retryCount: 0, pendingCancel: false, cancelPending: false, cancelled: false, deferred: false
							});
							continue;
						}
						if (!file.sessionId) continue;
						const snapshot = byId.get(file.sessionId);
						if (!snapshot) continue;
						const { session, progress } = snapshot;
						if (session.status === "cancelled") {
							forgetUploadBatchFile(batch.uploadBatchId, file.index);
							continue;
						}
						// Foreground callbacks own this page's active rows; snapshots represent the
						// other tabs and restored rows without replacing an in-flight controller.
						const existing = existingById.get(session.id) ?? existingByBatchFile.get(`${batch.uploadBatchId}:${file.index}`);
						if (session.status === "complete" && !existing && !unfinishedBatchIds.has(batch.uploadBatchId)) continue;
						if (existing?.laneOwned || existing?.abortController || existing?.cancelPending || existing?.cancelled) continue;
						if (existing?.localRecordLost && session.targetPending && !session.sourcePrepared) continue;
						const failed = session.status === "needs-attention" || session.status === "recovery-blocked";
						const active = !failed && session.status !== "complete";
						// A metadata refresh must not undo the foreground's handoff or hide its cancel control.
						const finishingInBackground = active && Boolean(existing?.deferred || existing?.handedOff || existing?.ownershipUncertain);
						const row: FileWithProgress = {
							name: session.fileName, size: session.fileSize, type: session.fileType, lastModified: 0,
							sessionId: session.id, uploadId: session.uploadId, uploadOwnerId: session.uploadOwnerId,
							uploadOwnerClaimId: session.uploadOwnerClaimId, uploadBatchId: batch.uploadBatchId, batchFileIndex: file.index,
							progress: session.status === "complete" ? 100 : Math.min(progress, 99),
							verificationPending: active && isUploadVerificationPending(session.lastError),
							isPreparing: session.targetPending === true && active && !finishingInBackground,
							preparationProgress: session.sourcePrepared ? 100 : 0,
							sourcePrepared: session.sourcePrepared === true,
							failed, needsAttention: failed, refreshNotice: false, retryCeilingReached: failed, localRecordLost: false, recoveryBlocked: session.status === "recovery-blocked",
							serverTerminal: failed && isUploadTerminalFailure(session.lastError),
							failureMessage: failed
								? session.status === "recovery-blocked"
									? RECOVERY_BLOCKED_ROW_MESSAGE
									: parkedFailureMessage(session.lastError)
								: undefined,
							retryCount: 0,
							// Metadata/chunk acknowledgements refresh observer rows while a decision is open.
							// Carry it only for this same active durable upload and batch file.
							pendingCancel: Boolean(active && existing?.pendingCancel &&
								existing.sessionId === session.id && existing.uploadId === session.uploadId &&
								existing.uploadBatchId === batch.uploadBatchId && existing.batchFileIndex === file.index),
							cancelPending: false, cancelled: false,
							retrying: false, handedOff: active && existing?.handedOff === true,
							ownershipUncertain: active && existing?.ownershipUncertain === true, verifyingOrphan: false,
							deferred: active && (existing?.deferred === true || (session.status === "pending" && !session.targetPending))
						};
						if (existing) Object.assign(existing, row);
						else added.push(row);
					}
				}
				this.resumedFileList.push(...added);
				if (added.some((file) => !this.dismissedResumedSessionIds.has(file.sessionId!))) this.dialog = true;
			},

			// LSP-19521: metadata can explain the lost File, but cannot reconstruct its bytes.
			async restoreUnpersistedUploadNotices(isCurrent: () => boolean = () => true) {
				const restoredRows: FileWithProgress[] = [];
				// LSP-22007 (silent-failure rule): an org switch or remount supersedes this pass, which used to stop without a trace.
				const stillCurrent = (stage: string) => {
					if (isCurrent()) return true;
					console.info("[Upload] Unpersisted batch restore superseded", { stage });
					return false;
				};
				for (const notice of readUnpersistedUploadNotices()) {
					const context = { conversationId: notice.conversationId, uploadBatchId: notice.uploadBatchId };
					const currentOrganizationId = useAuthSessionStore().organizationId;
					// LSP-23085: the mount pass runs before the organization loads and is re-run once it does, so name
					// which condition skipped the batch; one shared line made every restore look like a refusal.
					const skipReason = !currentOrganizationId
						? "organization-not-loaded"
						: notice.organizationId !== currentOrganizationId
							? "different-organization"
							: notice.laneId === getCurrentFileUploadLaneId()
								? "current-page"
								: undefined;
					if (skipReason === "organization-not-loaded") {
						console.info("[Upload] Unpersisted batch restore waiting for the organization to load", context);
						continue;
					}
					if (skipReason) {
						console.warn("[Upload] Unpersisted batch restore skipped", { ...context, reason: skipReason });
						continue;
					}
					const ownerLockProbe = await isUploadOwnerLockFree(notice.laneId);
					const ownerGone = ownerLockProbe === true;
					if (!ownerGone && hasUploadOwnerLockManager()) {
						// `null` is an indeterminate probe, not a held lock; the two need different triage.
						console.warn("[Upload] Unpersisted batch restore deferred; original page may still be active", {
							...context,
							ownerLockProbe
						});
						continue;
					}
					if (!stillCurrent("owner-probe")) return;
					let unreadFileCount = 0;
					let deferredFileCount = 0;
					const settledCleanups: Record<string, number> = {};
					for (const file of notice.files) {
						try {
							const target = file.target;
							const uploadId = target?.type === "multipart" ? uploadTargetOpaqueId(target) : target?.registryId;
							// A storage write may have landed just before reload but before the manifest was retired.
							const session = file.sessionId
								? await getFileUploadSessionById(file.sessionId)
								: uploadId
									? await getFileUploadSessionByUploadId(uploadId)
									: undefined;
							if (!stillCurrent("session-read")) return;
							if (session && (!session.targetPending || session.sourcePrepared)) {
								if (file.sessionId) updateUploadBatchFile(notice.uploadBatchId, file.index, { persisted: true });
								else forgetUnpersistedUploadFile(notice.uploadBatchId, file.index);
								continue;
							}
							if (ownerGone && session?.targetPending) {
								// LSP-22007: the warning below must not depend on this cancel. One that does not take
								// effect is finished by the orphan sweep, which removes a dead placeholder under its
								// writer fence; retrying here could hit a live tab's row or another organization's.
								let cancelError: unknown;
								const outcome = await cancelAdminUploadSession({ sessionId: session.id }).catch((error) => {
									cancelError = error;
									return "threw" as const;
								});
								if (outcome === "acknowledged" || outcome === "missing") {
									settledCleanups[outcome] = (settledCleanups[outcome] ?? 0) + 1;
								} else {
									const remaining = await getFileUploadSessionById(session.id).catch(() => undefined);
									console.warn("[Upload] Unpersisted file cleanup after refresh", {
										...context,
										sessionId: session.id,
										fileIndex: file.index,
										outcome,
										status: remaining?.status,
										...(cancelError === undefined ? {} : { error: cancelError })
									});
								}
							}
							if (!stillCurrent("cancel")) return;
							const existing = this.allDisplayFiles.find(
								(row) => row.uploadBatchId === notice.uploadBatchId && row.batchFileIndex === file.index
							);
							if (
								existing &&
								(existing.laneOwned ||
									existing.abortController ||
									existing.cancelPending ||
									(!existing.localRecordLost &&
										(!existing.isPreparing || existing.preparationProgress === 100 || existing.progress === 100)))
							) {
								console.warn("[Upload] Ignoring stale unread-source probe; tray already has newer upload state", {
									...context,
									sessionId: existing.sessionId,
									fileName: file.name
								});
								continue;
							}
							const warning: FileWithProgress = {
								// This notice explains uncertainty; dismissing it must never cancel another page's session.
								sessionId: undefined, uploadId: undefined, uploadTarget: undefined,
								uploadOwnerId: undefined, uploadOwnerClaimId: undefined,
								name: file.name,
								size: file.size,
								type: file.type,
								lastModified: 0,
								progress: 0,
								isPreparing: false,
								failed: false,
								retryCount: 0,
								pendingCancel: false,
								cancelPending: false,
								cancelled: false,
								deferred: false,
								needsAttention: true,
								refreshNotice: true,
								localRecordLost: true,
								recoveryBlocked: true,
								uploadBatchId: notice.uploadBatchId,
								batchFileIndex: file.index,
								failureMessage: ownerGone
									? "This file wasn't uploaded because the page refreshed. Please re-add it to upload it."
									: "This file may still be preparing in another tab. If that tab was refreshed or closed, please re-add it to upload it."
							};
							if (existing) Object.assign(existing, warning);
							else restoredRows.push(warning);
							if (!ownerGone) {
								deferredFileCount++;
								continue;
							}
							unreadFileCount++;
							const abort =
								target?.type === "multipart"
									? abortAdminMultipartTarget(target)
									: target?.family === "uploads"
										? abortAdminDirectTarget(target)
										: undefined;
							void abort?.catch((error) =>
								console.warn("[Upload] Could not abort unpersisted file after refresh", {
									...context,
									fileName: file.name,
									error
								})
							);
						} catch (error) {
							console.warn("[Upload] Could not reconcile unpersisted file after refresh", {
								...context,
								fileName: file.name,
								error
							});
						}
					}
					// LSP-22007: one line per batch — a notice is re-read on every page load for up to seven days,
					// and a per-file line made a 950-file batch log 950 lines on each load. A cleanup that did not
					// take effect still gets its own line above.
					if (unreadFileCount > 0)
						console.warn("[Upload] Reserved files were not persisted before refresh", {
							...context,
							fileCount: unreadFileCount,
							settledCleanups
						});
					if (deferredFileCount > 0)
						console.warn("[Upload] Unread sources need attention; cleanup deferred without page liveness proof", {
							...context,
							fileCount: deferredFileCount
						});
				}
				if (!stillCurrent("render")) return;
				this.resumedFileList.push(...restoredRows);
				if (restoredRows.length) this.dialog = true;
			},

			/**
			 * LSP-20327 (data-loss, reload boundary): put back the LSP-19767 rows this browser was told
			 * about before the page was reloaded.
			 *
			 * Reload is not a resolution. Until the person presses Try again or Remove, they are owed the
			 * same sentence and the same two controls they had a moment ago — the alternative is the
			 * reported defect: a silent tray, a missing file, and a conversation stuck on "1 Pending"
			 * that explains nothing.
			 *
			 * Rows are rebuilt exactly the way `projectParkedUploadsOntoRows` builds a parked row it owns,
			 * then handed to `markLocalUploadRecordLost` so the restored row and the original are the same
			 * row by construction, evidence-specific copy included.
			 */
			restoreLostUploadRecordNotices() {
				const notices = readLostUploadRecordNotices();
				if (notices.length === 0) return;
				this.resumedFileList ??= [];
				for (const notice of notices) {
					// Silent-failure rule: every refusal below says which one it was. A notice that is held
					// but not shown is this defect happening again, and it must not look like "none stored".
					const existing = this.findFileBySessionId(notice.sessionId) as FileWithProgress | undefined;
					if (existing) {
						console.warn("[Upload] Not restoring a lost-upload-record notice; the tray already has this row", {
							sessionId: notice.sessionId,
							conversationId: notice.conversationId,
							evidence: notice.evidence
						});
						continue;
					}
					const restoredRow: FileWithProgress = {
						progress: 0,
						isPreparing: false,
						name: notice.fileName,
						size: notice.fileSize,
						type: notice.fileType,
						lastModified: 0,
						failed: false,
						retryCount: 0,
						sessionId: notice.sessionId,
						uploadId: notice.uploadId,
						pendingCancel: false,
						cancelPending: false,
						cancelled: false,
						deferred: false,
						// Same ownership claim the tray's own parked rows make: nothing else created this row,
						// so the tray may remove it.
						parkedRowCreatedByTray: true
					};
					(this.resumedFileList as FileWithProgress[]).push(restoredRow);
					this.markLocalUploadRecordLost(notice.sessionId, notice.evidence, notice.conversationId);
					// LSP-20327: a server-confirmed finish retires the notice even when no local row survived.
					// Legacy ids cannot prove an exact assembled registry receipt and leave the warning intact.
					if (notice.uploadId) {
						const restored = this.findFileBySessionId(notice.sessionId);
						void readUploadRegistryForCompletionRefusal({ uploadId: notice.uploadId, endpointFamily: "uploads" })
							.then((verdict) => {
								if (
									verdict === "assembled" &&
									this.findFileBySessionId(notice.sessionId) === restored &&
									restored?.uploadId === notice.uploadId &&
									restored?.localRecordLost
								) {
									this.markResumedFileComplete(notice.sessionId);
								}
							})
							.catch((error) =>
								console.warn("[Upload] Could not reconcile a restored lost-upload notice", {
									sessionId: notice.sessionId,
									error
								})
							);
					}
				}
			},

			/**
			 * LSP-19384: true when the user resolved this parked upload (Try again or Remove) at or after
			 * the moment `readAt` names, so a snapshot read before that click must not resurrect it.
			 *
			 * A snapshot with no read time cannot prove it is newer than the click, so a resolved session
			 * is refused outright — the safe direction. The row is not lost: the very next discovery pass
			 * carries a `readAt` after the resolution and re-adds the row if the DB still says it is
			 * parked, which is the case where the user retried and it genuinely re-parked later.
			 */
			wasParkedUploadResolvedAfter(sessionId: string, readAt?: number): boolean {
				const resolvedAt = this.parkedUploadResolvedAt.get(sessionId);
				if (resolvedAt === undefined) return false;
				return readAt === undefined || readAt <= resolvedAt;
			},

			/**
			 * Record that the user has dealt with a parked upload themselves. Bounded like the
			 * invalidation tombstones beside it, so a long-lived tab cannot accumulate these without end.
			 */
			noteParkedUploadResolvedLocally(sessionId: string | undefined) {
				if (!sessionId) return;
				// LSP-20327: the person has acted on this row (a retry that restarted, or a Remove the
				// server acknowledged), so the reload-survival notice has done its job. Every other caller
				// of this method never had a notice, and forgetting one that does not exist is a no-op.
				forgetLostUploadRecordNotice(sessionId);
				this.parkedUploadResolvedAt.set(sessionId, Date.now());
				if (this.parkedUploadResolvedAt.size > MAX_INVALIDATED_UPLOAD_SESSION_TOMBSTONES) {
					const oldestSessionId = this.parkedUploadResolvedAt.keys().next().value;
					if (oldestSessionId !== undefined) this.parkedUploadResolvedAt.delete(oldestSessionId);
				}
			},

			/**
			 * LSP-19384: the user's explicit Retry on a parked row. This is the ONLY thing that re-arms
			 * automatic retry — see `retryTerminallyParkedFileUploadSession`, which clears the ceiling
			 * and its counters together so the row gets a full budget rather than re-parking instantly.
			 */
			async retryParkedUpload(entry: FileWithProgress) {
				if (!entry.sessionId || entry.retryInFlight || entry.cancelled || entry.cancelPending) return;
				entry.retryInFlight = true;
				try {
					const outcome = await retryTerminallyParkedFileUploadSession(entry.sessionId, entry.uploadId);
					if (outcome === "retrying") {
						// Before removing the row: a parked-upload discovery pass that READ before this click
						// still lists the session, and would otherwise insert a ghost row beside the upload
						// that is now restarting — whose Remove would abort that very upload.
						this.noteParkedUploadResolvedLocally(entry.sessionId);
						// Hand the row back to the drain's presentation: it is pending again, so the next
						// discovery pass owns it and will re-render it as an ordinary resumed upload.
						this.resumedFileList = (this.resumedFileList as FileWithProgress[]).filter((file) => file !== entry);
						this.fileList = (this.fileList as FileWithProgress[]).filter((file) => file !== entry);
						// ...but only if a discovery pass actually happens. `retryTerminallyParkedFileUploadSession`
						// sets `retryAt: null` so the attempt is due immediately, and the drain's scheduler only
						// arms a timer from a NUMERIC `retryAt` — so re-arming alone schedules nothing, and a
						// drain that had gone idle stays idle. Ask for a pass explicitly.
						this.$emit("parked-upload-rearmed", entry.sessionId);
						const message = "Trying that upload again.";
						this.toast.info(message, { timeout: calculateTimeout(message) });
						return;
					}
					// LSP-23172: the server has failed this upload for good, so the row stops offering Try again and the
					// toast repeats the row's own support instruction rather than a "refresh" that cannot help.
					if (outcome === "server-terminal") this.adoptServerTerminalVerdict(entry);
					const message =
						outcome === "no-local-bytes"
							? "This file is no longer stored on this device, so we can't try again. You'll need to upload it once more."
							: outcome === "server-terminal"
								? (entry.failureMessage ?? stoppedUploadMessage(undefined))
								: "That upload has already changed. Refresh to see where it is now.";
					this.toast.warning(message, { timeout: calculateTimeout(message) });
				} catch (error) {
					console.warn(`[Upload] Could not retry the parked upload "${entry.name}"`, error);
					const message = "Something went wrong starting that upload again. Please try again.";
					this.toast.warning(message, { timeout: calculateTimeout(message) });
				} finally {
					entry.retryInFlight = false;
				}
			},

			/**
			 * LSP-23172: the store refused this row's Try again because its durable record carries the server's
			 * permanent verdict, which the snapshot behind the row predates. The refusing write already proved the
			 * verdict for this upload id (it checks `expectedUploadId`), so remember the id and update the row's
			 * copy now — no second IndexedDB read that could fail or disagree with the refusal. From here on
			 * `applyParkedPresentation` keeps the row terminal whatever snapshot it is handed.
			 */
			adoptServerTerminalVerdict(entry: FileWithProgress) {
				if (entry.uploadId) {
					this.serverTerminalUploadIds.add(entry.uploadId);
					if (this.serverTerminalUploadIds.size > MAX_INVALIDATED_UPLOAD_SESSION_TOMBSTONES) {
						const oldestUploadId = this.serverTerminalUploadIds.values().next().value;
						if (oldestUploadId !== undefined) this.serverTerminalUploadIds.delete(oldestUploadId);
					}
				}
				entry.serverTerminal = true;
				entry.failureMessage = stoppedUploadMessage(undefined);
			},

			findFileBySessionId(sessionId: string) {
				const activeEntry =
					this.resumedFileList.find((f) => f.sessionId === sessionId && !f.failed && !f.cancelled) ||
					this.fileList.find((f) => f.sessionId === sessionId && !f.failed && !f.cancelled);
				return (
					activeEntry ||
					this.resumedFileList.find((f) => f.sessionId === sessionId) ||
					this.fileList.find((f) => f.sessionId === sessionId)
				);
			},
			/**
			 * LSP-19766: presentation-only row for a session the recurring drain has found orphaned by a
			 * prior page load but is deliberately NOT reclaiming yet (heartbeat not yet stale — see
			 * fileUploadDb.ts's `resetUploadingSessions`). Deliberately a sibling of `showTerminallyParkedUploads`
			 * rather than a branch inside `openForResumedUploads`: no controller is attached and no attempt
			 * is started, exactly like that method's own docstring describes for parked rows.
			 *
			 * Adversarial pass (LSP-19766): production is structurally supposed to prevent the drain
			 * from ever reporting a session THIS page is actively driving (fileUploadDb.ts's
			 * `isCurrentPageAdmittedRow` gate), but this presentation method must not trust that across
			 * a reload, a duplicate-tab race, or a future refactor. If a row already exists for this
			 * sessionId and is doing something real — genuinely uploading (has a controller or upload
			 * target, or real byte progress) or durably parked awaiting the user's Try again/Remove —
			 * leave it alone entirely rather than downgrading it to "verifying".
			 */
			showVerifyingOrphanedUpload(session: { sessionId: string; fileName: string; fileSize: number; fileType: string }) {
				const existing = this.findFileBySessionId(session.sessionId);
				if (existing) {
					const isGenuinelyActiveOrParked =
						existing.progress > 0 ||
						!!existing.abortController ||
						!!existing.uploadTarget ||
						!!existing.directUploadTarget ||
						!!existing.retryCeilingReached ||
						// Bugbot (LSP-19766): a row still queued in this page's own batch or mid-preparation is
						// also doing something real — the docstring above promises to leave such a row alone
						// entirely, but omitting these two flags let a real queued/preparing row get downgraded
						// to "verifying" with no promotion path back once its real upload actually started.
						!!existing.queuedForUpload ||
						!!existing.isPreparing;
					// Bugbot (LSP-19766): flagging an EXISTING row (as opposed to the brand-new placeholder
					// pushed below) never marks it `verifyingOrphanRowCreatedByTray` — that row is real
					// (still queued, still preparing, whatever it is), not a tray-only placeholder, and
					// `clearVerifyingOrphanedUpload` must never delete it outright the way it deletes a
					// genuine placeholder.
					if (!existing.failed && !existing.cancelled && !isGenuinelyActiveOrParked) existing.verifyingOrphan = true;
					return;
				}
				this.resumedFileList.push({
					progress: 0,
					isPreparing: false,
					name: session.fileName,
					size: session.fileSize,
					type: session.fileType,
					lastModified: 0,
					failed: false,
					retryCount: 0,
					sessionId: session.sessionId,
					pendingCancel: false,
					cancelPending: false,
					cancelled: false,
					deferred: false,
					verifyingOrphan: true,
					verifyingOrphanRowCreatedByTray: true
				});
				this.dialog = true;
			},
			/**
			 * LSP-19766: the drain calls this once a previously-reported orphaned session is no longer
			 * being held back (reclaimed, or genuinely gone). `openForResumedUploads` already clears the
			 * flag on the success path when the row resumes for real, by attaching a real uploadId and
			 * (via `setResumedFileAbortController`) a controller — this is the direct clear for the OTHER
			 * case, where the row simply stops being reported and nothing genuine ever attached to it.
			 *
			 * Bugbot (LSP-19766, round 1): merely flipping the flag left a ghost row behind — 0%
			 * progress, no failed/cancelled/deferred/handedOff flags — which then matched
			 * `remainingFiles` and read as a live "Uploading 1 item" forever, engaging the screen wake
			 * lock for a session that is gone.
			 *
			 * Bugbot (LSP-19766, round 2): the first fix over-corrected — it deleted ANY row carrying
			 * `verifyingOrphan`, but `showVerifyingOrphanedUpload` can also set that flag on an EXISTING
			 * real row (still queued, still preparing) rather than a placeholder it created. Only a row
			 * `showVerifyingOrphanedUpload` itself PUSHED (`verifyingOrphanRowCreatedByTray`) is safe to
			 * delete outright; an existing row that was merely flagged just has the flag cleared, the
			 * same "delete a tray-only placeholder, not just its flag" rule `projectParkedUploadsOntoRows`'s
			 * `keepAfterUnparking` already applies to parked rows.
			 *
			 * Also closes the tray when this was the last row left to show — a removal is presentation
			 * state changing, same as every other row-removing method in this file (see
			 * `finishUploadBatch`'s and `projectParkedUploadsOntoRows`'s own `allDisplayFiles.length === 0`
			 * checks) — so an emptied tray does not linger open on a stale "Uploaded 0 items".
			 */
			clearVerifyingOrphanedUpload(sessionId: string) {
				const file = this.findFileBySessionId(sessionId);
				if (!file?.verifyingOrphan) return;
				if (file.verifyingOrphanRowCreatedByTray) {
					this.resumedFileList = (this.resumedFileList as FileWithProgress[]).filter((f) => f !== file);
					this.fileList = (this.fileList as FileWithProgress[]).filter((f) => f !== file);
				} else {
					file.verifyingOrphan = false;
				}
				if (this.allDisplayFiles.length === 0) this.dialog = false;
			},
			setResumedFileAbortController(sessionId: string, controller: AbortController) {
				const file = this.findFileBySessionId(sessionId);
				if (file) {
					file.abortController = controller;
				}
			},
			updateResumedFileIdentity(session: FileUploadSession) {
				const file = this.findFileBySessionId(session.id);
				if (!file) return;
				if (file.uploadId && session.uploadId && file.uploadId !== session.uploadId) {
					file.pendingCancel = false;
				}
				file.uploadId = session.uploadId;
				file.uploadOwnerId = session.uploadOwnerId;
				file.uploadOwnerClaimId = session.uploadOwnerClaimId;
			},
			updateResumedFileProgress(sessionId: string, bytesUploaded: number) {
				const file = this.findFileBySessionId(sessionId);
				if (file) {
					// Rechecking a landed upload replays its byte checkpoint before reading acceptance.
					// Keep the finishing phase stable until an actual outcome changes it.
					if (file.verificationPending) return;
					logBackgroundFinishingCleared("resume-progress", file);
					file.deferred = false;
					file.handedOff = false;
					// Bytes are moving again, so the "we'll keep retrying" status from the previous pass
					// is stale. Clear it with the other not-active-right-now flags. `needsAttention` too:
					// a parked row the requeue cycle picked back up is being driven right now, and the
					// template checks that flag before every active state (Fable review, LSP-19325).
					file.retrying = false;
					file.needsAttention = false;
					file.recoveryBlocked = false;
					file.failureMessage = undefined;
					// LSP-22113: bytes reaching storage prove preparation is over; a restored row would
					// otherwise read "Preparing… 100%" for the whole resumed upload.
					file.isPreparing = false;
					file.progress = Math.min(Math.round((bytesUploaded / file.size) * 100), 99);
				}
			},
			markResumedFileComplete(sessionId: string) {
				const file = this.findFileBySessionId(sessionId);
				if (file?.cancelPending) {
					// A durable cancellation is mid-flight for this row and owns its terminal state.
					// Clearing `cancelled` here would silently revoke the user's explicit cancellation
					// while the tombstone write is still outstanding, re-admitting the row to every
					// `!file.cancelled` active-row filter (openForResumedUploads, findFileBySessionId).
					// `cancelPending` is likewise NOT cleared here: it is confirmCancel's re-entrancy
					// guard and the gate that hides the cancel control, and confirmCancel clears it on
					// every exit — so the "Cancellation pending…" row is transient, not stranded.
					console.warn(`[Upload] Ignored resume completion for "${file.name}" while its cancellation is in flight`, {
						sessionId
					});
				} else if (file) {
					file.verificationPending = false;
					const wasParked = file.retryCeilingReached === true || file.localRecordLost === true;
					logBackgroundFinishingCleared("resume-complete", file);
					file.deferred = false;
					// A parked row that the slow requeue eventually delivered is COMPLETE, and must count in
					// "N uploaded" rather than read "needs attention" at 100% forever.
					file.needsAttention = false;
					file.handedOff = false;
					file.failed = false;
					file.retrying = false;
					file.failureMessage = undefined;
					file.cancelled = false;
					file.pendingCancel = false;
					// LSP-22113: a row restored after a refresh as "Preparing… 100%" is frozen once the drain
					// claims it (the restore skips rows with an `abortController`), so completion must retire it.
					file.isPreparing = false;
					file.progress = 100;
					// The parked presentation must go with it. `retryCeilingReached` is what the Try again
					// button keys off — and, since this PR let a parked row be removed at 100%, the Remove
					// control too — so leaving it set on a row that just COMPLETED offers the user two
					// actions on a finished upload (AI review, PR #4964). `localRecordLost` likewise: the
					// record cannot be lost for a row the driver just reported complete.
					file.retryCeilingReached = false;
					file.recoveryBlocked = false;
					file.serverTerminal = false;
					file.localRecordLost = false;
					file.parkedReadAt = undefined;
					file.preParkedPresentation = undefined;
					// Clearing the FLAGS is not enough: a discovery pass whose read predates this completion
					// can still be holding the session in `parkedUploads`, and the next projection would
					// re-apply the parked presentation over a finished row. Retire it the same way the user's
					// own Try again does (AI review, PR #4964) — but ONLY for a row that was actually
					// parked. The tombstone map is capped, so spending an entry on every ordinary resume
					// completion would evict genuine user resolutions from a large batch.
					if (wasParked) {
						this.parkedUploads.delete(sessionId);
						this.noteParkedUploadResolvedLocally(sessionId);
					}
				}
				this.dismissedResumedSessionIds.delete(sessionId);
			},
			markResumedFileFailed(sessionId: string, failureMessage?: string) {
				const file = this.findFileBySessionId(sessionId);
				if (file) {
					file.verificationPending = false;
					logBackgroundFinishingCleared("drain-resume-failed", file);
					file.failureMessage = failureMessage;
					file.deferred = false;
					file.handedOff = false;
					file.retrying = false;
					file.needsAttention = false;
					file.failed = true;
				}
			},
			/**
			 * The durable row is parked as `needs-attention` — not failed, and not retrying.
			 *
			 * Company policy: red and "N uploads failed" are for rows the CUSTOMER has to act on
			 * because something they supplied was wrong. This state is the opposite — a good file whose
			 * server-side upload target went away underneath it — so it renders warning-toned and is
			 * counted in its own header bucket. It is also not `retrying`, because that copy promises an
			 * automatic retry that will land and here it will not; saying so is the point of LSP-19325.
			 *
			 * `deferred` is cleared too: it renders "Finishing in the background…", which would claim the
			 * server has taken ownership of a row nothing is currently going to finish.
			 */
			markResumedFileNeedsAttention(sessionId: string, statusMessage?: string, options?: { recoveryBlocked?: boolean }) {
				const file = this.findFileBySessionId(sessionId);
				if (file) {
					logBackgroundFinishingCleared("drain-resume-needs-attention", file);
					file.failureMessage = statusMessage;
					file.verificationPending = false;
					file.deferred = false;
					file.handedOff = false;
					file.retrying = false;
					file.failed = false;
					file.needsAttention = true;
					file.refreshNotice = false;
					file.recoveryBlocked = options?.recoveryBlocked === true;
					// Recovery-blocked is terminal for automatic recovery as soon as the resume lane
					// reports it. Set the durable-row marker immediately so Cancel cannot briefly route
					// through the legacy local-only path while parked discovery is still catching up.
					if (file.recoveryBlocked) {
						file.retryCeilingReached = true;
						this.parkedUploads.set(sessionId, {
							sessionId,
							uploadId: file.uploadId,
							fileName: file.name,
							fileSize: file.size,
							fileType: file.type,
							status: "recovery-blocked",
							readAt: Date.now()
						});
					}
				} else {
					console.warn("[Upload] Cannot surface upload needing attention: row missing", {
						sessionId, recoveryBlocked: options?.recoveryBlocked === true
					});
				}
			},
			/**
			 * Every caller means the same thing — "this row is between attempts, and the drain is coming
			 * back for it" — so every caller sets `retrying`, and `statusMessage` is purely additive copy
			 * on top of that.
			 *
			 * It used to be the flag's TRIGGER (`retrying = statusMessage !== undefined`), which left the
			 * message-less `RESUME_OUTCOME.RETRY` mapping in `adminUploadDrain.ts` setting only `deferred`,
			 * and `deferred` renders "Finishing in the
			 * background…". A row the drain had merely bounced off therefore told the user the server had
			 * taken ownership of it. Observed 2026-09-01: a 16-file batch stranded four rows in that copy,
			 * each one a `/complete` 404 (contact-api conflates a stale attempt fence with a genuinely
			 * absent upload into that one status — see `requestAdminMultipartCompletion`) that resolved as
			 * `alreadyComplete` on a later drain pass. The bytes were always fine; only the row was stuck.
			 *
			 * `retrying` is checked BEFORE `handedOff || deferred || ownershipUncertain` in the template,
			 * and `retryingFiles` before `handedOffFiles` in the header, so setting it is all it takes to
			 * route the row to the copy already written for exactly this state ("We'll keep retrying
			 * automatically." / "N still uploading"). `deferred` stays set: it is what suppresses the
			 * progress bar and keeps the cancel button reachable, and it remains true — this page is not
			 * driving the row right now.
			 */
			/**
			 * "This page's own upload lane still intends to upload this row" — the signal
			 * `adminUploadDrain`'s `discoverPending` needs and, until now, had no way to get.
			 *
			 * Pre-persist parks EVERY row of the batch as `status: "pending"` with the owner cleared the
			 * instant its bytes reach IndexedDB (`adminSessionStore.finishPrePersist`, deliberate
			 * crash-safety), but this dialog only uploads `FILE_UPLOAD_CONCURRENCY` of them at a time. A
			 * 23-file batch therefore finishes pre-persisting in under a second and leaves ~20 rows unowned
			 * and pending while they wait their turn — and the only thing `discoverPending` excluded was its
			 * OWN `context.controllers`. It could not tell those rows from a genuine orphan left by a dead
			 * tab, so it resumed them underneath us.
			 *
			 * Both lanes then drove the same row: the loser's claim was refused, the server superseded one
			 * attempt and aborted its S3 multipart upload, and the survivor's in-flight `UploadPart` PUTs
			 * started answering 404 NoSuchUpload. Observed 2026-09-01 across a 23-file batch — 6 refused
			 * claims, 4 yields, 5 superseded `/progress` + `/complete` 409 pairs, 3 dead S3 part PUTs, and
			 * 13 of 23 files never delivered.
			 *
			 * Three things bound the pin, each one a defect a break-it round found in an earlier shape of it:
			 *
			 *  - `laneOwned` spans the WHOLE attempt, not just the queue wait. It is cleared in the batch
			 *    loop's per-row `finally`, so it covers the several awaits between `uploadEntry` clearing
			 *    `queuedForUpload` and the durable lease claim actually landing. Pinning on
			 *    `queuedForUpload` alone left that window open and the drain took the active rows (angle B).
			 *  - `batchLaneActivityAt` expires the pin if the whole batch stops making progress, so a lane
			 *    that hangs somewhere no `finally` can observe hands its rows back instead of stranding them
			 *    forever — which would be strictly worse than the bug this fixes (angle A).
			 *  - `uploadId` is compared when both sides know one, so a stale entry cannot pin a row whose
			 *    upload identity has since been reinitialized to something this lane never owned (angle A).
			 *
			 * The pin also cannot outlive the page: it is component state. A genuinely abandoned row is
			 * still the drain's on the next load.
			 */
			ownsQueuedUploadSession(sessionId: string, uploadId?: string): boolean {
				if (!sessionId) return false;
				if (batchLaneActivityAt !== 0 && Date.now() - batchLaneActivityAt >= ADMIN_LANE_INTENT_TTL_MS) return false;
				return this.fileList.some(
					(entry: FileWithProgress) =>
						entry.laneOwned === true &&
						entry.sessionId === sessionId &&
						!entry.cancelled &&
						!entry.failed &&
						// Given up to the background on purpose — the drain is exactly who should take these.
						!entry.deferred &&
						!entry.handedOff &&
						!entry.ownershipUncertain &&
						// A row whose target was reinitialized is a different upload identity, and this entry only
						// speaks for the one it holds. Requiring the match is safe even mid-setup: the entry's
						// `uploadId` is stamped as soon as its durable session exists, so an entry that has none
						// yet has no pending row for the drain to find in the first place.
						(uploadId === undefined || entry.uploadId === undefined || entry.uploadId === uploadId)
				);
			},
			markResumedFileRetrying(sessionId: string, statusMessage?: string) {
				const file = this.findFileBySessionId(sessionId);
				if (file?.cancelPending) {
					// Same reasoning as markResumedFileComplete: a background retry announcement must not
					// revoke a cancellation the user explicitly asked for and that is still in flight.
					console.warn(`[Upload] Ignored resume retry for "${file.name}" while its cancellation is in flight`, {
						sessionId
					});
				} else if (file) {
					// LSP-19867: a row the drain is now driving no longer renders "finishing in the
					// background" — `retrying` wins in the template — so this is an exit, not a re-entry.
					logBackgroundFinishingCleared("drain-resume-retry", file);
					file.verificationPending = false;
					file.failureMessage = statusMessage;
					file.retrying = true;
					file.needsAttention = false;
					file.recoveryBlocked = false;
					file.isPreparing = false;
					file.deferred = true;
					file.handedOff = false;
					file.failed = false;
					file.cancelled = false;
					// A background retry updates status, not the user's open confirmation.
					logBackgroundFinishingEntered("drain-resume-retry", file, { statusMessage });
				}
				// Do NOT clear the dismissal here: retry is an automatic background action, not a user
				// action. During a prolonged flap the recovery poll re-runs every 5s/10s/20s and each
				// still-transient session lands here; un-dismissing would resurrect a tray the user
				// already closed and re-fire the retry toast on every cycle (LSP-17079).
			},
			markResumedFileFinishing(sessionId: string) {
				const file = this.findFileBySessionId(sessionId);
				if (!file || file.cancelled || file.cancelPending || file.failed) return;
				Object.assign(file, {
					verificationPending: true,
					retrying: false,
					needsAttention: false,
					recoveryBlocked: false,
					isPreparing: false,
					deferred: true,
					handedOff: false,
					failureMessage: undefined
				});
			},
			markResumedFileHandedOff(sessionId: string) {
				// Couldn't resume locally, but the MPU is still open and the server sweep will finish
				// it. Show "finishing in the background" (NOT failed/lost) and stop treating it as an
				// active local upload. dismissedResumedSessionIds is cleared so the tray reflects it.
				const file = this.findFileBySessionId(sessionId);
				if (file) {
					file.verificationPending = false;
					file.deferred = false;
					file.handedOff = true;
					file.retrying = false;
					file.needsAttention = false;
					file.recoveryBlocked = false;
					file.failureMessage = undefined;
					logBackgroundFinishingEntered("drain-handed-off-to-server-sweep", file);
				}
				this.dismissedResumedSessionIds.delete(sessionId);
			},
			clearUploadSessionInvalidation(sessionId: string) {
				this.invalidatedUploadSessionIds.delete(sessionId);
				this.confirmedAbsentInvalidatedUploadSessionIds.delete(sessionId);
				this.observedInvalidatedUploadSessionIds.delete(sessionId);
				this.invalidatedUploadIdsBySessionId.delete(sessionId);
			},
			registerUploadSessionInvalidation(sessionId: string, uploadId?: string) {
				if (sessionId.trim().length === 0) {
					console.info("[Upload] Ignoring upload session invalidation registration with blank sessionId", { sessionId });
					return;
				}
				if (this.invalidatedUploadSessionIds.has(sessionId)) {
					// A tombstone recorded before any local uploadId was known must still be backfillable —
					// otherwise the legitimate-reuse reconciliation in openForResumedUploads can never trust it.
					if (uploadId !== undefined && !this.invalidatedUploadIdsBySessionId.has(sessionId)) {
						this.invalidatedUploadIdsBySessionId.set(sessionId, uploadId);
					}
					return;
				}

				this.invalidatedUploadSessionIds.add(sessionId);
				if (uploadId !== undefined) this.invalidatedUploadIdsBySessionId.set(sessionId, uploadId);

				if (this.invalidatedUploadSessionIds.size > MAX_INVALIDATED_UPLOAD_SESSION_TOMBSTONES) {
					const oldestSessionId = this.invalidatedUploadSessionIds[Symbol.iterator]().next().value;
					if (oldestSessionId !== undefined) {
						this.clearUploadSessionInvalidation(oldestSessionId);
						console.info("[Upload] Evicted oldest upload session invalidation tombstone", {
							sessionId: oldestSessionId,
							tombstoneCount: this.invalidatedUploadSessionIds.size
						});
					}
				}
			},
			invalidateUploadSession(sessionId: string, remoteUploadId?: string) {
				if (sessionId.trim().length === 0) {
					console.info("[Upload] Ignoring upload session invalidation with blank sessionId", { sessionId });
					return;
				}

				const matchingEntries = [
					...new Set(
						this.allDisplayFiles.filter((file) => {
							const entryUploadId =
								file.uploadId ?? (file.uploadTarget ? uploadTargetOpaqueId(file.uploadTarget) : undefined);
							// LSP-23105: an entry still holding its own placeholder is the same row whatever target
							// the row has since been given, so a cancellation of that row removes it.
							if (
								file.sessionId !== sessionId ||
								(remoteUploadId !== undefined &&
									entryUploadId !== undefined &&
									entryUploadId !== remoteUploadId &&
									!isOwnPendingTargetPlaceholder(sessionId, entryUploadId))
							) {
								return false;
							}
							// LSP-22061: BroadcastChannel delivers this tab's own invalidation back to it (the
							// publisher and the subscriber are different channel objects), so this tab's own
							// tombstone write lands here while its row is still in the tray. A durable
							// confirmCancel in flight owns the row's removal and its "Upload cancelled." toast;
							// a row this tab tombstoned itself keeps its own failure state. Neither is a cancel
							// from another tab.
							if (file.cancelPending || file.tombstonedLocally) {
								console.info("[Upload] Upload session invalidation skipped a row this tab is cancelling itself", {
									sessionId,
									remoteUploadId,
									fileName: file.name,
									reason: file.cancelPending ? "local-cancel-pending" : "tombstoned-locally"
								});
								return false;
							}
							return true;
						})
					)
				];
				// LSP-23105: never record an entry's own placeholder as the tombstone identity — the resume
				// reconciler would read the row's real uploadId as reuse. Prefer a real id, then the broadcast's.
				const invalidatedUploadId =
					matchingEntries
						.map((entry) => entry.uploadId)
						.find((uploadId) => uploadId !== undefined && !isOwnPendingTargetPlaceholder(sessionId, uploadId)) ?? remoteUploadId;
				this.registerUploadSessionInvalidation(sessionId, invalidatedUploadId);
				for (const entry of matchingEntries) {
					logBackgroundFinishingCleared("cancelled-in-another-tab", entry);
					forgetUploadBatchFile(entry.uploadBatchId, entry.batchFileIndex);
					entry.cancelled = true;
					entry.abortController?.abort();
				}
				this.fileList = this.fileList.filter((file) => !matchingEntries.includes(file));
				this.resumedFileList = this.resumedFileList.filter((file) => !matchingEntries.includes(file));
				this.dismissedResumedSessionIds.delete(sessionId);
				if (matchingEntries.length > 0) {
					const message = "This upload was cancelled in another tab.";
					this.toast.info(message, { timeout: calculateTimeout(message) });
					if (this.allDisplayFiles.length === 0) this.dialog = false;
				} else {
					console.info("[Upload] Upload session invalidation had no matching local entries", {
						sessionId,
						remoteUploadId
					});
				}
			},
			requestCancel(entry: FileWithProgress) {
				if (entry.failed) {
					if (entry.sessionId || entry.uploadTarget) void this.confirmCancel(entry);
					else {
						forgetUploadBatchFile(entry.uploadBatchId, entry.batchFileIndex);
						this.fileList = this.fileList.filter(file => file !== entry);
						this.resumedFileList = this.resumedFileList.filter(file => file !== entry);
						if (this.allDisplayFiles.length === 0) this.dialog = false;
					}
					return;
				}
				entry.pendingCancel = true;
			},

			dismissCancel(entry: FileWithProgress) {
				entry.pendingCancel = false;
			},

			async confirmCancelLegacy(entry: FileWithProgress) {
				// Background recovery owns handed-off or deferred uploads; local cancellation must not abort or delete its work.
				if (entry.handedOff || entry.deferred || entry.ownershipUncertain) {
					console.info(`[Upload] confirmCancel skipped for handed-off or deferred entry "${entry.name}"`, {
						sessionId: entry.sessionId
					});
					// Clear stale intent defensively even though the template hides confirmation for handed-off or deferred entries.
					entry.pendingCancel = false;
					return;
				}
				// break-it: admin surface — concurrent confirmCancel calls for the same entry (double-click,
				// or two triggers racing before the first await) must coalesce into one destructive cleanup
				// instead of double-deleting chunk data / double-writing the "cancelled" session status.
				if (entry.cancelled) return;
				// entry.uploadId wins, matching getEntrySessionIdentity's default: it is re-stamped from the persisted
				// session on every reclaim/resume (lines ~857, ~1214, ~1229, ~1300), while entry.uploadTarget is assigned
				// once and never refreshed. The CAS/tombstone identity below must track the CURRENT persisted record,
				// not the possibly-stale original target — entry.uploadTarget's own fields are still used verbatim for
				// the actual S3 abort call, since that's the only place s3Key/bucket exist.
				const cleanupIdentity = entry.uploadId ?? (entry.uploadTarget ? uploadTargetOpaqueId(entry.uploadTarget) : undefined);
				const sessionCleanupIdentityKey =
					entry.sessionId !== undefined && cleanupIdentity !== undefined ? `${entry.sessionId}::${cleanupIdentity}` : undefined;
				const cleanupOwnedByAnotherCancellation =
					sessionCleanupIdentityKey !== undefined && this.activeSessionCleanupIdentities.has(sessionCleanupIdentityKey);
				if (!cleanupOwnedByAnotherCancellation && sessionCleanupIdentityKey !== undefined) {
					this.activeSessionCleanupIdentities.add(sessionCleanupIdentityKey);
				}

				try {
					if (entry.sessionId) this.registerUploadSessionInvalidation(entry.sessionId, cleanupIdentity);
					logBackgroundFinishingCleared("cancelled-by-user", entry);
					entry.cancelled = true;
					forgetUploadBatchFile(entry.uploadBatchId, entry.batchFileIndex);
					entry.pendingCancel = false;
					console.info(`[Upload] Cancelled by user: "${entry.name}"`);
					// Abort in-flight upload first, before freeing IDB data it may be reading
					if (entry.abortController) {
						entry.abortController.abort();
					}
					this.fileList = this.fileList.filter((file) => toRaw(file) !== toRaw(entry));
					this.resumedFileList = this.resumedFileList.filter((file) => toRaw(file) !== toRaw(entry));

					if (!cleanupOwnedByAnotherCancellation) {
						const message = "Upload cancelled.";
						if (!entry.failed) this.toast.info(message, { timeout: calculateTimeout(message) });
					}
					if (this.allDisplayFiles.length === 0) this.dialog = false;
					if (cleanupOwnedByAnotherCancellation) {
						console.info(
							`[Upload] Deferred duplicate cancellation cleanup for "${entry.name}" to ${sessionCleanupIdentityKey}`
						);
						return;
					}

					const refreshPersistedIdentity = async (): Promise<boolean> => {
						if (!entry.sessionId) return false;
						const session = await getFileUploadSessionById(entry.sessionId);
						if (!session) return false;
						if (session.uploadId !== this.getEntrySessionIdentity(entry, cleanupIdentity)?.uploadId) {
							console.warn(
								`[Upload] Refusing to refresh identity for cancelled "${entry.name}"; the persisted uploadId no longer matches`
							);
							return false;
						}
						entry.uploadOwnerId = session.uploadOwnerId;
						entry.uploadOwnerClaimId = session.uploadOwnerClaimId;
						return true;
					};
					const markSessionCancelled = async (): Promise<boolean> => {
						const cancelled = await this.markEntrySessionCancelled(entry, cleanupIdentity, { notifyOtherTabs: true });
						if (cancelled || !(await refreshPersistedIdentity())) return cancelled;
						return this.markEntrySessionCancelled(entry, cleanupIdentity, { notifyOtherTabs: true });
					};
					const deleteSession = async (): Promise<boolean> => {
						const deleted = await this.deleteEntrySession(entry, cleanupIdentity);
						if (deleted || !(await refreshPersistedIdentity())) return deleted;
						return this.deleteEntrySession(entry, cleanupIdentity);
					};
					const existingPrePersistAbortPromise = entry.prePersistAbortPromise;
					if (!existingPrePersistAbortPromise && entry.uploadTarget) {
						// Create this before the awaited chunk deletion so a persist continuation joining the
						// cancellation always observes and reuses this exact abort request.
						entry.prePersistAbortPromise = trackPrePersistAbortOutcome(
							entry,
							abortPrePersistTarget(entry.uploadTarget, entry.name, "user cancellation")
						);
					} else if (existingPrePersistAbortPromise && entry.uploadTarget) {
						// Keep the original request shared until it settles. If it failed, this explicit
						// cancellation owns one retry without racing a still-in-flight abort.
						entry.prePersistAbortPromise = trackPrePersistAbortOutcome(
							entry,
							existingPrePersistAbortPromise.then((outcome) =>
								outcome === "resolved"
									? outcome
									: abortPrePersistTarget(entry.uploadTarget!, entry.name, "user cancellation retry")
							)
						);
					}
					const abortOutcomePromise = entry.prePersistAbortPromise
						? Promise.race([
								entry.prePersistAbortPromise.then((status) => ({ status }) as const),
								new Promise<{ status: "timed-out" }>((resolve) =>
									setTimeout(() => resolve({ status: "timed-out" }), CONFIRM_CANCEL_ABORT_TIMEOUT_MS)
								)
							])
						: undefined;

					if (entry.sessionId) {
						await this.deleteEntryChunkData(entry, cleanupIdentity).catch(() => {});
					}

					if (!entry.uploadTarget) {
						if (entry.directUploadTarget) {
							try {
								await abortAdminDirectTarget(entry.directUploadTarget);
								console.info(`[Upload] Direct uploads target aborted for "${entry.name}"`);
							} catch (error) {
								console.warn(`[Upload] Direct uploads abort failed for "${entry.name}"`, error);
							}
							return;
						}
						// For resumed entries, App.vue handles S3 abort when it catches UploadCancelledError.
						// Mark the session cancelled here so the startup drain covers it if that handler doesn't reach.
						if (entry.sessionId) {
							await markSessionCancelled().catch((markError) =>
								console.warn(
									`[Upload] markEntrySessionCancelled threw for "${entry.name}" — cross-tab notification may not have fired`,
									markError
								)
							);
						}
						return;
					}

					try {
						const abortOutcome = await abortOutcomePromise!;
						if (abortOutcome.status === "rejected") throw new Error("S3 multipart abort failed");
						if (abortOutcome.status === "timed-out") {
							console.warn(
								`[Upload] S3 multipart abort for "${entry.name}" did not complete within ${CONFIRM_CANCEL_ABORT_TIMEOUT_MS}ms — marking the session cancelled now so other tabs are notified promptly; the abort request continues in the background`
							);
							if (entry.sessionId) {
								await markSessionCancelled().catch((markError) =>
									console.warn(
										`[Upload] markEntrySessionCancelled threw for "${entry.name}" — cross-tab notification may not have fired`,
										markError
									)
								);
							}
							return;
						}
						console.info(`[Upload] S3 multipart upload aborted for "${entry.name}"`);
						if (entry.sessionId) {
							// If the local delete fails, fall back to marking the session "cancelled" so the
							// startup drain can clean it up. Otherwise the row stays "uploading" with no chunk
							// data and resetUploadingSessions will retry a doomed resume on every page load.
							try {
								const sessionDeleted = await deleteSession();
								if (!sessionDeleted) {
									console.warn(
										`[Upload] S3 abort succeeded but local cleanup was rejected for "${entry.name}" — marking session cancelled for next-load drain`
									);
									await markSessionCancelled().catch((markError) =>
										console.warn(
											`[Upload] markEntrySessionCancelled threw for "${entry.name}" — cross-tab notification may not have fired`,
											markError
										)
									);
								}
							} catch (idbError) {
								console.warn(
									`[Upload] S3 abort succeeded but local cleanup failed for "${entry.name}" — marking session cancelled for next-load drain`,
									idbError
								);
								await markSessionCancelled().catch((markError) =>
									console.warn(
										`[Upload] markEntrySessionCancelled threw for "${entry.name}" — cross-tab notification may not have fired`,
										markError
									)
								);
							}
						}
					} catch (error) {
						console.warn(`[Upload] Immediate S3 abort failed for "${entry.name}" — will retry on next page load`, error);
						if (entry.sessionId) {
							await markSessionCancelled().catch((markError) =>
								console.warn(
									`[Upload] markEntrySessionCancelled threw for "${entry.name}" — cross-tab notification may not have fired`,
									markError
								)
							);
						}
					}
				} finally {
					if (!cleanupOwnedByAnotherCancellation && sessionCleanupIdentityKey !== undefined) {
						this.activeSessionCleanupIdentities.delete(sessionCleanupIdentityKey);
					}
				}
			},

			/**
			 * True when the user's cancellation is already recorded durably — the row carries the
			 * "cancelled" tombstone, or it is gone entirely. A read failure answers false so the row
			 * is restored rather than silently dropped from a tray it still belongs in.
			 */
			async isSessionDurablyCancelled(sessionId?: string): Promise<boolean> {
				if (!sessionId) return false;
				try {
					const session = await getFileUploadSessionById(sessionId);
					return !session || session.status === "cancelled";
				} catch (error) {
					console.warn(`[Upload] Could not read cancellation state for session "${sessionId}"`, error);
					return false;
				}
			},

			async confirmCancel(entry: FileWithProgress) {
				// Persisted facade targets need the server outcome before local cleanup, including active
				// rows whose completion response is still in flight. Target-less restored batches/videos
				// need the same coordinator because their resume driver may not have installed its abort
				// controller yet. Legacy and pre-persistence targets keep their target-owned path.
				const requiresServerCancellation = Boolean(
					entry.sessionId &&
						((!entry.uploadTarget && (entry.uploadBatchId || entry.type?.toLowerCase().startsWith("video/"))) ||
							(entry.uploadTarget && isUploadsUploadTarget(entry.uploadTarget)))
				);
				// LSP-19384: a parked row is durable-only in exactly the way this branch exists for — a
				// session id, no in-memory target, no controller — and its multipart upload is still
				// open server-side. The legacy path would clear the local row and leave that upload
				// dangling, so Remove takes the durable route with its server abort, like a resumed row.
				if (
					!requiresServerCancellation &&
					!entry.retryCeilingReached &&
					!entry.handedOff &&
					!entry.deferred &&
					!entry.ownershipUncertain
				) {
					return this.confirmCancelLegacy(entry);
				}
				if (entry.cancelPending || entry.cancelled) return;
				// LSP-19867: the "left the finishing state" exit is logged only once the durable
				// cancellation is known to have landed (the two committed branches below). The
				// `pending`-not-tombstoned, `stale-owner` and thrown outcomes roll the row back to its
				// finishing presentation, and logging the exit up front would have consumed the latch
				// so the row's eventual real exit went silent (Bugbot, PR review).
				entry.pendingCancel = false;
				entry.cancelPending = true;
				entry.cancelled = true;
				// The resumed driver owns the durable writer while it is attempting completion.
				// Abort that attempt before acquiring the cancellation writer; otherwise the
				// cancellation coordinator correctly sees a live successor and leaves the row
				// pending, with no one left to issue the server abort.
				if (requiresServerCancellation) entry.abortController?.abort();
				const expectedUploadId = entry.uploadId ?? (entry.uploadTarget ? uploadTargetOpaqueId(entry.uploadTarget) : undefined);
				// LSP-23105: a placeholder names no upload the resume snapshot will ever report once a target is
				// attached, so recording it would let the snapshot read the row's real uploadId as "legitimate
				// reuse" and drop this tombstone early. Record none; this tab's own invalidation echo backfills it.
				const invalidationUploadId = isOwnPendingTargetPlaceholder(entry.sessionId, expectedUploadId) ? undefined : expectedUploadId;
				if (entry.sessionId) this.registerUploadSessionInvalidation(entry.sessionId, invalidationUploadId);
				try {
					let result: Awaited<ReturnType<typeof cancelAdminUploadSession>> = "missing";
					for (let attempt = 0; entry.sessionId && attempt <= RESUMED_CANCEL_RETRY_ATTEMPTS; attempt += 1) {
						result = await cancelAdminUploadSession({
							sessionId: entry.sessionId,
							expectedUploadId,
							// Every row that reaches this durable path is one background recovery may
							// already own: a resumed row renders the predecessor's owner identity (or
							// none at all) until a claim broadcast refreshes it, and that broadcast is
							// an in-process listener set — it never crosses a tab boundary, and it is
							// dropped outright when the claim lands before the tray row exists. Fencing
							// an explicit user cancellation on that snapshot refuses at
							// cancelAdminUploadSession's first gate, before the handoff/tombstone path
							// can run, so the cancel control we chose to show could not stop the upload.
							// The immutable uploadId above still fences the target; let the cancellation
							// coordinator acquire the current writer for this explicit user action.
							expectedOwner: undefined,
							onTombstoned: () => entry.abortController?.abort()
						});
						if (result !== "pending" || !requiresServerCancellation) break;
						await new Promise<void>((resolve) => setTimeout(resolve, RESUMED_CANCEL_RETRY_DELAY_MS));
					}
					if (result === "completed") {
						entry.cancelled = false;
						entry.cancelPending = false;
						this.clearUploadSessionInvalidation(entry.sessionId ?? "");
						if (entry.sessionId) this.markResumedFileComplete(entry.sessionId);
						// The batch owner emits UploadFinished after every sibling settles.
						const message = "This file finished uploading before it could be cancelled.";
						this.toast.info(message, { timeout: calculateTimeout(message) });
					} else if (result === "acknowledged" || result === "missing") {
						forgetUploadBatchFile(entry.uploadBatchId, entry.batchFileIndex);
						logBackgroundFinishingCleared("cancelled-by-user", entry);
						entry.cancelPending = false;
						// LSP-19384: this branch RETRACTS the invalidation tombstone below, so it is the one
						// removal with no standing protection against a parked-upload snapshot that was read
						// before this cancellation and is still in flight.
						this.noteParkedUploadResolvedLocally(entry.sessionId);
						// LSP-22061: compare raw identities (as confirmCancelLegacy does). This removal is the
						// row's only one: this tab's own invalidation echo deliberately skips a row whose
						// cancellation is still pending, so it can no longer paper over a proxy/raw mismatch.
						this.fileList = this.fileList.filter((file) => toRaw(file) !== toRaw(entry));
						this.resumedFileList = this.resumedFileList.filter((file) => toRaw(file) !== toRaw(entry));
						this.clearUploadSessionInvalidation(entry.sessionId ?? "");
						const message = "Upload cancelled.";
						if (!entry.failed) this.toast.info(message, { timeout: calculateTimeout(message) });
						if (this.allDisplayFiles.length === 0) this.dialog = false;
					} else if (result === "pending") {
						// "pending" covers two very different states, and only the durable tombstone
						// decides which. If the row is cancelled (or already gone), the user's intent
						// IS recorded and the cancelled-upload drain owns the outstanding server abort;
						// leaving the row on "Cancellation pending…" strands it forever, because this
						// tab is never told when that reconciliation lands — the drain's delete passes
						// notifyOtherTabs: false, so nothing is published at all. (The tombstone write
						// itself IS echoed back to this tab over BroadcastChannel, but
						// invalidateUploadSession deliberately skips this cancelPending row — LSP-22061.)
						// If instead nothing was tombstoned (no durable writer could be acquired),
						// nothing was cancelled and the row must come back.
						const tombstoned = await this.isSessionDurablyCancelled(entry.sessionId);
						if (tombstoned) {
							forgetUploadBatchFile(entry.uploadBatchId, entry.batchFileIndex);
							logBackgroundFinishingCleared("cancelled-by-user", entry);
							entry.cancelPending = false;
							this.noteParkedUploadResolvedLocally(entry.sessionId);
							this.fileList = this.fileList.filter((file) => toRaw(file) !== toRaw(entry));
							this.resumedFileList = this.resumedFileList.filter((file) => toRaw(file) !== toRaw(entry));
							// The invalidation tombstone stays registered: the row is cancelled but not
							// yet removed, and the resume snapshot must not resurrect it in the meantime.
							const message = "Upload cancelled.";
							if (!entry.failed) this.toast.info(message, { timeout: calculateTimeout(message) });
							if (this.allDisplayFiles.length === 0) this.dialog = false;
						} else {
							entry.cancelled = false;
							entry.cancelPending = false;
							this.clearUploadSessionInvalidation(entry.sessionId ?? "");
							const message = "Could not cancel this upload just now. Please try again.";
							this.toast.warning(message, { timeout: calculateTimeout(message) });
						}
					} else if (result === "stale-owner") {
						entry.cancelled = false;
						entry.cancelPending = false;
						// Retract the tombstone registered before the call: nothing was cancelled, so the
						// session is still live. Leaving it registered makes openForResumedUploads'
						// invalidated-session filter drop a RUNNING upload from the tray, and the user
						// can then neither see it nor retry the cancel.
						this.clearUploadSessionInvalidation(entry.sessionId ?? "");
						this.toast.warning("This upload changed in another tab; it was not cancelled.", {
							timeout: calculateTimeout("cancel")
						});
					}
				} catch (error) {
					console.warn(`[Upload] Could not persist cancellation for "${entry.name}"`, error);
					entry.cancelled = false;
					entry.cancelPending = false;
					// Same retraction as the stale-owner branch above: the throw means the cancellation
					// did not land, so the pre-registered tombstone must not survive to hide a live row.
					this.clearUploadSessionInvalidation(entry.sessionId ?? "");
				}
			},

			_handleOffline() {
				this.isOffline = true;
			},
			_handleOnline() {
				this.isOffline = false;
			},
			_handleBeforeUnload(event: BeforeUnloadEvent) {
				if (
					!this.hasFilesPreparing &&
					this.checkingFiles.length === 0 &&
					this.remainingFiles.length === 0 &&
					this.retryingFiles.length === 0 &&
					!this.fileList.some(
						(file) => file.uploadBatchId && !file.sessionId && !file.failed && !file.cancelled && file.progress < 100
					)
				)
					return;
				event.preventDefault();
				event.returnValue = "";
			},
			_handlePageHide() {
				void releaseLaneLeaseIfHeld();
			},

			closeDialog() {
				if (this.quotaDialogVisible) {
					this.onQuotaDialogCancel();
				}
				// Record every displayed unfinished session as dismissed — including live uploads in
				// fileList (not just resumedFileList). A live upload deferred by a transient auth flap
				// stays in fileList but becomes a pending session, so the recovery poll surfaces it; if
				// close only dismissed resumedFileList, the poll would reopen the tray every few seconds
				// during a prolonged flap (LSP-17079).
				for (const f of this.allDisplayFiles) {
					// Bugbot (LSP-19766): a TRAY-CREATED verifying-orphan placeholder is purely informational —
					// it hasn't started uploading yet — so dismissing the tray while only it is shown must not
					// suppress the tray reopening once the drain actually reclaims and resumes the session.
					// Marking it dismissed made openForResumedUploads's allPreviouslyDismissed early-return keep
					// the tray closed forever after, so the real resume-and-complete happened with zero user
					// feedback. But `verifyingOrphan` alone is not the right test: showVerifyingOrphanedUpload
					// also re-flags an EXISTING real row during its documented 0%-progress, no-controller retry
					// window (verifyingOrphanRowCreatedByTray stays false for that row — see its own comment).
					// That row is a genuine resumed upload dismissedResumedSessionIds exists to suppress across
					// a flap, so only the tray-created placeholder is excluded here, not every flagged row.
					const isTrayCreatedPlaceholder = f.verifyingOrphan && f.verifyingOrphanRowCreatedByTray;
					if (f.sessionId && !isTrayCreatedPlaceholder && (f.failed || f.progress < 100)) {
						this.dismissedResumedSessionIds.add(f.sessionId);
					}
				}
				this.dialog = false;
			}
		},
		mounted() {
			// Resolved per tick, not at mount: a tick that cannot read is logged and retried by the
			// registry, whereas a binding evaluated here would make every mount depend on it.
			this.deferredRowFollowUps = createDeferredRowFollowUpRegistry({
				readRows: (sessionIds) => getFileUploadSessionsByIds(sessionIds)
			});
			this.unsubscribeUploadLeaseClaims = subscribeAdminUploadLeaseClaims((session) => this.updateResumedFileIdentity(session));
			this.unsubscribeUploadSessionInvalidations = subscribeFileUploadSessionInvalidations((sessionId, uploadId) =>
				this.invalidateUploadSession(sessionId, uploadId)
			);
			// LSP-20327: before any drain pass, because the rows this restores are precisely the ones no
			// durable pass can ever discover. Synchronous, so it cannot lose that race.
			this.restoreLostUploadRecordNotices();
			void Promise.resolve().then(() => pruneCompletedUploadBatchHistory()).catch((error) => console.warn("[Upload] Could not expire completed batch history", { error }));
			// App hydrates auth after child mount; retry when that organization becomes available.
			this.$watch(
				() => useAuthSessionStore().organizationId,
				(_organizationId, _previous, onCleanup) => {
					let current = true;
					const subscription = liveQuery(async () => {
						const foregroundIds = new Set(this.allDisplayFiles.filter((file) => file.laneOwned).map((file) => file.sessionId));
						const sessions = (await listFileUploadSessions()).filter((session) => session.retainBatchHistory && !foregroundIds.has(session.id));
						const activeIds = sessions.filter((session) => session.status !== "complete").map((session) => session.id);
						const chunks = activeIds.length ? await uploadDb.fileUploadChunks.where("sessionId").anyOf(activeIds).toArray() : [];
						const uploadedBytes = new Map<string, number>();
						for (const chunk of chunks) if (chunk.isUploaded) uploadedBytes.set(chunk.sessionId, (uploadedBytes.get(chunk.sessionId) ?? 0) + chunk.size);
						return sessions.map((session) => ({ session, progress: session.status === "complete" ? 100 : Math.round((uploadedBytes.get(session.id) ?? 0) / session.fileSize * 100) }));
					}).subscribe({
						next: (snapshots) => { if (current) this.restoreDurableUploadBatchRows(snapshots); },
						error: (error) => console.warn("[Upload] Durable batch tray observation failed", { organizationId: _organizationId, error })
					});
					onCleanup(() => {
						current = false;
						subscription.unsubscribe();
					});
					this.resumedFileList = this.resumedFileList.filter((file) => !file.uploadBatchId);
					void this.restoreUnpersistedUploadNotices(() => current).catch((error) =>
						console.warn("[Upload] Unpersisted batch restore failed", { error })
					);
				},
				{ immediate: true }
			);
			window.addEventListener("offline", this._handleOffline);
			window.addEventListener("online", this._handleOnline);
			window.addEventListener("beforeunload", this._handleBeforeUnload);
			window.addEventListener("pagehide", this._handlePageHide);
		},
		beforeUnmount() {
			this.isUnmounting = true;
			if (this.longPreparationHintTimer !== undefined) clearTimeout(this.longPreparationHintTimer);
			this.deferredRowFollowUps?.stopAll();
			for (const reconcile of this.prePersistTargetReconciles) reconcile.stop();
			this.prePersistTargetReconciles = [];
			this.unsubscribeUploadLeaseClaims?.();
			this.unsubscribeUploadSessionInvalidations?.();
			this.screenWakeLock.stop();
			for (const file of this.allDisplayFiles) {
				if (file.releaseRetryTimer) globalThis.clearTimeout(file.releaseRetryTimer);
			}
			window.removeEventListener("offline", this._handleOffline);
			window.removeEventListener("online", this._handleOnline);
			window.removeEventListener("beforeunload", this._handleBeforeUnload);
			window.removeEventListener("pagehide", this._handlePageHide);
		}
	};
</script>

<style lang="scss">
	/* Styles for file upload snackbar */
	.v-snackbar.file-upload-snackbar {
		.v-snackbar__wrapper {
			/* LSP-22130: VSnackbar's wrapper is a row flexbox with align-items: center. Once the panel
				outgrew max-height (two-line "Preparing and uploading N of M items" heading plus both
				preparation hints), centering pushed half the overflow ABOVE the wrapper, where scrolling
				can't reach it — clipping the top padding. A column chain lets the file list shrink
				instead, and anything that still overflows on a very short screen spills downward. */
			flex-direction: column;
			align-items: stretch;
			border-radius: 16px !important;
			max-width: 500px !important;
			width: 100% !important;
			overflow-x: hidden;
			/* LSP-22119: the snackbar is anchored to the bottom of the room Vuetify leaves it — its root is
				padded by var(--v-layout-top) / var(--v-layout-bottom) (app bar, bottom navigation) plus an 8px
				margin on each side — so the panel may be no taller than that room, or it grows up under the
				fixed shell chrome and the heading is clipped. The top chrome is the larger of Vuetify's
				layout top and App.vue's measured --shell-header-height, which also counts the shell banners
				stacked above the app bar (they are not Vuetify layout items). Max height should not exceed:
				100dvh (dynamic screen height, with a 100vh fallback for browsers without dvh support)
					- top chrome - var(--v-layout-bottom)
					- 16px (the snackbar's 8px top and bottom margins)
					- env(safe-area-inset-top, 0px) (safe area inset top). */
			max-height: min(
				500px,
				calc(
					100vh - max(var(--v-layout-top, 0px), var(--shell-header-height, 0px)) - var(--v-layout-bottom, 0px) -
						16px - env(safe-area-inset-top, 0px)
				)
			);
			max-height: min(
				500px,
				calc(
					100dvh - max(var(--v-layout-top, 0px), var(--shell-header-height, 0px)) - var(--v-layout-bottom, 0px) -
						16px - env(safe-area-inset-top, 0px)
				)
			);
			overflow-y: auto;

			@media #{map.get($display-breakpoints, 'sm-and-down')} and (orientation: landscape) {
				max-height: min(
					80vh,
					calc(
						100vh - max(var(--v-layout-top, 0px), var(--shell-header-height, 0px)) - var(--v-layout-bottom, 0px) -
							16px - env(safe-area-inset-top, 0px)
					)
				);
				max-height: min(
					80vh,
					calc(
						100dvh - max(var(--v-layout-top, 0px), var(--shell-header-height, 0px)) - var(--v-layout-bottom, 0px) -
							16px - env(safe-area-inset-top, 0px)
					)
				);
			}
		}

		.v-snackbar__content {
			display: flex;
			flex-direction: column;
			min-height: 0;
			max-width: 100%;
			width: 100%;
			overflow-x: hidden;
		}
	}
</style>

<style scoped lang="scss">
	@use "vuetify/settings";

	.max-width-100 {
		max-width: 100% !important;
		width: 100% !important;
	}

	/* LSP-22130: lets the panel shrink to the snackbar's max-height; the file list absorbs it. */
	.file-upload-panel {
		min-height: 0;
	}

	/* Keeps at least one upload row visible when a tall header shrinks the list. */
	.file-upload-list {
		min-height: 56px;
	}

	.file-info-container {
		min-width: 0; /* This is crucial for text-truncate to work */
		flex: 1;
	}

	.file-upload-row {
		min-height: 56px;
		flex-shrink: 0;
	}

	/* LSP-16860: thickens the close/cancel "x" glyph strokes so they read as bold, not just bigger. */
	.icon-close-bold {
		-webkit-text-stroke: 1.8px currentColor;
	}
</style>
